import axios from 'axios';
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import Select from "react-select";
import generateBarcode, { generateBarcodeFromValue } from '../components/barcodeHelper';
import ProductTable from './products/ProductTable';
import Modal from '../components/Modal';
import ProductDetails from './products/ProductDetails';
import { createWooCommerceProduct, getCategoriesWooCommerce, getWooCommerceProduct, getWooCommerceProductsPage, updateWooCommerceProductCategories, updateWooCommerceProductDeliveryMeta, updateWooCommerceProductVaseMeta } from '../components/woocommerceHelper';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { calculateFees } from '../components/amountHelper';
import { header } from '../components/header.encry';
import ProductService, { PRODUCT_CACHE_UPDATED_EVENT } from '../components/ProductService';
import * as ProductAddonService from '../components/ProductAddonService';
import VariableService from '../components/VariableService';
import ComponentsOfProducts from './products/ComponentsOfProducts';
import SquareImageCropModal from '../components/SquareImageCropModal';
import GlovoToggleProductModal from '../components/GlovoToggleProductModal';
import PublierPlateformesModal from '../components/PublierPlateformesModal';
import { buildWebsitePublication, websitePublicationMeta } from '../utils/websitePublishing';
import { matchesPlatformSummary } from '../utils/platformSummaryFilter';
import PromotionsModal from '../components/PromotionsModal';
import * as XLSX from 'xlsx';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography,
    LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { changeAimerProductStatus, createAimerProduct, fetchPlateforms as fetchAllPlateforms } from '../components/PlateformService';

const API_URL = process.env?.REACT_APP_PEGASUS_API_URL;
const STORE_QUICK_PRODUCT_CACHE_KEY = 'storeQuickProduct.products.stream.v1';

const readCachedStoreQuickProducts = () => {
    if (typeof window === 'undefined') {
        return [];
    }

    try {
        const rawValue = window.localStorage.getItem(STORE_QUICK_PRODUCT_CACHE_KEY);
        if (!rawValue) {
            return [];
        }

        const parsedValue = JSON.parse(rawValue);
        const cachedProducts = Array.isArray(parsedValue)
            ? parsedValue
            : parsedValue?.products;

        return Array.isArray(cachedProducts) ? cachedProducts : [];
    } catch (error) {
        console.warn('Unable to read StoreQuickProduct cache:', error);
        return [];
    }
};

const writeCachedStoreQuickProducts = (products) => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        if (!Array.isArray(products) || products.length === 0) {
            window.localStorage.removeItem(STORE_QUICK_PRODUCT_CACHE_KEY);
            return;
        }

        window.localStorage.setItem(
            STORE_QUICK_PRODUCT_CACHE_KEY,
            JSON.stringify({
                products,
                updatedAt: new Date().toISOString(),
            })
        );
    } catch (error) {
        console.warn('Unable to write StoreQuickProduct cache:', error);
    }
};


const MARJANE_FILTERS = [
    { value: 'draft', label: 'Éligible' },
    { value: 'importing', label: 'En cours de création' },
    { value: 'imported', label: 'Importé' },
    { value: 'rejected', label: 'Rejeté' },
    { value: 'active', label: 'Active' },
];

const FILTER_BUTTON_COLORS = {
    draft: 'info',
    importing: 'primary',
    imported: 'success',
    rejected: 'warning',
    active: 'secondary',
};

const normalizeId = (value) => {
    if (value === null || value === undefined) {
        return '';
    }

    if (Array.isArray(value)) {
        return value.map((item) => normalizeId(item)).filter(Boolean).join('|');
    }

    if (typeof value === 'object') {
        const nestedValue = (
            value.$oid ||
            value._id ||
            value.$id ||
            value.id ||
            value.value
        );
        return nestedValue ? normalizeId(nestedValue) : '';
    }

    return String(value).trim();
};

const normalizeProductMatchName = (value) => (
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/&amp;/gi, '&')
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .trim()
        .toLowerCase()
);

const tokenizeProductName = (value) => normalizeProductMatchName(value)
    .split(' ')
    .map((token) => token.trim())
    .filter((token) => token.length >= 3);

const calculateTokenSimilarity = (leftValue, rightValue) => {
    const leftTokens = new Set(tokenizeProductName(leftValue));
    const rightTokens = new Set(tokenizeProductName(rightValue));

    if (!leftTokens.size || !rightTokens.size) {
        return 0;
    }

    let intersection = 0;
    leftTokens.forEach((token) => {
        if (rightTokens.has(token)) {
            intersection += 1;
        }
    });

    return (2 * intersection) / (leftTokens.size + rightTokens.size);
};

const normalizePriceNumber = (value) => {
    const parsed = Number(String(value ?? '').replace(/\s/g, '').replace(',', '.').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
};

const firstAvailableValue = (...values) => values.find((value) => value !== null && value !== undefined && String(value).trim() !== '');

const imageBasename = (value) => {
    const rawValue = String(value || '').split('?')[0].split('#')[0];
    const parts = rawValue.split('/');
    return normalizeProductMatchName(parts[parts.length - 1] || '');
};

const normalizeWebsiteDelivery = (value = {}) => {
    const expressEnabled = value?.express_enabled === true || value?.express_enabled === '1' || value?.express_enabled === 1;
    const moroccoEnabled = value?.morocco_enabled === true || value?.morocco_enabled === '1' || value?.morocco_enabled === 1;
    const expressZones = expressEnabled && Array.isArray(value?.express_zones)
        ? value.express_zones
            .map((zone) => ({
                code: String(zone?.code || zone?.value || '').trim().toUpperCase(),
                name: String(zone?.name || zone?.label || '').trim(),
            }))
            .filter((zone) => zone.code)
        : [];

    return {
        express_enabled: expressEnabled,
        express_zones: expressZones,
        morocco_enabled: moroccoEnabled,
    };
};

const extractWooCommerceIds = (product) => {
    const rawCandidates = [];
    const addCandidate = (value) => {
        if (Array.isArray(value)) {
            value.forEach(addCandidate);
            return;
        }

        const normalizedValue = normalizeId(value);
        if (!normalizedValue) {
            return;
        }

        String(normalizedValue)
            .split('|')
            .map((item) => item.trim())
            .filter(Boolean)
            .forEach((item) => rawCandidates.push(item));
    };

    [
        product?.woocommerce_id,
        product?.woocommerceId,
        product?.woo_id,
        product?.wooId,
        product?.idwoo,
        product?.idWoo,
        product?.website_id,
        product?.websiteId,
        product?.website?.woocommerce_id,
        product?.website?.woocommerceId,
        product?.website?.id,
        product?.website?.woo_id,
        product?.website?.wooId,
        product?.woo?.id,
        product?.woocommerce?.id,
        product?.metadata?.woocommerce_id,
        product?.metadata?.woo_id,
        product?.meta?.woocommerce_id,
        product?.meta?.woo_id,
    ].forEach(addCandidate);

    const platformRelations = [
        ...(Array.isArray(product?.plateforms) ? product.plateforms : []),
        ...(Array.isArray(product?.platforms) ? product.platforms : []),
        ...(Array.isArray(product?.platform_products) ? product.platform_products : []),
        ...(Array.isArray(product?.platformProducts) ? product.platformProducts : []),
    ];

    if (platformRelations.length) {
        platformRelations.forEach((platform) => {
            const platformType = String(platform?.type || platform?.platform_type || platform?.platformType || platform?.platform || platform?.name || '').toLowerCase();
            if (platformType.includes('woo') || platformType.includes('website') || platformType.includes('site')) {
                [
                    platform?.remote_id,
                    platform?.remoteId,
                    platform?.product_id,
                    platform?.productId,
                    platform?.woocommerce_id,
                    platform?.woocommerceId,
                    platform?.woo_id,
                    platform?.wooId,
                    platform?.website_id,
                    platform?.websiteId,
                    platform?.idwoo,
                    platform?.id,
                ].forEach(addCandidate);
            }
        });
    }

    return Array.from(new Set(rawCandidates));
};

const StoreQuickProduct = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const [products, setProducts] = useState(() => readCachedStoreQuickProducts());
    const [pagedProducts, setPagedProducts] = useState([]);
    const [productPagination, setProductPagination] = useState({
        total: 0,
        perPage: 20,
        pageNumber: 1,
        totalPages: 1,
    });
    const [isFullProductsLoading, setIsFullProductsLoading] = useState(true);
    const [isPagedProductsLoading, setIsPagedProductsLoading] = useState(false);
    const [stores, setStores] = useState([]); // Store list for dropdown
    const [picture, setPicture] = useState(null);
    const [filterProducts, setFilterProducts] = useState([]);
    const [filterProductsByCategory, setFilterProductsByCategory] = useState([]);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [productCategories, setProductCategories] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [fees, setFees] = useState(0);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [components, setComponents] = useState([
        { product: '', quantity: '1' }
    ]);
    const [availableComponents, setAvailableComponents] = useState([]);
    const [stockModalProduct, setStockModalProduct] = useState(null);
    const [stockMovementForm, setStockMovementForm] = useState({
        storeId: '',
        quantity: '1',
    });
    const [isSubmittingStockMovement, setIsSubmittingStockMovement] = useState(false);
    const [bulkStockModalOpen, setBulkStockModalOpen] = useState(false);
    const [bulkStockRows, setBulkStockRows] = useState({});
    const [bulkStockGlobalStoreId, setBulkStockGlobalStoreId] = useState('');
    const [isSubmittingBulkStock, setIsSubmittingBulkStock] = useState(false);
    const [bulkPluCategories, setBulkPluCategories] = useState([]);
    const [defaultData, setDefaultData] = useState({
        name: '',
        description: '',
        long_description: '',
        regular_price: '',
        discounted_price: '',
        category: null,
        url_product: '',
        gallery_images: [],
        availability: true,
        barcode: generateBarcode()?.barcodeValue,
        details: [],
        type: "borne",
        stores: [], // Updated to handle store-specific availability
        isComposant: false,
        woocommerce_id: '',
        website_delivery: normalizeWebsiteDelivery(),
    });
    const [sitecategories, setSiteCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [isWooCategoryUpdate, setIsWooCategoryUpdate] = useState(false);
    const [kooulDialogOpen, setKooulDialogOpen] = useState(false);
    const [kooulPlateforms, setKooulPlateforms] = useState([]);
    const [selectedKooulPlateform, setSelectedKooulPlateform] = useState(null);
    const [selectedKooulCategoryPath, setSelectedKooulCategoryPath] = useState([]);
    const [isKooulSubmitting, setIsKooulSubmitting] = useState(false);
    const [aimerPlateforms, setAimerPlateforms] = useState([]);
    const [glovoPlatform, setGlovoPlatform] = useState(null);
    const [productAddons, setProductAddons] = useState([]);
    const [aimerStatusDialogOpen, setAimerStatusDialogOpen] = useState(false);
    const [aimerTargetProduct, setAimerTargetProduct] = useState(null);
    const [aimerRemoteProductId, setAimerRemoteProductId] = useState('');
    const [isAimerSubmitting, setIsAimerSubmitting] = useState(false);
    const [aimerCategoryDialogOpen, setAimerCategoryDialogOpen] = useState(false);
    const [aimerCategoryProduct, setAimerCategoryProduct] = useState(null);
    const [selectedAimerCategories, setSelectedAimerCategories] = useState([]);
    const [plateforms, setPlateforms] = useState([]);
    const [plateformFilters, setPlateformFilters] = useState([]);
    const [summaryPlatformFilter, setSummaryPlatformFilter] = useState(null);
    const [filtersModalOpen, setFiltersModalOpen] = useState(false);
    const [isMarjaneActionsOpen, setIsMarjaneActionsOpen] = useState(false);
    const [isCropModalOpen, setIsCropModalOpen] = useState(false);
    const [pendingCropFile, setPendingCropFile] = useState(null);
    const [glovoToggleModalProduct, setGlovoToggleModalProduct] = useState(null);
    const [glovoToggleModalProducts, setGlovoToggleModalProducts] = useState([]);
    const [publierPlateformesOpen, setPublierPlateformesOpen] = useState(false);
    const [publierPlateformesProduct, setPublierPlateformesProduct] = useState(null);
    const [promotionsModalOpen, setPromotionsModalOpen] = useState(false);
    const [glovoDiscounts, setGlovoDiscounts] = useState([]);
    const [bulkDisableModalOpen, setBulkDisableModalOpen] = useState(false);
    const [selectedBulkDisableStore, setSelectedBulkDisableStore] = useState(null);
    const [bulkDisableInProgress, setBulkDisableInProgress] = useState(false);
    const [bulkDisablePaused, setBulkDisablePaused] = useState(false);
    const [bulkDisableQueue, setBulkDisableQueue] = useState([]); // [{product, status: 'pending'|'in-progress'|'done'|'error', errorMsg?}]
    const [bulkDisableProgress, setBulkDisableProgress] = useState({ current: 0, total: 0, errors: [] });
    const [storeExpressDialogOpen, setStoreExpressDialogOpen] = useState(false);
    const [storeExpressProducts, setStoreExpressProducts] = useState([]);
    const [storeExpressSavingKey, setStoreExpressSavingKey] = useState('');
    const [wooSyncModalOpen, setWooSyncModalOpen] = useState(false);
    const [wooSyncRunning, setWooSyncRunning] = useState(false);
    const [wooSyncPaused, setWooSyncPaused] = useState(false);
    const [wooSyncStopped, setWooSyncStopped] = useState(false);
    const [wooSyncTab, setWooSyncTab] = useState(0);
    const [wooSyncProgress, setWooSyncProgress] = useState({ processed: 0, matched: 0, review: 0, missing: 0, updated: 0, total: 0, page: 0, totalPages: 0 });
    const [wooSyncMatched, setWooSyncMatched] = useState([]);
    const [wooSyncMissing, setWooSyncMissing] = useState([]);
    const [wooSyncErrors, setWooSyncErrors] = useState([]);
    const [wooSyncPreviewImage, setWooSyncPreviewImage] = useState(null);
    const [wooSyncSearchTerm, setWooSyncSearchTerm] = useState('');
    const [wooSyncPages, setWooSyncPages] = useState({ matched: 1, missing: 1, errors: 1 });
    const [wooLinkingIds, setWooLinkingIds] = useState([]);
    const [wooBulkLinking, setWooBulkLinking] = useState(false);
    const hasFetchedAllProducts = useRef(false);
    const wooSyncPauseRef = useRef(false);
    const wooSyncStopRef = useRef(false);
    const currentPageRef = useRef(currentPage);
    const hasVisibleProductsRef = useRef(Array.isArray(products) && products.length > 0);
    const productsPerPage = 20;

    const priceFormatter = useMemo(() => {
        try {
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'MAD',
                minimumFractionDigits: 2,
            });
        } catch (error) {
            console.warn('Currency formatter unavailable', error);
            return null;
        }
    }, []);

    const bootstrapModalSelectProps = useMemo(() => ({
        menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
        menuPosition: 'fixed',
        styles: {
            menuPortal: (base) => ({
                ...base,
                zIndex: 2600,
            }),
        },
    }), []);

    const updateLocalProduct = (updatedProduct) => {
        if (!updatedProduct) return;
        const normalizedId = normalizeId(updatedProduct._id || updatedProduct.id || updatedProduct._id);

        setProducts(prev => Array.isArray(prev) ? prev.map(p => (normalizeId(p._id) === normalizedId ? { ...p, ...updatedProduct } : p)) : prev);
        setPagedProducts(prev => Array.isArray(prev) ? prev.map(p => (normalizeId(p._id) === normalizedId ? { ...p, ...updatedProduct } : p)) : prev);
        setCurrentProduct(prev => (prev && normalizeId(prev._id) === normalizedId) ? { ...prev, ...updatedProduct } : prev);
    };

    const wooSyncPageSize = 8;
    const normalizedWooSyncSearch = useMemo(
        () => normalizeProductMatchName(wooSyncSearchTerm),
        [wooSyncSearchTerm]
    );
    const filteredWooSyncMatched = useMemo(() => {
        if (!normalizedWooSyncSearch) {
            return wooSyncMatched.filter((item) => item?.matchStatus !== 'review');
        }
        return wooSyncMatched.filter((item) =>
            item?.matchStatus !== 'review'
            && (
                normalizeProductMatchName(item?.wooName).includes(normalizedWooSyncSearch)
                || normalizeProductMatchName(item?.localName).includes(normalizedWooSyncSearch)
                || normalizeProductMatchName(item?.sku).includes(normalizedWooSyncSearch)
            )
        );
    }, [normalizedWooSyncSearch, wooSyncMatched]);
    const filteredWooSyncReview = useMemo(() => {
        const reviewItems = wooSyncMatched.filter((item) => item?.matchStatus === 'review');
        if (!normalizedWooSyncSearch) {
            return reviewItems;
        }
        return reviewItems.filter((item) =>
            normalizeProductMatchName(item?.wooName).includes(normalizedWooSyncSearch)
            || normalizeProductMatchName(item?.localName).includes(normalizedWooSyncSearch)
            || normalizeProductMatchName(item?.sku).includes(normalizedWooSyncSearch)
        );
    }, [normalizedWooSyncSearch, wooSyncMatched]);
    const filteredWooSyncMissing = useMemo(() => {
        if (!normalizedWooSyncSearch) {
            return wooSyncMissing;
        }
        return wooSyncMissing.filter((item) =>
            normalizeProductMatchName(item?.name).includes(normalizedWooSyncSearch)
            || normalizeProductMatchName(item?.sku).includes(normalizedWooSyncSearch)
        );
    }, [normalizedWooSyncSearch, wooSyncMissing]);
    const filteredWooSyncErrors = useMemo(() => {
        if (!normalizedWooSyncSearch) {
            return wooSyncErrors;
        }
        return wooSyncErrors.filter((item) =>
            normalizeProductMatchName(item?.wooName || item?.localName || item?.message).includes(normalizedWooSyncSearch)
        );
    }, [normalizedWooSyncSearch, wooSyncErrors]);
    const selectedProductIdSet = useMemo(
        () => new Set((Array.isArray(selectedProducts) ? selectedProducts : []).map((productId) => normalizeId(productId)).filter(Boolean)),
        [selectedProducts]
    );
    const selectedBulkStockProducts = useMemo(() => {
        const selectedIds = selectedProductIdSet;
        const sourceProducts = [
            ...(Array.isArray(products) ? products : []),
            ...(Array.isArray(pagedProducts) ? pagedProducts : []),
        ];
        const uniqueProducts = new Map();

        sourceProducts.forEach((product) => {
            const productId = normalizeId(product?._id || product?.id);
            if (productId && selectedIds.has(productId) && !uniqueProducts.has(productId)) {
                uniqueProducts.set(productId, product);
            }
        });

        return Array.from(uniqueProducts.values());
    }, [pagedProducts, products, selectedProductIdSet]);
    const selectedWooSyncMatchedPending = useMemo(
        () => wooSyncMatched.filter((item) => item?.matchStatus !== 'review' && selectedProductIdSet.has(normalizeId(item?.localId)) && !normalizeId(item?.localWooId)),
        [selectedProductIdSet, wooSyncMatched]
    );
    const wooSyncStrongPending = useMemo(
        () => wooSyncMatched.filter((item) => item?.matchStatus !== 'review' && !normalizeId(item?.localWooId) && Number(item?.matchScore || 0) >= 72),
        [wooSyncMatched]
    );

    useEffect(() => {
        const hasVisibleProducts = Array.isArray(products) && products.length > 0;
        hasVisibleProductsRef.current = hasVisibleProducts;
        writeCachedStoreQuickProducts(products);
    }, [products]);

    const addLocalProductAndGoToFirstPage = useCallback((newProduct) => {
        if (!newProduct) {
            return;
        }

        setProducts((prevProducts) => {
            const safeProducts = Array.isArray(prevProducts) ? prevProducts : [];
            const nextProducts = [...safeProducts, newProduct];
            setCurrentPage(1);
            return nextProducts;
        });

        setPagedProducts((prevProducts) => {
            const safeProducts = Array.isArray(prevProducts) ? prevProducts : [];
            return [newProduct, ...safeProducts].slice(0, productsPerPage);
        });

        setProductPagination((prev) => {
            const nextTotal = (prev?.total ?? 0) + 1;
            const nextTotalPages = Math.max(1, Math.ceil(nextTotal / productsPerPage));
            return {
                total: nextTotal,
                perPage: prev?.perPage ?? productsPerPage,
                pageNumber: nextTotalPages,
                totalPages: nextTotalPages,
            };
        });
    }, []);


    const parsePriceValue = useCallback((value) => {
        if (value === null || value === undefined) {
            return undefined;
        }
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : undefined;
        }
        if (typeof value === 'string') {
            const normalized = value
                .replace(/[^0-9,.-]/g, '')
                .replace(/,/g, '.');
            if (!normalized) {
                return undefined;
            }
            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : undefined;
        }
        return undefined;
    }, []);

    const formatCurrency = useCallback((value) => {
        if (typeof value !== 'number' || !Number.isFinite(value)) {
            return '';
        }
        if (priceFormatter) {
            return priceFormatter.format(value);
        }
        return `${value.toFixed(2)} MAD`;
    }, [priceFormatter]);

    const getProductCategoryIds = useCallback((product) => {
        const ids = new Set();

        const directCandidates = [
            product?.category,
            product?.category_id,
            product?.categoryId,
        ];

        directCandidates.forEach((candidate) => {
            const normalized = normalizeId(candidate);
            if (normalized) {
                ids.add(normalized);
            }
        });

        if (Array.isArray(product?.categories)) {
            product.categories.forEach((entry) => {
                const normalized = normalizeId(entry?._id || entry?.id || entry?.value || entry);
                if (normalized) {
                    ids.add(normalized);
                }
            });
        }

        return ids;
    }, []);

    const getProductPlateformIds = useCallback((product) => {
        const plateformEntries = Array.isArray(product?.plateforms) ? product.plateforms : [];

        const plateformIds = plateformEntries
            .map((entry) => normalizeId(entry?.plateform_id || entry?._id || entry?.id || entry?.value))
            .filter(Boolean);

        return new Set(plateformIds);
    }, []);

    const getProductKooulPlateformIds = useCallback((product) => {
        const plateformIds = new Set(getProductPlateformIds(product));

        if (product?.kooul?.plateform_id) {
            plateformIds.add(normalizeId(product.kooul.plateform_id));
        }

        if (product?.kooul?.plateformId) {
            plateformIds.add(normalizeId(product.kooul.plateformId));
        }

        return plateformIds;
    }, [getProductPlateformIds]);

    const aimerPlateformId = useMemo(() => {
        const firstAimer = Array.isArray(aimerPlateforms) ? aimerPlateforms[0] : null;
        if (!firstAimer) {
            return '';
        }

        return normalizeId(firstAimer?.optionValue || firstAimer?._id || firstAimer?.id || firstAimer?.plateform_id);
    }, [aimerPlateforms]);

    const kooulPlateformOptions = useMemo(() => (
        Array.isArray(kooulPlateforms)
            ? kooulPlateforms.map((plateform) => {
                const optionValue = normalizeId(plateform._id || plateform.id || plateform.plateform_id);
                return {
                    ...plateform,
                    optionValue,
                    optionLabel: plateform?.name || optionValue || 'Plateforme Kooul',
                };
            })
            : []
    ), [kooulPlateforms]);

    const pegasusCategories = useMemo(
        () => (Array.isArray(productCategories)
            ? productCategories.filter((category) => category?.type === 'pegasus')
            : []),
        [productCategories]
    );

    const aimerCategories = useMemo(
        () => (Array.isArray(productCategories)
            ? productCategories.filter((category) => `${category?.type ?? ''}`.toLowerCase() === 'aimer')
            : []),
        [productCategories]
    );

    const glovoPlateformIds = useMemo(() => (
        new Set(
            (Array.isArray(plateforms) ? plateforms : [])
                .filter((plateform) => ['glovo', 'glovo_sisters'].includes(`${plateform?.type ?? ''}`.toLowerCase()))
                .map((plateform) => normalizeId(plateform._id || plateform.id || plateform.plateform_id))
                .filter(Boolean)
        )
    ), [plateforms]);

    const kooulPlateformIds = useMemo(() => (
        Array.isArray(kooulPlateforms)
            ? new Set(
                kooulPlateforms
                    .map((plateform) => normalizeId(plateform._id || plateform.id || plateform.plateform_id))
                    .filter(Boolean)
            )
            : new Set()
    ), [kooulPlateforms]);

    const availableKooulPlateformOptions = useMemo(() => {
        if (!currentProduct) {
            return kooulPlateformOptions;
        }

        const existingIds = getProductKooulPlateformIds(currentProduct);

        return kooulPlateformOptions.filter((option) => !existingIds.has(option.optionValue));
    }, [currentProduct, getProductKooulPlateformIds, kooulPlateformOptions]);

    const kooulPlateformStoreLabels = useMemo(() => {
        if (!selectedKooulPlateform) {
            return [];
        }

        const storeEntries = Array.isArray(selectedKooulPlateform?.store_ids)
            ? selectedKooulPlateform.store_ids
            : [];

        return storeEntries
            .map((entry) => {
                const storeId = normalizeId(entry?.store_id || entry);
                const matchingStore = stores.find((store) =>
                    store.value === storeId || store.rawId === storeId || store.storeCode === storeId
                );
                return matchingStore?.label || storeId;
            })
            .filter(Boolean);
    }, [selectedKooulPlateform, stores]);

    const plateformFilterOptions = useMemo(() => {
        if (!Array.isArray(plateforms)) {
            return [];
        }

        return plateforms
            .map((plateform) => {
                const value = normalizeId(plateform?._id || plateform?.id || plateform?.plateform_id || plateform?.value);
                if (!value) {
                    return null;
                }

                const label = plateform?.name || value || 'Plateforme';
                const storeLabels = Array.isArray(plateform?.store_ids)
                    ? plateform.store_ids
                        .map((storeEntry) => {
                            const storeId = normalizeId(storeEntry?.store_id || storeEntry);
                            if (!storeId) {
                                return null;
                            }

                            const matchingStore = stores.find((store) => (
                                store?.value === storeId || store?.rawId === storeId || store?.storeCode === storeId
                            ));

                            return matchingStore?.label || storeId;
                        })
                        .filter(Boolean)
                    : [];

                return { value, label, storeLabels };
            })
            .filter(Boolean);
    }, [plateforms, stores]);

    const kooulCategories = useMemo(() => {
        const plateformId = normalizeId(selectedKooulPlateform?.optionValue || selectedKooulPlateform?._id || selectedKooulPlateform?.id);
        return productCategories.filter((category) => {
            if (category.type !== 'kooul') {
                return false;
            }
            if (!plateformId) {
                return true;
            }
            return normalizeId(category.plateform_id) === plateformId;
        });
    }, [productCategories, selectedKooulPlateform]);

    const getKooulChildren = useCallback((parentId) => {
        const normalizedParent = normalizeId(parentId);
        return kooulCategories.filter((category) => normalizeId(category.parent_id) === normalizedParent);
    }, [kooulCategories]);

    const kooulCategoryLevels = useMemo(() => {
        if (!selectedKooulPlateform) {
            return [];
        }

        const levels = [];
        let parentId = '';
        let levelIndex = 0;

        while (true) {
            const options = getKooulChildren(parentId);
            if (!options.length) {
                break;
            }

            const selectedId = selectedKooulCategoryPath[levelIndex];
            const selected = selectedId
                ? options.find((option) => normalizeId(option._id) === selectedId) || null
                : null;

            levels.push({ options, selected });

            if (!selected) {
                break;
            }

            parentId = normalizeId(selected._id);
            const hasChildren = kooulCategories.some((category) => normalizeId(category.parent_id) === parentId);
            if (!hasChildren) {
                break;
            }

            levelIndex += 1;
        }

        return levels;
    }, [getKooulChildren, kooulCategories, selectedKooulCategoryPath, selectedKooulPlateform]);

    const selectedKooulCategoryId = selectedKooulCategoryPath[selectedKooulCategoryPath.length - 1];

    const selectedKooulCategory = useMemo(
        () => kooulCategories.find((category) => normalizeId(category._id) === selectedKooulCategoryId) || null,
        [kooulCategories, selectedKooulCategoryId]
    );

    const selectedKooulCategoryHasChildren = useMemo(() => {
        if (!selectedKooulCategory) {
            return false;
        }
        const categoryId = normalizeId(selectedKooulCategory._id);
        return kooulCategories.some((category) => normalizeId(category.parent_id) === categoryId);
    }, [kooulCategories, selectedKooulCategory]);

    const fetchPlateformsData = useCallback(async () => {
        try {
            const data = await fetchAllPlateforms();
            const normalized = Array.isArray(data) ? data : [];

            const kooulOnly = normalized.filter((plateform) => {
                const name = (plateform?.name || '').toLowerCase();
                const type = (plateform?.type || '').toLowerCase();
                return type === 'kooul' || name.startsWith('kooul_');
            });

            const aimerOnly = normalized.filter((plateform) => {
                const name = (plateform?.name || '').toLowerCase();
                const type = (plateform?.type || '').toLowerCase();
                return type === 'aimer' || name.includes('aimer');
            });

            const glovoPortal = normalized.find((plateform) => {
                const type = (plateform?.type || '').toLowerCase();
                return type === 'glovo' && plateform?.additionnal_data?.isportal === true;
            });

            setPlateforms(normalized);
            setKooulPlateforms(kooulOnly);
            setAimerPlateforms(aimerOnly);
            setGlovoPlatform(glovoPortal || null);
        } catch (error) {
            console.error('Erreur lors du chargement des plateformes', error);
            toast.error("Impossible de charger les plateformes");
        }
    }, []);

    const fetchGlovoDiscounts = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            params.append('limit', '100');
            params.append('status', 'active');
            
            const response = await axios.get(
                `${process.env.REACT_APP_PEGASUS_API_URL}/discounts?${params.toString()}`,
                header()
            );

            if (response.data.success) {
                setGlovoDiscounts(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching Glovo discounts:', error);
            setGlovoDiscounts([]);
        }
    }, []);

    const fetchAddons = useCallback(async () => {
        try {
            const addonsData = await ProductAddonService.fetchProductAddons();
            setProductAddons(addonsData || []);
        } catch (error) {
            console.error('Error fetching product addons:', error);
            toast.error('Erreur lors du chargement des addons');
        }
    }, []);

    const printProductBarcode = useCallback((product) => {
        if (!product || typeof window === 'undefined') {
            return;
        }

        const includePrice = window.confirm(
            'Inclure le prix sur le ticket ?\n\nCliquez sur "OK" pour imprimer avec le prix.\nCliquez sur "Annuler" pour imprimer sans le prix.'
        );

        const escapeHtml = (value = '') =>
            String(value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');

        const resolvePrice = () => {
            if (!includePrice) {
                return undefined;
            }
            const candidates = [
                product?.regular_price,
                product?.regularPrice,
                product?.price,
                product?.price_ht,
                product?.priceHT,
                product?.price_ttc,
                product?.priceTTC,
                product?.discounted_price,
                product?.discountedPrice,
            ];
            for (const candidate of candidates) {
                const parsed = parsePriceValue(candidate);
                if (typeof parsed === 'number') {
                    return parsed;
                }
            }
            return undefined;
        };

        const name = product?.name?.trim() || 'Produit';
        const priceValue = resolvePrice();
        const formattedPrice = includePrice ? formatCurrency(priceValue) : '';
        const barcodeValue = product?.barcode ? String(product.barcode).trim() : '';
        const barcodeSvg = barcodeValue ? generateBarcodeFromValue(barcodeValue) : '';
        const fallbackBarcodeText = barcodeValue || 'Aucun code-barres disponible';
        const printWindow = window.open('', '_blank', 'width=600,height=400');

        if (!printWindow) {
            return;
        }

        const priceMarkup = includePrice && formattedPrice
            ? `<div class="price">${escapeHtml(formattedPrice)}</div>`
            : '';
        const barcodeMarkup = barcodeSvg
            ? `<div class="barcode">${barcodeSvg}</div>`
            : `<div class="barcode barcode--text">${escapeHtml(fallbackBarcodeText)}</div>`;

        printWindow.document.open();
        printWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Impression code-barres - ${escapeHtml(name)}</title>
    <style>
      @page { size: 50mm 25mm; margin: 0; }
      html, body {
        margin: 0;
        padding: 0;
        width: 50mm;
        height: 25mm;
      }
      body {
        font-family: 'Roboto', Arial, sans-serif;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
      }
      .label {
        box-sizing: border-box;
        width: 50mm;
        height: 25mm;
        padding: 2mm;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        gap: 1mm;
      }
      .name {
        font-size: 10pt;
        font-weight: 600;
        margin: 5px 0 0 0;
        line-height: 1.2;
      }
      .price {
        font-size: 9pt;
        font-weight: 500;
        margin: 0;
      }
      .barcode {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .barcode svg {
        width: 90%;
        max-width: 44mm;
        height: auto;
        max-height: 14mm;
      }
      .barcode--text {
        font-size: 8pt;
        letter-spacing: 1px;
      }
    </style>
  </head>
  <body>
    <div class="label">
      <div class="name">${escapeHtml(name)}</div>
      ${priceMarkup}
      ${barcodeMarkup}
    </div>
  </body>
</html>`);
        printWindow.document.close();

        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
        }, 100);
    }, [formatCurrency, parsePriceValue]);
    const updateIsComponentStatus = async (productId, isComponentValue) => {
        if (!productId) {
            return false;
        }
        try {
            await axios.patch(
                `${API_URL}/product/${productId}/iscomponent`,
                { isComposant: Boolean(isComponentValue) },
                header({ "Content-Type": "application/json" })
            );
            return true;
        } catch (error) {
            const message = error?.response?.data?.message || error.message;
            toast.error(`Impossible de mettre à jour le statut composant : ${message}`);
            return false;
        }
    };

    const handleSiteCategoryChange = (cats) => {
        if (!cats) {
            setSelectedCategories([]);
            return;
        }
        let updated = [...cats];
        cats.forEach(cat => {
            const option = sitecategories.find(c => c.value === cat.value);
            if (option && option.parent && option.parent !== 0) {
                const parentOpt = sitecategories.find(c => c.value === option.parent);
                if (parentOpt && !updated.some(s => s.value === parentOpt.value)) {
                    updated.push(parentOpt);
                }
            }
        });
        setSelectedCategories(updated);
    };
    const normalizeComponentEntries = (rawComponents) => {
        if (!Array.isArray(rawComponents) || rawComponents.length === 0) {
            return [{ product: '', quantity: '1' }];
        }

        const normalized = rawComponents
            .map((item) => {
                const rawReference = item?.product ?? item?.component ?? item?.componentId ?? '';
                const productId = typeof rawReference === 'object'
                    ? rawReference?._id || rawReference?.id || rawReference?.product || rawReference?.component
                    : rawReference;
                const quantityValue = item?.quantity;
                const quantityString =
                    quantityValue === undefined || quantityValue === null
                        ? '1'
                        : String(quantityValue);

                return {
                    product: productId ? String(productId) : '',
                    quantity: quantityString,
                    component: productId ? String(productId) : '',
                };
            })
            .filter((entry) => entry.product);

        return normalized.length ? normalized : [{ product: '', quantity: '1' }];
    };

    const deriveAvailableComponents = useCallback((items) => {
        if (!Array.isArray(items)) return [];

        return items
            .map((item) => {
                const id = item?._id ?? item?.id ?? item?.product ?? item?.component;
                const name = item?.name ?? item?.label;
                if (!id || !name) {
                    return null;
                }

                const primaryImage =
                    item?.url_product ||
                    (Array.isArray(item?.gallery_images) ? item.gallery_images[0] : null) ||
                    item?.picture ||
                    item?.image;

                return {
                    ...item,
                    _id: String(id),
                    name,
                    imageUrl: primaryImage,
                    isComposant: item?.isComposant === true,
                };
            })
            .filter((entry) => entry && entry.isComposant);
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        long_description: '',
        regular_price: '',
        discounted_price: '',
        url_product: '',
        gallery_images: [],
        barcode: generateBarcode()?.barcodeValue,
        category: null,
        availability: true,
        details: [],
        type: "borne",
        stores: [], // Updated to handle store-specific availability
        isComposant: false,
        woocommerce_id: '',
        website_delivery: normalizeWebsiteDelivery(),
    });
    const [showLongDescription, setShowLongDescription] = useState(false);
    const [deliveryZoneOptions, setDeliveryZoneOptions] = useState([]);
    const [componentFilter, setComponentFilter] = useState([]);
    const [ispublishing, setIsPublishing] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null); // New state for editing product
    const [cloningSourceProduct, setCloningSourceProduct] = useState(null);
    const pictureInputRef = useRef(null);
    const galleryInputRef = useRef(null);
    const [formImagePreview, setFormImagePreview] = useState('');
    const [galleryPictures, setGalleryPictures] = useState([]);
    const [galleryPreviewUrls, setGalleryPreviewUrls] = useState([]);
    const resolvePrefillImageUrl = useCallback((path) => {
        if (!path) {
            return '';
        }

        const normalized = `${path}`.replace(/^\//, '');
        if (normalized.startsWith('http')) {
            return normalized;
        }

        return `${process.env.REACT_APP_DATA_PIC_PATH}/${normalized}`;
    }, []);
    const syncPictureInputFile = useCallback((file) => {
        if (!file || !pictureInputRef.current || typeof DataTransfer === 'undefined') {
            return;
        }

        try {
            const transfer = new DataTransfer();
            transfer.items.add(file);
            pictureInputRef.current.files = transfer.files;
        } catch (error) {
            console.warn('Unable to sync picture input file', error);
        }
    }, []);
    const resetPictureField = useCallback(() => {
        setPicture(null);
        setPendingCropFile(null);
        if (pictureInputRef.current) {
            pictureInputRef.current.value = null;
        }
    }, []);
    const resetGalleryField = useCallback(() => {
        setGalleryPictures([]);
        if (galleryInputRef.current) {
            galleryInputRef.current.value = null;
        }
    }, []);
    const preloadPictureFromImageSource = useCallback(async (imageSource) => {
        const resolvedImageUrl = resolvePrefillImageUrl(imageSource);
        if (!resolvedImageUrl) {
            return;
        }

        try {
            const response = await fetch(resolvedImageUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch image (${response.status})`);
            }

            const blob = await response.blob();
            const fileNameFromUrl = resolvedImageUrl.split('/').pop()?.split('?')[0] || 'processed-image.jpg';
            const hasExtension = /\.[a-z0-9]+$/i.test(fileNameFromUrl);
            const mimeExtension = blob.type?.split('/')[1] || 'jpg';
            const fileName = hasExtension ? fileNameFromUrl : `${fileNameFromUrl}.${mimeExtension}`;
            const file = new File([blob], fileName, {
                type: blob.type || 'image/jpeg',
                lastModified: Date.now(),
            });

            setPicture(file);
            setPendingCropFile(null);
            syncPictureInputFile(file);
        } catch (error) {
            console.warn('Unable to preload processed image as file input', error);
        }
    }, [resolvePrefillImageUrl, syncPictureInputFile]);
    const openProductDraftModal = useCallback((draftProduct) => {
        if (!draftProduct) {
            return;
        }

        resetPictureField();
        resetGalleryField();
        setEditingProduct(null);
        setCloningSourceProduct(null);
        setCurrentProduct(draftProduct);
        setDefaultData(draftProduct);
        setFormData(draftProduct);
        setShowLongDescription(Boolean(`${draftProduct?.long_description || ''}`.trim()));
        setComponents([{ product: '', quantity: '1' }]);

        if (typeof window !== 'undefined') {
            window.setTimeout(() => {
                const modalElement = document.getElementById('crudModal');
                if (modalElement && window.bootstrap?.Modal) {
                    const modalInstance = window.bootstrap.Modal.getInstance(modalElement)
                        || new window.bootstrap.Modal(modalElement);
                    modalInstance.show();
                }
            }, 0);
        }
    }, [resetGalleryField, resetPictureField]);

    useEffect(() => {
        const stateImage = location.state?.image;
        if (!stateImage) {
            return;
        }

        if (location.state?.openAddProduct) {
            const draftProduct = {
                name: '',
                description: '',
                long_description: '',
                regular_price: '',
                discounted_price: '',
                category: null,
                url_product: stateImage,
                availability: true,
                barcode: generateBarcode()?.barcodeValue,
                details: [],
                type: "borne",
                stores: [],
                isComposant: false,
                gallery_images: [],
                website_delivery: normalizeWebsiteDelivery(),
            };

            openProductDraftModal(draftProduct);
            preloadPictureFromImageSource(stateImage);
            navigate(location.pathname, { replace: true, state: null });
            return;
        }

        setFormData(prev => ({ ...prev, url_product: stateImage }));
        preloadPictureFromImageSource(stateImage);
    }, [location.pathname, location.state, navigate, openProductDraftModal, preloadPictureFromImageSource]);

    useEffect(() => {
        if (picture) {
            const previewUrl = URL.createObjectURL(picture);
            setFormImagePreview(previewUrl);
            return () => {
                URL.revokeObjectURL(previewUrl);
            };
        }

        setFormImagePreview(resolvePrefillImageUrl(formData?.url_product));
    }, [formData?.url_product, picture, resolvePrefillImageUrl]);
    useEffect(() => {
        if (!galleryPictures.length) {
            setGalleryPreviewUrls([]);
            return undefined;
        }

        const previewUrls = galleryPictures.map((file) => URL.createObjectURL(file));
        setGalleryPreviewUrls(previewUrls);

        return () => {
            previewUrls.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
        };
    }, [galleryPictures]);

    useEffect(() => {
        currentPageRef.current = currentPage;
    }, [currentPage]);

    useEffect(() => {
        if (promotionsModalOpen) {
            fetchGlovoDiscounts();
        }
    }, [promotionsModalOpen, fetchGlovoDiscounts]);
    useEffect(() => {
        const fetchBulkPluCategories = async () => {
            try {
                const pluVars = await VariableService.listVariables('plu');
                const pluVar = Array.isArray(pluVars)
                    ? pluVars.find((item) => item?.name && item.name.trim().toLowerCase().includes('plu carrefour'))
                    : null;

                if (!pluVar?.value) {
                    setBulkPluCategories([]);
                    return;
                }

                const categories = JSON.parse(pluVar.value);
                setBulkPluCategories(Array.isArray(categories) ? categories : []);
            } catch (error) {
                console.warn('Unable to load PLU categories for bulk stock modal', error);
                setBulkPluCategories([]);
            }
        };

        fetchBulkPluCategories();
    }, []);
    useEffect(() => {
        setDefaultData(prev => ({
            ...prev,
            barcode: generateBarcode()?.barcodeValue,
            isComposant: false,
        }));

        setPicture(null);
        setGalleryPictures([]);

    }, [currentProduct, editingProduct]);
    useEffect(() => {
        setAvailableComponents(deriveAvailableComponents(products));
    }, [products, deriveAvailableComponents]);
    useEffect(() => {
        if (!selectedKooulPlateform && availableKooulPlateformOptions.length === 1) {
            setSelectedKooulPlateform(availableKooulPlateformOptions[0]);
        }

        if (selectedKooulPlateform) {
            const stillAvailable = availableKooulPlateformOptions.some(
                (option) => option.optionValue === selectedKooulPlateform.optionValue
            );
            if (!stillAvailable) {
                setSelectedKooulPlateform(null);
            }
        }
    }, [availableKooulPlateformOptions, selectedKooulPlateform]);
    useEffect(() => {
        if (!stockModalProduct) {
            return;
        }

        if (stockMovementForm.storeId) {
            return;
        }

        const fallbackStoreId = resolveDefaultStoreId(stockModalProduct);
        if (fallbackStoreId) {
            setStockMovementForm((prev) => ({
                ...prev,
                storeId: fallbackStoreId,
            }));
        }
    }, [stockModalProduct, stockMovementForm.storeId, stores]);
    useEffect(() => {
        const modalElement = document.getElementById('stockMovementModal');
        if (!modalElement) {
            return;
        }

        const handleHidden = () => {
            setStockModalProduct(null);
            setStockMovementForm({ storeId: '', quantity: '1' });
            setIsSubmittingStockMovement(false);
        };

        modalElement.addEventListener('hidden.bs.modal', handleHidden);
        return () => {
            modalElement.removeEventListener('hidden.bs.modal', handleHidden);
        };
    }, []);
    const getWooCategories = async () => {
        const categoriesWoo = await getCategoriesWooCommerce();
        setSiteCategories(categoriesWoo);
        return categoriesWoo;
    };
    const fetchWebsiteDeliveryZones = async () => {
        try {
            const response = await axios.get(`${API_URL}/woocommerce/delivery-settings/admin`, header());
            const settings = response?.data?.response?.settings || {};
            const sourceZones = [
                ...(Array.isArray(settings.shipping_zones) ? settings.shipping_zones : []),
                ...(Array.isArray(settings.map_zones) ? settings.map_zones : []),
            ];
            const seen = new Set();
            const zoneOptions = sourceZones
                .map((zone) => ({
                    value: String(zone?.code || '').trim().toUpperCase(),
                    label: String(zone?.name || zone?.code || '').trim(),
                }))
                .filter((zone) => {
                    if (!zone.value || seen.has(zone.value)) {
                        return false;
                    }
                    seen.add(zone.value);
                    return true;
                });
            setDeliveryZoneOptions(zoneOptions);
        } catch (error) {
            console.warn('Unable to load delivery zones for product flags', error);
            setDeliveryZoneOptions([]);
        }
    };
    const siteHandleModal = (currProd) => {

        setIsWooCategoryUpdate(false);
        setSelectedCategories([]);
        setCurrentProduct(currProd);
        const currenProdStore = stores.find(
            x => x.type === "site");

        if (currenProdStore && currProd?.regular_price) {
            const feesamount = calculateFees(currenProdStore.fees.type, currenProdStore.fees.value, currProd?.regular_price);
            setFees(feesamount ?? 0);
        } else {
            setFees(0);
        }

    };

    const handleWooCategoryEdit = async (product) => {
        setIsWooCategoryUpdate(true);
        setCurrentProduct(product);
        setFees(0);
        try {
            const categoriesList = sitecategories.length ? sitecategories : await getWooCategories();
            if (!product?.woocommerce_id) {
                toast.error("Aucun produit WooCommerce sélectionné");
                return;
            }

            const wooProduct = await getWooCommerceProduct(product.woocommerce_id);
            if (!wooProduct?.categories) {
                setSelectedCategories([]);
                return;
            }

            const mappedCategories = [];
            wooProduct.categories.forEach((cat) => {
                const existing = categoriesList.find((c) => c.value === cat.id);
                const option = existing || { value: cat.id, label: cat.name, parent: cat.parent ?? 0 };
                if (!mappedCategories.some((c) => c.value === option.value)) {
                    mappedCategories.push(option);
                }
                if (option.parent && option.parent !== 0) {
                    const parentOpt = categoriesList.find((c) => c.value === option.parent);
                    if (parentOpt && !mappedCategories.some((c) => c.value === parentOpt.value)) {
                        mappedCategories.push(parentOpt);
                    }
                }
            });
            setSelectedCategories(mappedCategories);
        } catch (error) {
            const message = error?.response?.data?.message || error.message;
            toast.error(`Erreur lors du chargement des catégories WooCommerce : ${message}`);
        }
    };
    const handleexportAimer = () => {
        const selectedItems = products.filter(prod => selectedProducts.includes(prod._id));
        (async () => {
            try {
                const response = await axios.post(
                    `${API_URL}/products/aimer`, // Replace with your endpoint
                    { products_id: selectedProducts },
                    header()
                );
                if (response.data.success) {
                    toast.success("Produits aimés avec succès !");

                } else {
                    toast.error(response.data.message || "Erreur inconnue");
                }
            } catch (error) {
                toast.error("Erreur réseau : " + (error.response?.data?.message || error.message));
            }
        })();
        const exportData = selectedItems.map(product => {
            const categoryId =
                typeof product.category === "object" && product.category !== null && product.category.$oid
                    ? product.category.$oid
                    : product.category;
            const foundCategory = productCategories.find(
                (cat) =>
                    cat._id === categoryId || (cat.value && cat.value === categoryId)
            );

            return {
                "Image du produit": product.url_product ? (process.env.REACT_APP_DATA_PIC_PATH + `/${product.url_product}`) : '',
                "Nom": product.name || '',
                "Quantité totale": product.stores?.reduce((sum, store) => sum + (store.stock || 0), 0) || 0,
                "Prix ttc": product.discounted_price || product.regular_price || '',
                "À propos du produit (Description)": product.description || '',
                "Nom du produit : Arabe": product.name_ar || '',
                "Description du produit : Arabe": product.description_ar || '',
                "Autres images 1": product.images?.[0] || '',
                "Autres images 2": product.images?.[1] || '',
                "Autres images 3": product.images?.[2] || '',
                "Autres images 4": product.images?.[3] || '',
                "Dimensions": product.dimensions || '',
                "delais de preparation (h)": product.preparation_delay || '',
                "Categorie": foundCategory?.name || foundCategory?.label || '',
                "Options": product.details?.map(d => d.name).join(', ') || '',
                "Remarque": product.remarque || '',
            }
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Produits Sélectionnés");
        XLSX.writeFile(workbook, "export-produits.xlsx");
    };

    const handleExportCatalog = () => {
        const selectedItems = products.filter(prod => selectedProducts.includes(prod._id));
        if (!selectedItems.length) {
            toast.error('Aucun produit sélectionné à exporter.');
            return;
        }

        const exportData = selectedItems.map((product) => {
            const categoryId =
                typeof product.category === "object" && product.category !== null && product.category.$oid
                    ? product.category.$oid
                    : product.category;
            const foundCategory = productCategories.find(
                (cat) =>
                    cat._id === categoryId ||
                    cat.value === categoryId ||
                    cat.id === categoryId
            );

            return {
                "Image URL": resolveImageUrl(product.url_product),
                "Nom": product.name || '',
                "Prix": product.discounted_price ?? product.regular_price ?? '',
                "Catégorie": foundCategory?.name || foundCategory?.label || '',
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Catalogue");
        XLSX.writeFile(workbook, "export-catalogue.xlsx");
    };


    const buildWooWebsiteImages = (product) => {
        const orderedSources = [
            product?.url_product,
            ...(Array.isArray(product?.gallery_images) ? product.gallery_images : []),
        ];

        const seen = new Set();
        const images = orderedSources.reduce((acc, imagePath) => {
            const resolved = resolveImageUrl(imagePath);
            if (!resolved) {
                return acc;
            }

            const normalized = resolved.trim();
            if (!normalized || seen.has(normalized)) {
                return acc;
            }

            seen.add(normalized);
            acc.push(normalized);
            return acc;
        }, []);

        return {
            primaryImage: images[0] || '',
            images,
        };
    };

    const addinWebsite = async (categoriesParam = selectedCategories, feesParam = fees, options = {}) => {
        const targetProduct = options?.product || currentProduct;
        try {
            if (!targetProduct?.barcode) {
                toast.error("Un code à bar est nécessaire pour l'ajout de ce produit sur le site");
                throw new Error("Un code à bar est nécessaire pour l'ajout de ce produit sur le site");
            }
            toast.info("Veuillez patienter, votre produit est en cours d'ajout.");
            const { primaryImage, images } = buildWooWebsiteImages(targetProduct);
            const rawPrice = options?.price ?? Number(targetProduct.regular_price || 0) + Number(feesParam || 0);
            const parsedPrice = Number(rawPrice);
            const sitePrice = Number.isFinite(parsedPrice) ? parsedPrice : Number(targetProduct.regular_price || 0) + Number(feesParam || 0);
            const inStock = options?.inStock !== false;

            // Debug: log categories shape before sending
            try { console.debug('addinWebsite: categoriesParam=', JSON.parse(JSON.stringify(categoriesParam || []))); } catch(e) {}

            const siteLongDescription = String(
                targetProduct.long_description || targetProduct.description || ''
            ).trim();
            const siteShortDescription = String(targetProduct.description || '').trim();

            const data = {
                name: targetProduct.name,
                type: 'simple',
                regular_price: sitePrice,
                description: siteLongDescription,
                short_description: siteShortDescription,
                long_description: siteLongDescription,
                categories: (categoriesParam || []).map(cat => ({ id: cat.value })),
                image: primaryImage,
                images,
                manage_stock: false,
                stock_status: inStock ? 'instock' : 'outofstock',
                barcode: targetProduct?.barcode ?? generateBarcode()?.barcodeValue,
                meta_data: websitePublicationMeta(targetProduct),
            };
            // Debug: log final payload (without sensitive fields)
            try { const logData = { ...data, categories: data.categories }; console.debug('addinWebsite: final payload', JSON.parse(JSON.stringify(logData))); } catch(e) {}
            const response = await createWooCommerceProduct(data);

            if (response.status == 500) {
                toast.error("Erreur lors l'ajout du produit au Site");
                throw new Error("Erreur lors l'ajout du produit au Site");
            } else {
                toast.success("Produit ajouté avec succès au site " + targetProduct.name);
                if (response.status == 201 && response.data) {
                    await axios.post(
                        `${API_URL}/products/${targetProduct._id}/woocommerce`,
                        { idwoo: response.data.id },
                        header()
                    ).then(response_ => {
                        if (response_.data.success) {
                            const updatedProduct = { ...targetProduct, woocommerce_id: response.data.id };
                            
                            // Update in products list
                            setProducts(prevProducts =>
                                prevProducts.map(product =>
                                    product._id === targetProduct._id
                                        ? updatedProduct
                                        : product
                                )
                            );
                            
                            // Update paged products
                            setPagedProducts(prevProducts =>
                                prevProducts.map(product =>
                                    product._id === targetProduct._id
                                        ? updatedProduct
                                        : product
                                )
                            );
                            
                            // Update publier plateformes modal product if open
                            if (publierPlateformesProduct?._id === targetProduct._id) {
                                setPublierPlateformesProduct(updatedProduct);
                            }

                            toast.success(response_.data.message);
                        } else {
                            toast.error(response_.data.message);
                        }
                    });
                }
            }

        } catch (ex) {
            throw ex;
        } finally {
            if (!options?.keepSelection) {
                setCurrentProduct(null);
                setSelectedCategories([]);
                setFees(0);
            }
        }

    }



    const updateWooCategories = async (categoriesParam = selectedCategories, options = {}) => {
        const targetProduct = options?.product || currentProduct;
        if (!targetProduct?.woocommerce_id) {
            toast.error("Aucun produit WooCommerce sélectionné");
            return;
        }
        try {
            try { console.debug('updateWooCategories: categoriesParam=', JSON.parse(JSON.stringify(categoriesParam || []))); } catch(e) {}
            const siteLongDescription = String(
                targetProduct?.long_description || targetProduct?.description || ''
            ).trim();
            const siteShortDescription = String(targetProduct?.description || '').trim();
            const { images } = buildWooWebsiteImages(targetProduct);
            const parsedPrice = Number(options?.price);
            const response = await updateWooCommerceProductCategories(
                targetProduct.woocommerce_id,
                (categoriesParam || []).map((cat) => ({ id: cat.value })),
                {
                    description: siteLongDescription,
                    ...(siteShortDescription ? { short_description: siteShortDescription } : {}),
                    ...(Number.isFinite(parsedPrice) ? { regular_price: String(parsedPrice) } : {}),
                    meta_data: websitePublicationMeta(targetProduct),
                    manage_stock: false,
                    stock_status: options?.inStock === false ? 'outofstock' : 'instock',
                    ...(images.length ? { images: images.map((src) => ({ src })) } : {}),
                }
            );
            if (response?.status >= 200 && response?.status < 300) {
                toast.success("Produit WooCommerce mis à jour avec succès");
                if (!options?.keepSelection) {
                    setIsWooCategoryUpdate(false);
                    setSelectedCategories([]);
                    setCurrentProduct(null);
                }
            } else {
                toast.error("Impossible de mettre à jour les catégories WooCommerce");
                throw new Error("Impossible de mettre à jour les catégories WooCommerce");
            }
        } catch (error) {
            const message = error?.response?.data?.message || error.message;
            toast.error(`Erreur lors de la mise à jour des catégories : ${message}`);
            throw error;
        }
    };

    const handleKooulPlateformChange = (_, value) => {
        setSelectedKooulPlateform(value);
        setSelectedKooulCategoryPath([]);
    };

    const handleKooulCategoryChange = (levelIndex, category) => {
        const categoryId = category ? normalizeId(category._id || category.id || category.value) : '';
        setSelectedKooulCategoryPath((prev) => {
            const next = prev.slice(0, levelIndex);
            if (categoryId) {
                next[levelIndex] = categoryId;
            }
            return next;
        });
    };

    const selectedKooulStoreId = useMemo(() => {
        if (!selectedKooulPlateform) {
            return '';
        }

        const additionnalStoreId = selectedKooulPlateform?.additionnal_data?.kooul?.store_id;
        const nameStoreId = typeof selectedKooulPlateform?.name === 'string'
            && selectedKooulPlateform.name.toLowerCase().startsWith('kooul_')
            ? selectedKooulPlateform.name.slice('kooul_'.length)
            : '';

        const storeIdsArray = Array.isArray(selectedKooulPlateform?.store_ids) ? selectedKooulPlateform.store_ids : [];
        const firstStoreId = storeIdsArray.length
            ? normalizeId(storeIdsArray[0]?.store_id || storeIdsArray[0])
            : '';

        return String(additionnalStoreId || nameStoreId || firstStoreId || '').trim();
    }, [selectedKooulPlateform]);

    const handleOpenKooulDialog = (product) => {
        setCurrentProduct(product);
        setKooulDialogOpen(true);
        setSelectedKooulPlateform(null);
        setSelectedKooulCategoryPath([]);
    };

    const handleCloseKooulDialog = () => {
        setKooulDialogOpen(false);
        setSelectedKooulCategoryPath([]);
        setIsKooulSubmitting(false);
    };

    const handleKooulSubmit = async () => {
        if (!currentProduct?._id) {
            toast.error('Aucun produit sélectionné');
            return;
        }

        if (!selectedKooulPlateform) {
            toast.warning('Veuillez choisir une plateforme Kooul.');
            return;
        }

        if (!selectedKooulCategory) {
            toast.warning('Veuillez sélectionner la catégorie Kooul la plus précise.');
            return;
        }

        if (selectedKooulCategoryHasChildren) {
            toast.warning('Veuillez descendre jusqu\'à la dernière sous-catégorie.');
            return;
        }

        const payload = {
            product_id: currentProduct._id,
            plateform_id: selectedKooulPlateform.optionValue || normalizeId(selectedKooulPlateform._id || selectedKooulPlateform.id),
            category_id: normalizeId(selectedKooulCategory._id),
        };

        if (selectedKooulStoreId) {
            payload.store_id = selectedKooulStoreId;
        }

        setIsKooulSubmitting(true);
        try {
            const response = await axios.post(`${API_URL}/kooul/add-product`, payload, header());
            if (response?.data?.success) {
                toast.success(response.data.message || 'Produit ajouté sur Kooul');
                
                // Update local product state immediately with the returned product data
                if (response?.data?.product) {
                    updateLocalProduct(response.data.product);
                }
            } else {
                toast.info(response?.data?.message || 'Réponse reçue de Kooul');
            }
            handleCloseKooulDialog();
        } catch (error) {
            const message = error?.response?.data?.message || error.message || 'Erreur lors de l\'envoi vers Kooul';
            toast.error(message);
        } finally {
            setIsKooulSubmitting(false);
        }
    };

    const handleOpenAddToAimer = (product) => {
        if (!product?._id) {
            toast.error("Aucun produit sélectionné");
            return;
        }

        const productCategoryIds = getProductCategoryIds(product);
        const defaults = aimerCategories.filter((category) => {
            const remoteId = normalizeId(category?.remote_plateform_id);
            if (!remoteId) {
                return false;
            }

            const linkedIds = Array.isArray(category?.link_ids)
                ? category.link_ids.map((id) => normalizeId(id)).filter(Boolean)
                : [];

            if (!linkedIds.length) {
                return productCategoryIds.has(normalizeId(category?._id));
            }

            return linkedIds.some((id) => productCategoryIds.has(id));
        });

        setAimerCategoryProduct(product);
        setSelectedAimerCategories(defaults);
        setAimerCategoryDialogOpen(true);
    };

    const handleCloseAddToAimer = () => {
        setAimerCategoryDialogOpen(false);
        setAimerCategoryProduct(null);
        setSelectedAimerCategories([]);
        setIsAimerSubmitting(false);
    };

    const handleSubmitAddToAimer = async () => {
        if (!aimerCategoryProduct?._id) {
            toast.error("Aucun produit sélectionné");
            return;
        }

        const aimerCategoryRemoteIds = selectedAimerCategories
            .map((category) => normalizeId(category?.remote_plateform_id))
            .filter(Boolean);

        if (!aimerCategoryRemoteIds.length) {
            toast.error('Veuillez sélectionner au moins une catégorie Aimer avant d\'envoyer le produit.');
            return;
        }

        setIsAimerSubmitting(true);
        try {
            const payload = {
                remote_plateform_id: aimerCategoryRemoteIds,
            };
            if (aimerPlateformId) {
                payload.plateform_id = aimerPlateformId;
            }

            const response = await createAimerProduct(aimerCategoryProduct._id, payload);
            if (response?.success) {
                toast.success(response?.message || 'Produit envoyé sur Aimer');
                
                // Update local product state immediately if product data is returned
                if (response?.product) {
                    updateLocalProduct(response.product);
                }
            } else {
                toast.info(response?.message || 'Réponse reçue lors de l\'envoi vers Aimer');
            }
            
            handleCloseAddToAimer();
        } catch (error) {
            const message = error?.response?.data?.message || error.message || 'Erreur lors de l\'envoi vers Aimer';
            toast.error(message);
        } finally {
            setIsAimerSubmitting(false);
        }
    };

    const handleOpenAimerStatusDialog = (product, remoteId = '') => {
        setAimerTargetProduct(product || null);
        setAimerRemoteProductId(remoteId || '');
        setAimerStatusDialogOpen(true);
    };

    const handleCloseAimerStatusDialog = () => {
        setAimerStatusDialogOpen(false);
        setAimerTargetProduct(null);
        setAimerRemoteProductId('');
        setIsAimerSubmitting(false);
    };

    const handleAimerStatusChange = async (statusValue) => {
        if (!aimerTargetProduct) {
            toast.error('Aucun produit sélectionné');
            return;
        }

        if (!aimerRemoteProductId) {
            toast.error('Identifiant produit Aimer introuvable');
            return;
        }

        setIsAimerSubmitting(true);
        try {
            const payload = { prdstatus: statusValue };
            if (aimerPlateformId) {
                payload.plateform_id = aimerPlateformId;
            }

            const response = await changeAimerProductStatus(aimerRemoteProductId, payload);
            if (response?.success) {
                toast.success(response?.message || 'Statut Aimer mis à jour');
                
                // Update local product if response contains product data
                if (response?.product) {
                    updateLocalProduct(response.product);
                }
            } else {
                toast.info(response?.message || 'Réponse reçue lors de la mise à jour');
            }
            handleCloseAimerStatusDialog();
        } catch (error) {
            const message = error?.response?.data?.message || error.message || 'Erreur lors de la mise à jour du statut Aimer';
            toast.error(message);
        } finally {
            setIsAimerSubmitting(false);
        }
    };

    const handleOpenGlovoToggleModal = (product) => {
        // Check if this is a roadmap-specific toggle (has context)
        if (product._selectedVendorId && product._selectedRemoteId) {
            handleToggleGlovoRoadmapAvailability(product);
            return;
        }
        
        // Otherwise open the modal for bulk toggle
        setGlovoToggleModalProduct(product);
        setGlovoToggleModalProducts([product]);
    };

    const handleToggleGlovoRoadmapAvailability = async (productWithContext) => {
        const {
            _id,
            _selectedVendorId,
            _selectedRemoteId,
            _selectedType,
            _selectedPlatformId,
            _currentAvailability,
            _targetAvailability,
            _storeInfo,
            plateforms: productPlateforms
        } = productWithContext;

        try {
            // Find the platform(s) that contain this roadmap entry
            const pagePlateforms = Array.isArray(plateforms) ? plateforms : [];
            const matchingPlatforms = [];

            if (_selectedPlatformId) {
                const matched = pagePlateforms.find(pf => normalizeId(pf._id || pf.id || pf.plateform_id || pf.value) === normalizeId(_selectedPlatformId));
                if (matched) {
                    matchingPlatforms.push(matched);
                }
            }

            if (matchingPlatforms.length === 0) {
                matchingPlatforms.push(
                    ...(productPlateforms || []).filter(pf => 
                        pf.roadmap?.some(rm => {
                            const rmVendor = normalizeId(rm.vendorId || rm.vendor_id || rm.data?.vendorId || rm.data?.store_id);
                            const selVendor = normalizeId(_selectedVendorId);
                            const rmRemote = normalizeId(rm.remote_id || rm.data?.id || rm.data?.remote_id);
                            const selRemote = normalizeId(_selectedRemoteId);
                            const rmType = (rm.type || rm.data?.type || '').toString().toLowerCase();
                            const selType = String(_selectedType || '').toLowerCase();
                            return rmVendor && selVendor && String(rmVendor) === String(selVendor) && (rmRemote === selRemote || !selRemote) && rmType === selType;
                        })
                    )
                );
            }

            if (matchingPlatforms.length === 0) {
                matchingPlatforms.push(
                    ...pagePlateforms.filter(pf => 
                pf.roadmap?.some(rm => {
                    const rmVendor = normalizeId(rm.vendorId || rm.vendor_id || rm.data?.vendorId || rm.data?.store_id);
                    const selVendor = normalizeId(_selectedVendorId);
                    const rmRemote = normalizeId(rm.remote_id || rm.data?.id || rm.data?.remote_id);
                    const selRemote = normalizeId(_selectedRemoteId);
                    const rmType = (rm.type || rm.data?.type || '').toString().toLowerCase();
                    const selType = String(_selectedType || '').toLowerCase();
                    return rmVendor && selVendor && String(rmVendor) === String(selVendor) && (rmRemote === selRemote || !selRemote) && rmType === selType;
                })
                    )
                );
            }

            if (matchingPlatforms.length === 0) {
                toast.error("Impossible de trouver la plateforme correspondante");
                return;
            }
console.log('[Toggle Glovo Availability] matchingPlatforms:', matchingPlatforms);
            // Determine whether any matching platform is of type 'glovo_sisters'.
            // Note: entries in `matchingPlatforms` may be lightweight (from product.plateforms)
            // and not contain the full platform `type` — resolve against the global `plateforms`.
            let glovoSistersPlatform = null;
            if (Array.isArray(matchingPlatforms) && Array.isArray(plateforms)) {
                for (const mp of matchingPlatforms) {
                    try {
                        const lookupId = normalizeId(mp.plateform_id || mp._id || mp.id || mp.value);
                        if (!lookupId) continue;
                        const candidate = plateforms.find(p => normalizeId(p._id || p.id || p.plateform_id || p.value) === lookupId && `${(p.type||'')}`.toLowerCase() === 'glovo_sisters') || null;
                        if (candidate) { glovoSistersPlatform = candidate; break; }
                    } catch (e) {
                        // ignore and continue
                    }
                }
            }
            
            


            let localStoreId = null;
            const platformIds = glovoSistersPlatform
                ? [normalizeId(glovoSistersPlatform._id || glovoSistersPlatform.id || glovoSistersPlatform.plateform_id || glovoSistersPlatform.value)].filter(Boolean)
                : matchingPlatforms.map(pf => normalizeId(pf.plateform_id || pf._id || pf.id || pf.value)).filter(Boolean);
        
            if (glovoSistersPlatform) {
                console.log('[Toggle Glovo Availability] Detected glovo_sisters platform:', glovoSistersPlatform);
                // For glovo_sisters, vendorId must come exclusively from platform.additional_data.portal.stores_maped
                const add = glovoSistersPlatform.additionnal_data || glovoSistersPlatform.additional_data || {};
               
                const maps = add?.stores_maped || add?.stores_mapped || add?.storesMapped || add.stores_maped || add.stores_mapped || null;

                if (!maps) {
                    console.log('[Toggle Glovo Availability] glovo_sisters platform has no stores_maped', { glovoSistersPlatform });
                    toast.error('vendorId not existing (platform mapping missing for glovo_sisters)');
                    return;
                }

                // Find mapping entry where remote_store_id matches the roadmap vendor id
                let mappingEntry = null;
                if (Array.isArray(maps)) {
                    mappingEntry = maps.find(m => normalizeId(m.remote_store_id || m.remoteStoreId || m.remote_id || m.remoteId) === normalizeId(_selectedVendorId)) || null;
                } else if (maps && typeof maps === 'object') {
                    // Single-entry object shape { store_id: '...', remote_store_id: '...' }
                    if (maps.store_id && (normalizeId(maps.remote_store_id) === normalizeId(_selectedVendorId) || normalizeId(maps.remoteStoreId) === normalizeId(_selectedVendorId))) {
                        mappingEntry = { store_id: maps.store_id, remote_store_id: maps.remote_store_id || maps.remoteStoreId };
                    } else {
                        // or a mapping object like { <localId>: <remoteId> }
                        const foundKey = Object.keys(maps).find(k => normalizeId(maps[k]) === normalizeId(_selectedVendorId));
                        if (foundKey) mappingEntry = { store_id: foundKey, remote_store_id: maps[foundKey] };
                    }
                }

                console.log('[Toggle Glovo Availability] stores_maped:', maps, 'mappingEntry:', mappingEntry);
                if (!mappingEntry || !mappingEntry.store_id) {
                    console.log('[Toggle Glovo Availability] mappingEntry not found or missing store_id');
                    toast.error('vendorId not existing');
                    return;
                }

                // Resolve the Store object by store_id
                localStoreId = normalizeId(mappingEntry.store_id);
                const store = (Array.isArray(stores) ? stores : []).find(s => {
                    const candidates = [normalizeId(s._id), normalizeId(s.value), normalizeId(s.id), normalizeId(s.rawId), normalizeId(s.storeCode), normalizeId(s.code)];
                    return candidates.some(v => v && localStoreId && String(v) === String(localStoreId));
                });
                console.log('[Toggle Glovo Availability] resolved localStoreId:', localStoreId, 'found store:', store);
                if (!store) {
                    console.log('[Toggle Glovo Availability] local store not found in `stores` list', { localStoreId, storesCount: Array.isArray(stores) ? stores.length : 0 });
                    toast.error('vendorId not existing (local store not found)');
                    return;
                }

                // vendor id comes from mapping
                const resolvedVendorId = mappingEntry.remote_store_id || mappingEntry.remoteStoreId || mappingEntry.remote_id || mappingEntry.remoteId;

                console.log('[Toggle Glovo Availability][glovo_sisters]', { productId: _id, storeIds: [localStoreId], platformIds, vendorId: resolvedVendorId, remoteId: _selectedRemoteId, currentAvailability: _currentAvailability, targetAvailability: _targetAvailability });

                const response = await fetch(`${process.env.REACT_APP_PEGASUS_API_URL}/glovo/sistersflowers/toggle-product`, {
                    method: 'POST',
                    headers: {
                        ...header().headers,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        product_id: _id,
                        store_ids: resolvedVendorId ? [String(resolvedVendorId)] : [],
                        vendor_ids: resolvedVendorId ? [String(resolvedVendorId)] : undefined,
                        vendorIds: resolvedVendorId ? [String(resolvedVendorId)] : undefined,
                        availability: _targetAvailability,
                        plateformId: platformIds[0] || null
                    })
                });

                const data = await response.json();
                if (data.success) {
                    toast.success(`Disponibilité ${_targetAvailability ? 'activée' : 'désactivée'} avec succès`);
                    const updatedProduct = await fetchUpdatedProduct(_id);
                    if (updatedProduct) handleProductUpdated(updatedProduct);
                } else {
                    toast.error(data.error || 'Erreur lors de la mise à jour de la disponibilité');
                }

                return;
            }

            // Non-glovo_sisters fallback (existing behavior)
            let store = _storeInfo || (Array.isArray(stores) ? stores.find(s => s.storeCode === _selectedVendorId || s.code === _selectedVendorId) : null);

            // If not found, as a last resort try to resolve via any global glovo_sisters platform mapping
            if (!store && pagePlateforms.length) {
                try {
                    for (const pf of pagePlateforms) {
                        const type = (pf?.type || pf?.additionnal_data?.type || pf?.additional_data?.type || '').toString().toLowerCase();
                        if (type !== 'glovo_sisters') continue;

                        const add = pf.additionnal_data || pf.additional_data || {};
                        const portal = add.portal || add.Portal || null;
                        const maps = portal?.stores_maped || portal?.stores_mapped || portal?.storesMapped || add.stores_maped || add.stores_mapped || null;
                        if (!maps) continue;

                        let mappingEntry = null;
                        if (Array.isArray(maps)) {
                            mappingEntry = maps.find(m => String(m.remote_store_id || m.remoteStoreId || m.remote_id || m.remoteId) === String(_selectedVendorId)) || null;
                        } else if (maps && typeof maps === 'object') {
                            if (maps.store_id && (String(maps.remote_store_id) === String(_selectedVendorId) || String(maps.remoteStoreId) === String(_selectedVendorId))) {
                                mappingEntry = { store_id: maps.store_id, remote_store_id: maps.remote_store_id || maps.remoteStoreId };
                            } else {
                                const foundKey = Object.keys(maps).find(k => String(maps[k]) === String(_selectedVendorId));
                                if (foundKey) mappingEntry = { store_id: foundKey, remote_store_id: maps[foundKey] };
                            }
                        }

                        if (mappingEntry && mappingEntry.store_id) {
                            const localId = mappingEntry.store_id;
                            const foundStore = (Array.isArray(stores) ? stores : []).find(s => {
                                const candidates = [s._id, s.value, s.id, s.rawId, s.storeCode, s.code];
                                return candidates.some(v => v && String(v) === String(localId));
                            });
                            if (foundStore) {
                                store = foundStore;
                                console.log('[Toggle Glovo Availability] resolved store via global glovo_sisters platform mapping', { platformId: normalizeId(pf._id || pf.id), mappingEntry, foundStore });
                                break;
                            }
                        }
                    }
                } catch (err) {
                    console.warn('Error while attempting global glovo_sisters mapping resolution', err);
                }
            }

            if (!store) {
                toast.error(`Magasin introuvable pour vendorId: ${_selectedVendorId}`);
                return;
            }

            const fallbackLocalStoreId = store.value || store._id || store.id;

            if (platformIds.length === 0) {
                toast.error("Impossible de trouver l'ID de plateforme");
                return;
            }

            console.log('[Toggle Glovo Availability]', {
                productId: _id,
                storeIds: [fallbackLocalStoreId],
                platformIds,
                vendorId: _selectedVendorId,
                remoteId: _selectedRemoteId,
                currentAvailability: _currentAvailability,
                targetAvailability: _targetAvailability
            });

            // Call the API
            const response = await fetch(`${process.env.REACT_APP_PEGASUS_API_URL}/glovo-portal/toggle-product`, {
                method: 'POST',
                headers: {
                    ...header().headers,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    product_id: _id,
                    store_ids: [fallbackLocalStoreId],
                    availability: _targetAvailability
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success(`Disponibilité ${_targetAvailability ? 'activée' : 'désactivée'} avec succès`);
                
                // Refresh product data
                const updatedProduct = await fetchUpdatedProduct(_id);
                if (updatedProduct) {
                    handleProductUpdated(updatedProduct);
                }
            } else {
                toast.error(data.error || 'Erreur lors de la mise à jour de la disponibilité');
            }
        } catch (error) {
            console.error('Error toggling Glovo availability:', error);
            toast.error('Erreur lors de la mise à jour de la disponibilité');
        }
    };

    const fetchUpdatedProduct = async (productId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_PEGASUS_API_URL}/product/${productId}`, {
                method: 'GET',
                headers: {
                    ...header().headers,
                }
            });
            
            if (!response.ok) {
                console.error('Failed to fetch updated product');
                return null;
            }
            
            const updatedProduct = await response.json();
            return updatedProduct;
        } catch (error) {
            console.error('Error fetching updated product:', error);
            return null;
        }
    };

    const handleOpenGlovoToggleBulkModal = () => {
        if (!selectedProducts || selectedProducts.length === 0) {
            toast.warning('Veuillez sélectionner au moins un produit');
            return;
        }

        // Filter to only Glovo products
        const glovoProducts = products.filter(p => 
            selectedProducts.includes(p._id) && 
            p.plateforms?.some(pf => pf.type?.toLowerCase() === 'glovo')
        );

        if (glovoProducts.length === 0) {
            toast.warning('Aucun produit Glovo sélectionné');
            return;
        }

        setGlovoToggleModalProducts(glovoProducts);
        setGlovoToggleModalProduct(null);
    };

    const handleCloseGlovoToggleModal = () => {
        setGlovoToggleModalProduct(null);
        setGlovoToggleModalProducts([]);
    };

    const handleGlovoToggleSuccess = () => {
        // Refresh products on current page to keep pagination
        fetchProducts(currentPage);
        setSelectedProducts([]);
    };

    const handleOpenBulkDisableModal = () => {
        setBulkDisableModalOpen(true);
        setSelectedBulkDisableStore(null);
        setBulkDisableProgress({ current: 0, total: 0, errors: [] });
        setBulkDisableQueue([]);
        setBulkDisablePaused(false);
    };

    const handleCloseBulkDisableModal = () => {
        if (bulkDisableInProgress) {
            toast.warning('Opération en cours, veuillez patienter...');
            return;
        }
        setBulkDisableModalOpen(false);
        setSelectedBulkDisableStore(null);
        setBulkDisableProgress({ current: 0, total: 0, errors: [] });
        setBulkDisableQueue([]);
        setBulkDisablePaused(false);
    };

    const handleBulkDisableGlovoProducts = async () => {
        if (!selectedBulkDisableStore) {
            toast.error('Veuillez sélectionner un magasin');
            return;
        }

        const storeCode = String(selectedBulkDisableStore.storeCode || selectedBulkDisableStore.code).trim();
        if (!storeCode) {
            toast.error('Code magasin invalide');
            return;
        }

        // Find all products that have Glovo plateform roadmap entries for this store
        const productsToDisable = products.filter(product => {
            if (!product.plateforms || product.plateforms.length === 0) return false;
            return product.plateforms.some(pf => {
                // Check if this plateform is a Glovo plateform
                const pfId = normalizeId(pf.plateform_id || pf._id || pf.id);
                if (!glovoPlateformIds.has(pfId)) return false;
                
                // Check if this Glovo plateform has a roadmap entry for the selected store
                if (!Array.isArray(pf.roadmap)) return false;
                return pf.roadmap.some(rm =>
                    String(rm.vendorId).trim() === storeCode &&
                    rm.data?.availability?.available === true
                );
            });
        });

        if (productsToDisable.length === 0) {
            toast.info('Aucun produit Glovo actif trouvé pour ce magasin');
            return;
        }

        // Prepare queue
        setBulkDisableQueue(productsToDisable.map(p => ({ product: p, status: 'pending', errorMsg: '' })));
        setBulkDisableInProgress(true);
        setBulkDisablePaused(false);
        setBulkDisableProgress({ current: 0, total: productsToDisable.length, errors: [] });
    };

    // Effect to process the queue step by step
    useEffect(() => {
        if (!bulkDisableInProgress || bulkDisablePaused || !bulkDisableQueue.length) return;
        const nextIndex = bulkDisableQueue.findIndex(item => item.status === 'pending');
        if (nextIndex === -1) {
            setBulkDisableInProgress(false);
            setBulkDisableProgress(prev => ({ ...prev, errors: bulkDisableQueue.filter(i => i.status === 'error').map(i => ({ product: i.product.name, error: i.errorMsg })) }));
            if (bulkDisableQueue.every(i => i.status === 'done')) {
                toast.success(`${bulkDisableQueue.length} produit(s) désactivé(s) avec succès`);
                fetchProducts(currentPage);
                setTimeout(() => { handleCloseBulkDisableModal(); }, 2000);
            } else {
                toast.warning(`${bulkDisableQueue.filter(i => i.status === 'done').length} produit(s) désactivé(s), ${bulkDisableQueue.filter(i => i.status === 'error').length} erreur(s)`);
            }
            return;
        }
        // Mark as in-progress
        setBulkDisableQueue(q => q.map((item, idx) => idx === nextIndex ? { ...item, status: 'in-progress' } : item));
        const { product } = bulkDisableQueue[nextIndex];
        const storeCode = String(selectedBulkDisableStore.storeCode || selectedBulkDisableStore.code).trim();
        const store = stores.find(s => [s.storeCode, s.code, s.value, s._id, s.id].map(x => x && String(x).trim()).includes(storeCode));
        if (!store) {
            setBulkDisableQueue(q => q.map((item, idx) => idx === nextIndex ? { ...item, status: 'error', errorMsg: 'Magasin introuvable' } : item));
            setBulkDisableProgress(prev => ({ ...prev, current: nextIndex + 1 }));
            return;
        }
        const storeId = store.value || store._id || store.id;
        fetch(`${process.env.REACT_APP_PEGASUS_API_URL}/glovo-portal/toggle-product`, {
            method: 'POST',
            headers: {
                ...header().headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                product_id: product._id,
                store_ids: [storeId],
                availability: false
            })
        })
        .then(res => res.json())
        .then(data => {
            if (data.success) {
                setBulkDisableQueue(q => q.map((item, idx) => idx === nextIndex ? { ...item, status: 'done' } : item));
            } else {
                setBulkDisableQueue(q => q.map((item, idx) => idx === nextIndex ? { ...item, status: 'error', errorMsg: data.error || 'Erreur inconnue' } : item));
            }
        })
        .catch(error => {
            setBulkDisableQueue(q => q.map((item, idx) => idx === nextIndex ? { ...item, status: 'error', errorMsg: error.message } : item));
        })
        .finally(() => {
            setBulkDisableProgress(prev => ({ ...prev, current: nextIndex + 1 }));
            setTimeout(() => {
                // Continue to next if not paused
                if (!bulkDisablePaused) setBulkDisableInProgress(true);
            }, 300);
        });
    }, [bulkDisableInProgress, bulkDisablePaused, bulkDisableQueue, selectedBulkDisableStore, stores]);
    


    const handleOpenPublierPlateformes = (product) => {
        if (!product?._id) {
            toast.error("Aucun produit sélectionné");
            return;
        }

        setPublierPlateformesProduct(product);
        setCurrentProduct(product);
        
        // Prepare Aimer categories - format them as {value, label} objects
        const productCategoryIds = getProductCategoryIds(product);
        const aimerDefaults = aimerCategories
            .filter((category) => {
                const remoteId = normalizeId(category?.remote_plateform_id);
                if (!remoteId) {
                    return false;
                }

                const linkedIds = Array.isArray(category?.link_ids)
                    ? category.link_ids.map((id) => normalizeId(id)).filter(Boolean)
                    : [];

                if (!linkedIds.length) {
                    return productCategoryIds.has(normalizeId(category?._id));
                }

                return linkedIds.some((id) => productCategoryIds.has(id));
            })
            .map((cat) => ({
                value: cat._id || cat.id || cat.value,
                label: cat.name || cat.label || 'Catégorie',
                remote_plateform_id: cat.remote_plateform_id,
                link_ids: cat.link_ids,
            }));
        
        setAimerCategoryProduct(product);
        setSelectedAimerCategories(aimerDefaults);

        // Check if product is already on site to determine update mode
        const hasWooCommerce = product?.woocommerce_id && product.woocommerce_id !== '';
        setIsWooCategoryUpdate(hasWooCommerce);

        setPublierPlateformesOpen(true);
    };

    const handleClosePublierPlateformes = () => {
        setPublierPlateformesOpen(false);
        setPublierPlateformesProduct(null);
    };

    const handleProductUpdated = (updatedProduct) => {
        console.log('[StoreQuickProduct] Product updated:', updatedProduct);
        
        // Update the product in the products list
        setProducts(prevProducts => 
            prevProducts.map(p => 
                p._id === updatedProduct._id ? updatedProduct : p
            )
        );
        
        // Update current product if it's the same one
        if (currentProduct?._id === updatedProduct._id) {
            setCurrentProduct(updatedProduct);
        }
        
        // Update the modal's product
        if (publierPlateformesProduct?._id === updatedProduct._id) {
            setPublierPlateformesProduct(updatedProduct);
        }
    };

    const handlePublierAimerSubmit = async (selectedCategories) => {
        // selectedCategories is an array of {value, label, ...} objects
        if (!selectedCategories || selectedCategories.length === 0) {
            toast.error("Veuillez sélectionner au moins une catégorie Aimer");
            return;
        }

        if (!publierPlateformesProduct?._id) {
            toast.error("Aucun produit sélectionné");
            return;
        }

        const aimerCategoryRemoteIds = selectedCategories
            .map((category) => normalizeId(category?.remote_plateform_id))
            .filter(Boolean);

        if (!aimerCategoryRemoteIds.length) {
            toast.error('Veuillez sélectionner au moins une catégorie Aimer avant d\'envoyer le produit.');
            return;
        }

        setIsAimerSubmitting(true);
        try {
            const payload = {
                remote_plateform_id: aimerCategoryRemoteIds,
            };
            if (aimerPlateformId) {
                payload.plateform_id = aimerPlateformId;
            }

            const response = await createAimerProduct(publierPlateformesProduct._id, payload);
            if (response?.success) {
                toast.success(response?.message || 'Produit envoyé sur Aimer');
                
                // Update local product state immediately if product data is returned
                if (response?.product) {
                    updateLocalProduct(response.product);
                    setPublierPlateformesProduct(response.product);
                }
            } else {
                toast.info(response?.message || 'Réponse reçue lors de l\'envoi vers Aimer');
            }
            
            handleClosePublierPlateformes();
        } catch (error) {
            const message = error?.response?.data?.message || error.message || 'Erreur lors de l\'envoi vers Aimer';
            toast.error(message);
        } finally {
            setIsAimerSubmitting(false);
        }
    };

    const handlePublierKooulSubmit = async (selectedPlatform, selectedCategories) => {
        if (!selectedPlatform || !selectedCategories || selectedCategories.length === 0) {
            toast.error("Veuillez sélectionner une plateforme et des catégories Kooul");
            return;
        }

        if (!currentProduct?._id) {
            toast.error('Aucun produit sélectionné');
            return;
        }

        // Get the category from the selectedCategories array (should have one leaf category)
        const selectedCategory = selectedCategories[0];
        if (!selectedCategory?.value) {
            toast.error('Catégorie Kooul invalide');
            return;
        }

        const payload = {
            product_id: currentProduct._id,
            plateform_id: selectedPlatform.optionValue || normalizeId(selectedPlatform._id || selectedPlatform.id || selectedPlatform.value),
            category_id: normalizeId(selectedCategory.value),
        };

        // Determine store_id from selectedPlatform
        const additionnalStoreId = selectedPlatform?.additionnal_data?.kooul?.store_id;
        const nameStoreId = typeof selectedPlatform?.name === 'string'
            && selectedPlatform.name.toLowerCase().startsWith('kooul_')
            ? selectedPlatform.name.slice('kooul_'.length)
            : '';

        const storeIdsArray = Array.isArray(selectedPlatform?.store_ids) ? selectedPlatform.store_ids : [];
        const firstStoreId = storeIdsArray.length
            ? normalizeId(selectedPlatform.store_ids[0]?.store_id || selectedPlatform.store_ids[0])
            : '';

        const resolvedStoreId = String(additionnalStoreId || nameStoreId || firstStoreId || '').trim();
        
        if (resolvedStoreId) {
            payload.store_id = resolvedStoreId;
        }

        setIsKooulSubmitting(true);
        try {
            const response = await axios.post(`${API_URL}/kooul/add-product`, payload, header());
            if (response?.data?.success) {
                toast.success(response.data.message || 'Produit ajouté sur Kooul');
                
                // Update local product state immediately with the returned product data
                if (response?.data?.product) {
                    updateLocalProduct(response.data.product);
                    setPublierPlateformesProduct(response.data.product);
                }
            } else {
                toast.info(response?.data?.message || 'Réponse reçue de Kooul');
            }
            
            handleClosePublierPlateformes();
        } catch (error) {
            const message = error?.response?.data?.message || error.message || 'Erreur lors de l\'envoi vers Kooul';
            toast.error(message);
        } finally {
            setIsKooulSubmitting(false);
        }
    };

    const handlePublierMarjaneSubmit = async (selectedCategory) => {
        if (!publierPlateformesProduct?._id) {
            toast.error("Aucun produit sélectionné");
            return;
        }

        const remoteCategoryId = normalizeId(
            selectedCategory?.remoteId ||
            selectedCategory?.remote_id ||
            selectedCategory?.remote_plateform_id ||
            selectedCategory?.category_id
        );
        const categoryName = `${selectedCategory?.name || selectedCategory?.label || ''}`.trim();
        if (!remoteCategoryId || !categoryName) {
            toast.error("Veuillez sélectionner une catégorie Marjane Mall valide");
            return;
        }

        const marjanePlateform = (Array.isArray(plateforms) ? plateforms : []).find(
            (plateform) => `${plateform?.type ?? ''}`.toLowerCase() === 'marjanemall'
        );

        const payload = {
            product_id: normalizeId(publierPlateformesProduct._id),
            category_id: remoteCategoryId,
            category_name: categoryName,
        };

        const marjanePlateformId = normalizeId(
            marjanePlateform?._id || marjanePlateform?.id || marjanePlateform?.plateform_id || marjanePlateform?.value
        );
        if (marjanePlateformId) {
            payload.plateform_id = marjanePlateformId;
        }

        try {
            const response = await axios.patch(`${API_URL}/marjanemall/flagit`, payload, header());
            const data = response?.data || {};

            if (!data?.success) {
                toast.error(data?.message || 'Échec du flag Marjane Mall');
                return;
            }

            toast.success(data?.message || 'Produit flagué pour Marjane Mall');

            const updatedProduct = await fetchUpdatedProduct(publierPlateformesProduct._id);
            if (updatedProduct) {
                handleProductUpdated(updatedProduct);
            }
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Erreur lors du flag Marjane Mall';
            toast.error(message);
            throw error;
        }
    };

    const prepareWebsitePublication = async (product, deliveryFees, options) => {
        const publication = buildWebsitePublication(product, deliveryFees, options);
        const fields = { vase_inclusion: publication.vase_inclusion,
            website_delivery: normalizeWebsiteDelivery(publication.website_delivery) };
        const response = await axios.put(`${API_URL}/products/${product._id}`, fields, header());
        if (response.data?.success === false || response.data?.result === false) {
            throw new Error(response.data?.message || 'Impossible de sauvegarder les options du site.');
        }
        const saved = { ...product, ...fields };
        updateLocalProduct(saved);
        return { ...options, product: saved, price: publication.price };
    };

    const handlePublierSiteSubmit = async (selectedCategories, fees, options = {}) => {
        if (!selectedCategories || selectedCategories.length === 0) {
            toast.error("Veuillez sélectionner au moins une catégorie");
            return;
        }

        const productsToPublish = Array.isArray(options?.products) && options.products.length
            ? options.products
            : publierPlateformesProduct?._id
                ? [publierPlateformesProduct]
                : [];

        if (!productsToPublish.length) {
            toast.error("Aucun produit sélectionné");
            return;
        }

        if (productsToPublish.length === 1) {
            const targetProduct = productsToPublish[0];
            const hasWooCommerce = targetProduct?.woocommerce_id && targetProduct.woocommerce_id !== '';

            setCurrentProduct(targetProduct);
            setSelectedCategories(selectedCategories);
            setFees(fees || 0);

            try {
                const publishOptions = await prepareWebsitePublication(targetProduct, fees, options);
                if (hasWooCommerce) {
                    await updateWooCategories(selectedCategories, publishOptions);
                } else {
                    await addinWebsite(selectedCategories, fees, publishOptions);
                }

                handleClosePublierPlateformes();
            } catch (error) {
                console.error('Error in handlePublierSiteSubmit:', error);
                toast.error(error?.response?.data?.message || error.message || 'Publication impossible.');
                throw error;
            }
            return;
        }

        setSelectedCategories(selectedCategories);
        setFees(fees || 0);

        let successCount = 0;
        const errors = [];

        try {
            for (const targetProduct of productsToPublish) {
                try {
                    const productOptions = {
                        ...await prepareWebsitePublication(targetProduct, fees, { ...options, price: undefined }),
                        keepSelection: true,
                    };
                    const hasWooCommerce = targetProduct?.woocommerce_id && targetProduct.woocommerce_id !== '';

                    setCurrentProduct(targetProduct);
                    if (hasWooCommerce) {
                        await updateWooCategories(selectedCategories, productOptions);
                    } else {
                        await addinWebsite(selectedCategories, fees, productOptions);
                    }
                    successCount += 1;
                } catch (error) {
                    errors.push({
                        product: targetProduct?.name || targetProduct?._id || 'Produit',
                        message: error?.response?.data?.message || error?.message || 'Erreur inconnue',
                    });
                }
            }

            if (successCount > 0) {
                toast.success(`${successCount} produit(s) publié(s) sur le site`);
            }
            if (errors.length > 0) {
                toast.error(`${errors.length} produit(s) non publié(s)`);
                console.error('Bulk website publish errors:', errors);
            }
            handleClosePublierPlateformes();
        } catch (error) {
            console.error('Error in handlePublierSiteSubmit:', error);
        } finally {
            setCurrentProduct(null);
            setSelectedCategories([]);
            setFees(0);
        }
    };

    const handlePublierInstagramSubmit = async (description) => {
        if (!publierPlateformesProduct?._id) {
            toast.error("Aucun produit sélectionné");
            return;
        }

        try {
            // Update currentProduct with the description and product from modal
            setCurrentProduct({ ...publierPlateformesProduct, description });
            
            await postInstagram();
            
            handleClosePublierPlateformes();
        } catch (error) {
            console.error('Error in handlePublierInstagramSubmit:', error);
        }
    };

    const handlePublierDolibarrSubmit = async (companyId, options = {}) => {
        if (!publierPlateformesProduct?._id) {
            toast.error('Aucun produit sélectionné');
            return;
        }

        try {
            if (!companyId) {
                toast.error('Veuillez sélectionner une société Dolibarr.');
                return;
            }

            const productToPublish = publierPlateformesProduct;
            const imageSource =
                productToPublish?.url_product ||
                (Array.isArray(productToPublish?.gallery_images) ? productToPublish.gallery_images[0] : '') ||
                '';
            const imageUrl = imageSource ? resolvePrefillImageUrl(imageSource) : '';

            const response = await axios.post(
                `${API_URL}/products/${productToPublish._id}/dolibarr/publish`,
                {
                    company_id: companyId,
                    image_url: imageUrl,
                    price: options?.price,
                    ref: options?.ref,
                },
                header()
            );

            if (!response?.data?.success) {
                toast.error(response?.data?.message || 'Erreur lors de la publication sur Dolibarr');
                return;
            }

            let nextProduct = null;
            const updatedProduct = await fetchUpdatedProduct(productToPublish._id);
            if (updatedProduct) {
                nextProduct = updatedProduct;
            } else if (response?.data?.product) {
                nextProduct = response.data.product;
            }

            if (!nextProduct) {
                nextProduct = productToPublish;
            }

            updateLocalProduct(nextProduct);
            setPublierPlateformesProduct(nextProduct);
            toast.success(
                response?.data?.message ||
                `Produit créé sur Dolibarr (${response?.data?.dolibarr?.ref || nextProduct?.dolibarr_ref || ''}).`
            );
            handleClosePublierPlateformes();
        } catch (error) {
            console.error('Error publishing to Dolibarr:', error);
            toast.error(error?.response?.data?.message || error?.message || 'Erreur lors de la publication sur Dolibarr');
            throw error;
        }
    };

    const handleFetchDolibarrStock = async (companyId) => {
        if (!publierPlateformesProduct?._id) {
            throw new Error('Aucun produit sélectionné');
        }

        const response = await axios.get(
            `${API_URL}/products/${publierPlateformesProduct._id}/dolibarr/stock`,
            {
                ...header(),
                params: companyId ? { company_id: companyId } : undefined,
            }
        );

        return response?.data;
    };

    const handleUpdateDolibarrStock = async (companyId, payload) => {
        if (!publierPlateformesProduct?._id) {
            throw new Error('Aucun produit sélectionné');
        }

        const response = await axios.patch(
            `${API_URL}/products/${publierPlateformesProduct._id}/dolibarr/stock`,
            {
                company_id: companyId,
                ...payload,
            },
            header()
        );

        return response?.data;
    };

    const getInstagramPlatformConfig = useCallback(() => {
        const instagramPlatform = (Array.isArray(plateforms) ? plateforms : []).find((plateform) => {
            const type = `${plateform?.type || ''}`.toLowerCase().trim();
            const name = `${plateform?.name || ''}`.toLowerCase().trim();
            return type === 'instagram' || name === 'instagram';
        });

        if (!instagramPlatform) {
            throw new Error('Plateforme Instagram introuvable');
        }

        const additionnalData = instagramPlatform?.additionnal_data || {};
        const accessToken =
            additionnalData.REACT_APP_FB_TMP_KEY ||
            additionnalData.fb_tmp_key ||
            additionnalData.access_token ||
            instagramPlatform.password;
        const igUserId =
            instagramPlatform.login ||
            additionnalData.REACT_APP_FB_KEY_PEGA ||
            additionnalData.fb_key_pega ||
            additionnalData.instagram_user_id;

        if (!accessToken || !igUserId) {
            throw new Error('Configuration Instagram incomplète sur la plateforme');
        }

        return {
            accessToken,
            igUserId,
        };
    }, [plateforms]);

    const resolveInstagramPublishImageUrl = useCallback((imagePath) => {
        const rawValue = String(imagePath || '').trim();
        if (!rawValue) {
            return '';
        }

        const decodedRawValue = decodeURIComponent(rawValue).trim();
        if (/^https?:\/\//i.test(decodedRawValue)) {
            return decodedRawValue;
        }

        const officeProxyMatch = rawValue.match(/image\.jpeg\.php\?path=(.+)$/i);
        if (officeProxyMatch?.[1]) {
            const decodedPath = decodeURIComponent(officeProxyMatch[1]).trim();
            if (/^https?:\/\//i.test(decodedPath)) {
                return decodedPath;
            }

            return `https://office.le-celadon.ma/image.jpeg.php?path=${encodeURIComponent(decodedPath)}`;
        }

        if (/^https?:\/\//i.test(rawValue)) {
            return rawValue;
        }

        return `https://office.le-celadon.ma/image.jpeg.php?path=${encodeURIComponent(rawValue)}`;
    }, []);



const postInstagram = async () => {
        setIsPublishing(true);

        const rawImagePath = currentProduct?.url_product;
        const caption = currentProduct?.description;

        if (!rawImagePath) {
            toast.error("❌ Aucun chemin d'image trouvé.");
            setIsPublishing(false);
            return;
        }

        const validatedImageUrl = resolveInstagramPublishImageUrl(rawImagePath);

        try {
            const { accessToken, igUserId } = getInstagramPlatformConfig();
            // Étape 1 : Création du média Instagram
            const createUrl = `https://graph.facebook.com/v22.0/${igUserId}/media`;

            const createResponse = await axios.post(
                createUrl,
                {
                    image_url: validatedImageUrl,
                    caption: caption,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `OAuth ${accessToken}`,
                    },
                }
            );

            const creationId = createResponse.data.id;

            // Étape 2 : Publication du média
            const publishUrl = `https://graph.facebook.com/v22.0/${igUserId}/media_publish`;

            // Attendre 2 secondes avant de publier pour laisser le temps à Facebook de traiter le média
            await new Promise((resolve) => setTimeout(resolve, 2000));

            const publishResponse = await axios.post(
                publishUrl,
                {
                    creation_id: creationId,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `OAuth ${accessToken}`,
                    },
                }
            );

            const instagramId = publishResponse.data.id;

            toast.info("✅ Média publié avec succès :", instagramId);

            // Étape 3 : Mise à jour du produit avec l'ID Instagram
            const updateResponse = await axios.post(`${process.env.REACT_APP_PEGASUS_API_URL}/products/${currentProduct._id}/instagram`, {
                instagramId: instagramId,
            }, header());

            toast.success("Produit ajouté avec succès sur Instagram");
            
            // Update local product if response contains product data
            if (updateResponse?.data?.product) {
                updateLocalProduct(updateResponse.data.product);
            }
            
            setCurrentProduct(null);

        } catch (error) {
            toast.error("❌ Erreur :", error.response ? error.response.data : error.message);
        } finally {
            setIsPublishing(false);
        }
    };

    const loadComponents = async (product_) => {
        setComponents([{ product: '', quantity: '1' }]);
        setCurrentProduct(product_);
        try {
            const response = await axios.get(`${API_URL}/product/${product_?._id}`, header());
            const product = response.data;

            if (product) {
                setCurrentProduct(product);
            }

            if (product && product.components) {
                const normalized = normalizeComponentEntries(product.components);
                setComponents(normalized);
            } else {
                setComponents([{ product: '', quantity: '1' }]);
            }
        } catch (error) {
            toast.error("Erreur lors du chargement des composants :", error);
        }
    };


    const fetchAllProductsOnce = useCallback(async (forceRefresh = false) => {
        if (hasFetchedAllProducts.current && !forceRefresh) {
            return products;
        }
        hasFetchedAllProducts.current = true;
        setIsFullProductsLoading(true);
        try {
            const allProducts = await ProductService.fetchProducts({ forceRefresh });
            setProducts(allProducts);
            return allProducts;
        } catch (error) {
            console.error('Erreur lors du chargement des produits via streaming:', error);
            if (!hasVisibleProductsRef.current) {
                toast.error('Erreur lors du chargement complet des produits.');
            }
            hasFetchedAllProducts.current = false; // Allow retry on error
            return [];
        } finally {
            setIsFullProductsLoading(false);
        }
    }, [products]);

    const fetchPagedProducts = useCallback(async (pageNumber = 1) => {
        setIsPagedProductsLoading(true);
        try {
            const response = await axios.get(
                `${API_URL}/product/pages/${productsPerPage}/${pageNumber}`,
                { ...header(), params: { sort: 'created_desc' } }
            );
            const payload = response?.data || {};
            const data = Array.isArray(payload.data) ? payload.data : [];
            setPagedProducts(data);
            setProductPagination({
                total: payload.total ?? data.length,
                perPage: payload.perPage ?? productsPerPage,
                pageNumber: payload.pageNumber ?? pageNumber,
                totalPages:
                    payload.totalPages ??
                    Math.max(1, Math.ceil((payload.total ?? data.length) / productsPerPage)),
            });
        } catch (error) {
            toast.error('Erreur lors du chargement des produits paginés.');
        } finally {
            setIsPagedProductsLoading(false);
        }
    }, []);

    const fetchProductCategories = useCallback(async () => {
        const response_ = await axios.get(`${API_URL}/productsCategories`, header());
        const categoriesPayload = response_.data;
        const categoriesList = Array.isArray(categoriesPayload)
            ? categoriesPayload
            : (categoriesPayload && categoriesPayload.data ? categoriesPayload.data : []);
        setProductCategories(categoriesList);
    }, []);

    const fetchProducts = useCallback(async (pageNumber = 1, options = {}) => {
        const resolvedPage = pageNumber ?? currentPageRef.current ?? 1;
        const { forceFullRefresh = false } = options;
        const shouldFetchPagedFallback = !hasVisibleProductsRef.current;
        await Promise.all([
            fetchAllProductsOnce(forceFullRefresh),
            shouldFetchPagedFallback ? fetchPagedProducts(resolvedPage) : Promise.resolve(),
            fetchProductCategories(),
        ]);
    }, [fetchAllProductsOnce, fetchPagedProducts, fetchProductCategories]);

    useEffect(() => {
        const handleProductsCacheUpdated = (event) => {
            const freshProducts = Array.isArray(event?.detail?.products) ? event.detail.products : [];
            if (!freshProducts.length) {
                return;
            }
            hasFetchedAllProducts.current = true;
            setProducts(freshProducts);
        };

        window.addEventListener(PRODUCT_CACHE_UPDATED_EVENT, handleProductsCacheUpdated);
        return () => {
            window.removeEventListener(PRODUCT_CACHE_UPDATED_EVENT, handleProductsCacheUpdated);
        };
    }, []);

    const handleCloseWooSyncModal = () => {
        if (wooSyncRunning) {
            return;
        }
        setWooSyncModalOpen(false);
    };

    const handleWooSyncTabChange = (_, value) => {
        setWooSyncTab(value);
    };

    const handleWooSyncSearchChange = (event) => {
        setWooSyncSearchTerm(event.target.value);
        setWooSyncPages({ matched: 1, review: 1, missing: 1, errors: 1 });
    };

    const paginatedWooSyncMatched = filteredWooSyncMatched.slice(
        (wooSyncPages.matched - 1) * wooSyncPageSize,
        wooSyncPages.matched * wooSyncPageSize,
    );
    const paginatedWooSyncReview = filteredWooSyncReview.slice(
        (wooSyncPages.review - 1) * wooSyncPageSize,
        wooSyncPages.review * wooSyncPageSize,
    );
    const paginatedWooSyncMissing = filteredWooSyncMissing.slice(
        (wooSyncPages.missing - 1) * wooSyncPageSize,
        wooSyncPages.missing * wooSyncPageSize,
    );
    const paginatedWooSyncErrors = filteredWooSyncErrors.slice(
        (wooSyncPages.errors - 1) * wooSyncPageSize,
        wooSyncPages.errors * wooSyncPageSize,
    );

    const persistWooMatchToLocalProduct = useCallback(async (matchItem, options = {}) => {
        const localId = normalizeId(matchItem?.localId);
        const wooId = normalizeId(matchItem?.wooId);

        if (!localId || !wooId) {
            if (!options.silent) {
                toast.error('Impossible de définir le Woo ID pour ce produit.');
            }
            return false;
        }

        const response = await axios.put(
            `${API_URL}/products/${localId}`,
            { woocommerce_id: wooId },
            header()
        );

        const updatedProduct = response?.data?.product || { _id: localId, woocommerce_id: wooId };
        const linkedWooId = normalizeId(updatedProduct?.woocommerce_id || wooId);

        updateLocalProduct(updatedProduct);
        setWooSyncMatched((prev) => prev.map((item) => (
            normalizeId(item?.localId) === localId
                ? {
                    ...item,
                    localWooId: linkedWooId,
                    alreadyLinked: String(linkedWooId) === String(item?.wooId || ''),
                    updated: true,
                }
                : item
        )));
        setWooSyncProgress((prev) => ({
            ...prev,
            updated: prev.updated + (matchItem?.alreadyLinked ? 0 : 1),
        }));

        if (!options.silent) {
            toast.success(`Woo ID ${wooId} défini pour ${matchItem?.localName || 'le produit local'}.`);
        }

        return true;
    }, [updateLocalProduct]);

    const handleLinkWooMatchSingle = useCallback(async (matchItem) => {
        const localId = normalizeId(matchItem?.localId);
        if (!localId || normalizeId(matchItem?.localWooId)) {
            return;
        }

        setWooLinkingIds((prev) => (prev.includes(localId) ? prev : [...prev, localId]));
        try {
            await persistWooMatchToLocalProduct(matchItem);
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Erreur inconnue';
            toast.error(`Impossible de définir le Woo ID : ${message}`);
        } finally {
            setWooLinkingIds((prev) => prev.filter((id) => id !== localId));
        }
    }, [persistWooMatchToLocalProduct]);

    const handleLinkWooMatchBulk = useCallback(async () => {
        if (!selectedWooSyncMatchedPending.length) {
            toast.warning('Aucun produit sélectionné à lier.');
            return;
        }

        if (!window.confirm(`Définir le Woo ID pour ${selectedWooSyncMatchedPending.length} produit(s) sélectionné(s) ?`)) {
            return;
        }

        setWooBulkLinking(true);
        try {
            const results = await Promise.allSettled(
                selectedWooSyncMatchedPending.map((item) => persistWooMatchToLocalProduct(item, { silent: true }))
            );

            const successCount = results.filter((result) => result.status === 'fulfilled' && result.value).length;
            const errorCount = results.length - successCount;

            if (successCount > 0) {
                toast.success(`${successCount} produit(s) local(aux) mis à jour avec le Woo ID.`);
            }
            if (errorCount > 0) {
                toast.warning(`${errorCount} produit(s) n'ont pas pu être mis à jour.`);
            }
        } finally {
            setWooBulkLinking(false);
        }
    }, [persistWooMatchToLocalProduct, selectedWooSyncMatchedPending]);

    const handleLinkWooStrongMatchesBulk = useCallback(async () => {
        if (!wooSyncStrongPending.length) {
            toast.warning('Aucun match fort non lié.');
            return;
        }

        if (!window.confirm(`Définir automatiquement le Woo ID pour ${wooSyncStrongPending.length} match(s) fort(s) ?`)) {
            return;
        }

        setWooBulkLinking(true);
        try {
            const results = await Promise.allSettled(
                wooSyncStrongPending.map((item) => persistWooMatchToLocalProduct(item, { silent: true }))
            );

            const successCount = results.filter((result) => result.status === 'fulfilled' && result.value).length;
            const errorCount = results.length - successCount;

            if (successCount > 0) {
                toast.success(`${successCount} match(s) fort(s) lié(s) automatiquement.`);
            }
            if (errorCount > 0) {
                toast.warning(`${errorCount} match(s) fort(s) n'ont pas pu être liés.`);
            }
        } finally {
            setWooBulkLinking(false);
        }
    }, [persistWooMatchToLocalProduct, wooSyncStrongPending]);

    const handlePauseWooSync = useCallback(() => {
        if (!wooSyncRunning || wooSyncPaused) {
            return;
        }
        wooSyncPauseRef.current = true;
        setWooSyncPaused(true);
        toast.info('Synchronisation WooCommerce mise en pause.');
    }, [wooSyncPaused, wooSyncRunning]);

    const handleResumeWooSync = useCallback(() => {
        if (!wooSyncRunning || !wooSyncPaused) {
            return;
        }
        wooSyncPauseRef.current = false;
        setWooSyncPaused(false);
        toast.info('Synchronisation WooCommerce reprise.');
    }, [wooSyncPaused, wooSyncRunning]);

    const handleStopWooSync = useCallback(() => {
        if (!wooSyncRunning) {
            return;
        }
        wooSyncStopRef.current = true;
        wooSyncPauseRef.current = false;
        setWooSyncPaused(false);
        setWooSyncStopped(true);
        toast.warning('Arrêt de la synchronisation demandé.');
    }, [wooSyncRunning]);

    const waitWhileWooSyncPaused = useCallback(async () => {
        while (wooSyncPauseRef.current && !wooSyncStopRef.current) {
            await new Promise((resolve) => setTimeout(resolve, 250));
        }
    }, []);

    const handleWooSync = async () => {
        setWooSyncModalOpen(true);
        setWooSyncRunning(true);
        setWooSyncPaused(false);
        setWooSyncStopped(false);
        wooSyncPauseRef.current = false;
        wooSyncStopRef.current = false;
        setWooSyncTab(0);
        setWooSyncMatched([]);
        setWooSyncMissing([]);
        setWooSyncErrors([]);
        setWooSyncSearchTerm('');
        setWooSyncPages({ matched: 1, review: 1, missing: 1, errors: 1 });
        setWooSyncProgress({ processed: 0, matched: 0, review: 0, missing: 0, updated: 0, total: 0, page: 0, totalPages: 0 });
        setWooLinkingIds([]);
        setWooBulkLinking(false);

        try {
            const localProducts = await fetchAllProductsOnce(true);

            const searchableLocalProducts = (Array.isArray(localProducts) ? localProducts : [])
                .map((product) => {
                    const normalizedName = normalizeProductMatchName(product?.name);
                    const barcode = String(firstAvailableValue(product?.barcode, product?.code_barre, product?.codeBarre, product?.ean, product?.ean13, product?.sku, product?.reference, product?.ref) || '').trim();
                    const skuCandidates = Array.from(new Set([
                        barcode,
                        barcode ? barcode.replace(/\D/g, '') : '',
                        barcode ? `CLDMA${barcode.replace(/\D/g, '') || barcode}` : '',
                    ]
                        .filter(Boolean)
                        .map((value) => normalizeProductMatchName(value))));
                    const wooIds = extractWooCommerceIds(product);
                    return {
                        product,
                        id: normalizeId(product?._id),
                        normalizedName,
                        tokens: tokenizeProductName(product?.name),
                        price: normalizePriceNumber(firstAvailableValue(product?.regular_price, product?.price, product?.prix, product?.selling_price, product?.price_ttc, product?.tarif)),
                        wooId: wooIds[0] || '',
                        wooIds,
                        skuCandidates,
                        imageName: imageBasename(resolveImageUrl(firstAvailableValue(product?.url_product, product?.image, product?.image_url, product?.photo, product?.picture, product?.main_image))),
                    };
                })
                .filter((item) => item.id && (item.normalizedName || item.wooIds.length || item.skuCandidates.length));

            const linkedWooIdMap = new Map();
            const skuMap = new Map();
            const exactNameMap = new Map();
            searchableLocalProducts.forEach((item) => {
                item.wooIds.forEach((wooId) => {
                    linkedWooIdMap.set(String(wooId), item);
                });
                item.skuCandidates.forEach((sku) => {
                    if (!sku) return;
                    const values = skuMap.get(sku) || [];
                    values.push(item);
                    skuMap.set(sku, values);
                });
                if (!item.normalizedName) {
                    return;
                }
                const values = exactNameMap.get(item.normalizedName) || [];
                values.push(item);
                exactNameMap.set(item.normalizedName, values);
            });

            const buildWooMatchCandidates = (wooProduct, usedLocalIds) => {
                const wooName = wooProduct?.name || '';
                const normalizedWooName = normalizeProductMatchName(wooName);
                const wooSku = normalizeProductMatchName(wooProduct?.sku || '');
                const wooPrice = normalizePriceNumber(wooProduct?.regular_price || wooProduct?.price);
                const wooImageName = imageBasename(wooProduct?.images?.[0]?.src || '');
                const wooBarcodeCandidates = [
                    wooSku,
                    normalizeProductMatchName(String(wooProduct?.id || '')),
                    normalizeProductMatchName(String(wooProduct?.sku || '').replace(/\D/g, '')),
                ].filter(Boolean);
                const candidateMap = new Map();

                const addCandidate = (item) => {
                    if (!item?.id || usedLocalIds.has(item.id)) return;
                    candidateMap.set(item.id, item);
                };

                addCandidate(linkedWooIdMap.get(String(wooProduct?.id || '')));
                wooBarcodeCandidates.forEach((candidateSku) => {
                    (skuMap.get(candidateSku) || []).forEach(addCandidate);
                });
                (exactNameMap.get(normalizedWooName) || []).forEach(addCandidate);

                const wooTokens = tokenizeProductName(wooName).slice(0, 4);
                searchableLocalProducts.forEach((item) => {
                    if (candidateMap.has(item.id) || usedLocalIds.has(item.id)) return;
                    const sharesStrongToken = wooTokens.some((token) => item.tokens.includes(token));
                    const includesName = normalizedWooName && item.normalizedName.includes(normalizedWooName);
                    const includedByName = normalizedWooName && normalizedWooName.includes(item.normalizedName);
                    if (sharesStrongToken || includesName || includedByName) {
                        addCandidate(item);
                    }
                });

                return Array.from(candidateMap.values())
                    .map((item) => {
                        let score = 0;
                        const reasons = [];
                        const wooProductId = String(wooProduct?.id || '');
                        const alreadyLinked = item.wooIds.some((wooId) => String(wooId) === wooProductId);

                        if (alreadyLinked) {
                            score += 100;
                            reasons.push('Woo ID déjà lié');
                        }

                        if (wooBarcodeCandidates.some((candidateSku) => item.skuCandidates.includes(candidateSku))) {
                            score += 42;
                            reasons.push('SKU / barcode identique');
                        }

                        if (normalizedWooName && item.normalizedName === normalizedWooName) {
                            score += 38;
                            reasons.push('Nom identique');
                        } else {
                            const tokenSimilarity = calculateTokenSimilarity(wooName, item.product?.name);
                            if (tokenSimilarity >= 0.75) {
                                score += 32;
                                reasons.push('Nom très proche');
                            } else if (tokenSimilarity >= 0.5) {
                                score += 22;
                                reasons.push('Nom similaire');
                            } else if (normalizedWooName && (item.normalizedName.includes(normalizedWooName) || normalizedWooName.includes(item.normalizedName))) {
                                score += 18;
                                reasons.push('Nom partiellement inclus');
                            }
                        }

                        if (wooPrice !== null && item.price !== null) {
                            const diff = Math.abs(wooPrice - item.price);
                            const base = Math.max(wooPrice, item.price, 1);
                            const percent = diff / base;
                            if (diff <= 1) {
                                score += 16;
                                reasons.push('Prix identique');
                            } else if (percent <= 0.05) {
                                score += 10;
                                reasons.push('Prix proche');
                            } else if (percent > 0.25) {
                                score -= 12;
                                reasons.push('Prix éloigné');
                            }
                        }

                        if (wooImageName && item.imageName && wooImageName === item.imageName) {
                            score += 8;
                            reasons.push('Image similaire');
                        } else if (wooProduct?.images?.[0]?.src && firstAvailableValue(item.product?.url_product, item.product?.image, item.product?.image_url, item.product?.photo, item.product?.picture, item.product?.main_image)) {
                            score += 2;
                            reasons.push('Images disponibles');
                        }

                        return {
                            item,
                            score: Math.max(0, Math.min(100, Math.round(score))),
                            reasons,
                            alreadyLinked,
                        };
                    })
                    .sort((left, right) => right.score - left.score)
                    .slice(0, 4);
            };

            let page = 1;
            let totalPages = 1;
            let processed = 0;
            let matched = 0;
            let review = 0;
            let missing = 0;
            let updated = 0;
            const usedLocalIds = new Set();

            while (page <= totalPages) {
                if (wooSyncStopRef.current) {
                    break;
                }
                await waitWhileWooSyncPaused();
                if (wooSyncStopRef.current) {
                    break;
                }
                const response = await getWooCommerceProductsPage({ per_page: 50, page });
                const wooProductsPage = Array.isArray(response?.data) ? response.data : [];
                totalPages = Number(response?.totalPages || 1);

                const simpleWooProducts = wooProductsPage.filter((wooProduct) => `${wooProduct?.type || ''}`.toLowerCase() === 'simple');

                for (const wooProduct of simpleWooProducts) {
                    if (wooSyncStopRef.current) {
                        break;
                    }
                    await waitWhileWooSyncPaused();
                    if (wooSyncStopRef.current) {
                        break;
                    }
                    processed += 1;
                    const candidates = buildWooMatchCandidates(wooProduct, usedLocalIds);
                    const bestCandidate = candidates[0];
                    const localMatch = bestCandidate?.item?.product;
                    const isStrongMatch = bestCandidate && (bestCandidate.alreadyLinked || bestCandidate.score >= 72);
                    const isReviewMatch = bestCandidate && !isStrongMatch && bestCandidate.score >= 40;

                    if (isStrongMatch || isReviewMatch) {
                        usedLocalIds.add(bestCandidate.item.id);
                        if (isReviewMatch) {
                            review += 1;
                        } else {
                            matched += 1;
                        }
                        const alreadyLinked = extractWooCommerceIds(localMatch).some((wooId) => String(wooId) === String(wooProduct?.id || ''));
                        if (alreadyLinked) {
                            updated += 1;
                        }

                        setWooSyncMatched((prev) => ([
                            ...prev,
                            {
                                wooId: wooProduct?.id,
                                wooName: wooProduct?.name || '',
                                wooImage: wooProduct?.images?.[0]?.src || '',
                                wooPrice: wooProduct?.regular_price || wooProduct?.price || '',
                                sku: wooProduct?.sku || '',
                                localId: normalizeId(localMatch?._id),
                                localName: localMatch?.name || '',
                                localImage: resolveImageUrl(firstAvailableValue(localMatch?.url_product, localMatch?.image, localMatch?.image_url, localMatch?.photo, localMatch?.picture, localMatch?.main_image)),
                                localPrice: firstAvailableValue(localMatch?.regular_price, localMatch?.price, localMatch?.prix, localMatch?.selling_price, localMatch?.price_ttc, localMatch?.tarif) || '',
                                localWooId: extractWooCommerceIds(localMatch)[0] || '',
                                updated: false,
                                alreadyLinked,
                                matchScore: bestCandidate.score,
                                matchReasons: bestCandidate.reasons,
                                matchStatus: isReviewMatch ? 'review' : 'strong',
                                candidates: candidates.map((candidate) => ({
                                    localId: candidate.item.id,
                                    localName: candidate.item.product?.name || '',
                                    localImage: resolveImageUrl(firstAvailableValue(candidate.item.product?.url_product, candidate.item.product?.image, candidate.item.product?.image_url, candidate.item.product?.photo, candidate.item.product?.picture, candidate.item.product?.main_image)),
                                    localPrice: firstAvailableValue(candidate.item.product?.regular_price, candidate.item.product?.price, candidate.item.product?.prix, candidate.item.product?.selling_price, candidate.item.product?.price_ttc, candidate.item.product?.tarif) || '',
                                    localWooId: extractWooCommerceIds(candidate.item.product)[0] || '',
                                    matchScore: candidate.score,
                                    matchReasons: candidate.reasons,
                                })),
                            },
                        ]));
                    } else {
                        missing += 1;
                        setWooSyncMissing((prev) => ([
                            ...prev,
                            {
                                wooId: wooProduct?.id,
                                name: wooProduct?.name || '',
                                image: wooProduct?.images?.[0]?.src || '',
                                galleryImages: Array.isArray(wooProduct?.images) ? wooProduct.images.map((img) => img?.src).filter(Boolean) : [],
                                description: wooProduct?.description || wooProduct?.short_description || '',
                                sku: wooProduct?.sku || '',
                                price: wooProduct?.regular_price || wooProduct?.price || '',
                                regularPrice: wooProduct?.regular_price || wooProduct?.price || '',
                                salePrice: wooProduct?.sale_price || '',
                                status: wooProduct?.status || '',
                            },
                        ]));
                    }

                    setWooSyncProgress({
                        processed,
                        matched,
                        review,
                        missing,
                        updated,
                        total: Number(response?.total || 0),
                        page,
                        totalPages,
                    });
                }

                page += 1;
            }

            if (wooSyncStopRef.current) {
                toast.warning('Synchronisation WooCommerce arrêtée.');
            } else {
                toast.success('Synchronisation WooCommerce terminée.');
            }
        } catch (error) {
            console.error('WooCommerce sync error:', error);
            setWooSyncErrors((prev) => ([
                ...prev,
                {
                    type: 'sync',
                    message: error?.response?.data?.message || error?.message || 'Erreur pendant la synchronisation WooCommerce',
                },
            ]));
            toast.error('Erreur pendant la synchronisation WooCommerce.');
        } finally {
            setWooSyncRunning(false);
            setWooSyncPaused(false);
            wooSyncPauseRef.current = false;
        }
    };

    const handleReloadProducts = useCallback(() => {
        const pageToReload = currentPageRef.current ?? currentPage ?? 1;
        fetchProducts(pageToReload, { forceFullRefresh: true });
    }, [currentPage, fetchProducts]);

    useEffect(() => {
        fetchProducts(1);
        fetchStores(); // Fetch stores for dropdown
        getWooCategories();
        fetchWebsiteDeliveryZones();
        fetchPlateformsData();
        fetchAddons();
        // Removed auto-fetch interval for products
    }, [fetchProducts, fetchPlateformsData, fetchAddons]);

    useEffect(() => {
        if (products.length > 0) {
            return;
        }
        fetchPagedProducts(currentPage);
    }, [currentPage, fetchPagedProducts, products.length]);

    const submitStockMovement = async () => {
        if (!stockModalProduct?._id) {
            toast.error("Aucun produit sélectionné pour l'ajout de stock.");
            return;
        }

        if (!stockMovementForm.storeId) {
            toast.warning('Veuillez sélectionner un magasin.');
            return;
        }

        const quantityValue = Number(stockMovementForm.quantity);

        if (!Number.isFinite(quantityValue) || quantityValue <= 0) {
            toast.warning('Veuillez saisir une quantité valide.');
            return;
        }

        const productName = stockModalProduct?.name;
        const selectedStore = stores.find((storeOption) => {
            if (!storeOption) {
                return false;
            }

            const selectedValue = stockMovementForm.storeId ? String(stockMovementForm.storeId) : '';
            if (!selectedValue) {
                return false;
            }

            const optionValue = storeOption.value ? String(storeOption.value) : '';
            if (optionValue && optionValue === selectedValue) {
                return true;
            }

            const optionStoreCode = storeOption.storeCode ? String(storeOption.storeCode) : '';
            if (optionStoreCode && optionStoreCode === selectedValue) {
                return true;
            }

            return false;
        });

        const resolvedStoreId = selectedStore?.rawId ? String(selectedStore.rawId) : '';

        if (!resolvedStoreId) {
            toast.error('Magasin sélectionné invalide. Veuillez réessayer.');
            return;
        }

        const payload = {
            store_id: resolvedStoreId,
            product_id: stockModalProduct._id,
            stock_state: 'available',
            quantity: quantityValue,
        };

        if (productName) {
            payload.manual = { description: `Ajout de stock - ${productName}` };
        }

        setIsSubmittingStockMovement(true);

        try {
            await axios.post(`${API_URL}/inventory/movements/in`, payload, header());

            toast.success(
                productName
                    ? `Stock ajouté pour ${productName}.`
                    : 'Stock ajouté avec succès.'
            );

            fetchProducts(currentPage);

            if (typeof window !== 'undefined') {
                const modalElement = document.getElementById('stockMovementModal');
                if (modalElement && window.bootstrap?.Modal) {
                    const modalInstance = window.bootstrap.Modal.getInstance(modalElement)
                        || new window.bootstrap.Modal(modalElement);
                    modalInstance.hide();
                }
            }

            closeStockMovementModal();
        } catch (error) {
            const message = error?.response?.data?.message || error.message;
            toast.error(`Impossible d'ajouter le stock : ${message}`);
        } finally {
            setIsSubmittingStockMovement(false);
        }
    };

    const componentFilterOptions = useMemo(() => {
        if (!Array.isArray(products)) {
            return [];
        }

        return products
            .filter((product) => product?.isComposant)
            .map((product) => ({
                value: product._id,
                label: product.name || 'Produit sans nom',
            }));
    }, [products]);

    const platformCounts = useMemo(() => {
        const counts = {
            glovo: 0,
            kooul: 0,
            woocommerce: 0,
            marjaneActive: 0,
        };

        if (!Array.isArray(products)) {
            return counts;
        }

        products.forEach((product) => {
            const productPlateformIds = getProductPlateformIds(product);
            Object.keys(counts).forEach((platform) => {
                if (matchesPlatformSummary(product, platform, {
                    productPlatformIds: productPlateformIds,
                    glovoPlatformIds: glovoPlateformIds,
                    kooulPlatformIds: kooulPlateformIds,
                })) counts[platform] += 1;
            });
        });

        return counts;
    }, [getProductPlateformIds, glovoPlateformIds, kooulPlateformIds, products]);

    const filterSummaryProducts = (items) => !summaryPlatformFilter ? items : items.filter((product) =>
        matchesPlatformSummary(product, summaryPlatformFilter, {
            productPlatformIds: getProductPlateformIds(product),
            glovoPlatformIds: glovoPlateformIds,
            kooulPlatformIds: kooulPlateformIds,
        }));

    useEffect(() => {
        setComponentFilter((prevFilter) => {
            const validIds = prevFilter.filter((id) =>
                componentFilterOptions.some((option) => option.value === id)
            );

            if (
                validIds.length === prevFilter.length &&
                validIds.every((id, index) => id === prevFilter[index])
            ) {
                return prevFilter;
            }

            return validIds;
        });
    }, [componentFilterOptions]);

    const handleToggleIsComponent = async (productId, nextValue) => {
        if (!productId) {
            return false;
        }

        const success = await updateIsComponentStatus(productId, nextValue);
        if (success) {
            setProducts((prevProducts) =>
                Array.isArray(prevProducts)
                    ? prevProducts.map((product) => (
                        product?._id === productId
                            ? { ...product, isComposant: nextValue }
                            : product
                    ))
                    : prevProducts
            );

            if (!nextValue) {
                setComponentFilter((prevFilter) =>
                    prevFilter.filter((id) => id !== productId)
                );
            }
        }

        return success;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };
    const handleLongDescriptionToggle = (e) => {
        const { checked } = e.target;
        setShowLongDescription(checked);
        if (!checked) {
            setFormData((prev) => ({ ...prev, long_description: '' }));
        }
    };
    const handleGalleryPicturesChange = (e) => {
        const files = Array.from(e.target.files || []);
        setGalleryPictures(files);
    };

    const openCreateProductFromWooMissing = (wooItem) => {
        const generatedBarcode = generateBarcode()?.barcodeValue || '';
        const draftProduct = {
            name: wooItem?.name || '',
            description: wooItem?.description || '',
            long_description: wooItem?.description || '',
            regular_price: wooItem?.regularPrice || wooItem?.price || '',
            discounted_price: wooItem?.salePrice || '',
            url_product: wooItem?.image || '',
            barcode: generatedBarcode,
            category: null,
            availability: true,
            details: [],
            type: "borne",
            stores: [],
            isComposant: false,
            gallery_images: Array.isArray(wooItem?.galleryImages) ? wooItem.galleryImages : [],
        };

        openProductDraftModal(draftProduct);
        setWooSyncModalOpen(false);
    };

    const resolveImageUrl = (path) => {
        if (!path) {
            return '';
        }

        const normalized = `${path}`.replace(/^\//, '');
        if (normalized.startsWith('http')) {
            return normalized;
        }

        return `${process.env.REACT_APP_DATA_PIC_PATH}/${normalized}`;
    };
    const uploadProductImageFiles = async ({ name, regularPrice, description, files }) => {
        const uploadedFiles = [];

        for (const file of files) {
            const uploadData = new FormData();
            uploadData.append('productName', name);
            uploadData.append('price', regularPrice);
            uploadData.append('description', description);
            uploadData.append('picture', file);

            const uploadResponse = await axios.post(
                'https://office.le-celadon.ma/uploadimagesgetUrl.php',
                uploadData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (!uploadResponse?.data?.success) {
                throw new Error(uploadResponse?.data?.error || 'Error uploading picture');
            }

            uploadedFiles.push(uploadResponse.data.filename);
        }

        return uploadedFiles;
    };

    const handleIsComposantChange = (e) => {
        const { checked } = e.target;
        setFormData(prev => ({ ...prev, isComposant: checked }));
    };

    const handleWebsiteDeliverySwitchChange = (field) => (event) => {
        const { checked } = event.target;
        setFormData((prev) => {
            const currentDelivery = normalizeWebsiteDelivery(prev.website_delivery);
            const nextDelivery = {
                ...currentDelivery,
                [field]: checked,
            };
            if (field === 'express_enabled' && !checked) {
                nextDelivery.express_zones = [];
            }
            return {
                ...prev,
                website_delivery: normalizeWebsiteDelivery(nextDelivery),
            };
        });
    };

    const handleWebsiteDeliveryZonesChange = (selectedOptions) => {
        setFormData((prev) => ({
            ...prev,
            website_delivery: normalizeWebsiteDelivery({
                ...prev.website_delivery,
                express_enabled: true,
                express_zones: (selectedOptions || []).map((option) => ({
                    code: option.value,
                    name: option.label,
                })),
            }),
        }));
    };

    const syncProductDeliveryMetaToWoo = async (product) => {
        const wooProductId = normalizeId(product?.woocommerce_id);
        if (!wooProductId) {
            return;
        }

        await updateWooCommerceProductDeliveryMeta(wooProductId, normalizeWebsiteDelivery(product?.website_delivery));
        await updateWooCommerceProductVaseMeta(wooProductId, product?.vase_inclusion || 'unspecified');
    };

    const handleWebsiteDeliveryUpdate = async (product, nextDelivery) => {
        const productId = normalizeId(product?._id || product?.id);
        if (!productId) {
            toast.error('Produit introuvable.');
            return null;
        }

        const normalizedDelivery = normalizeWebsiteDelivery(nextDelivery);
        const response = await axios.put(
            `${API_URL}/products/${productId}`,
            { website_delivery: normalizedDelivery },
            header()
        );

        const savedProduct = response.data?.product || {
            ...product,
            website_delivery: normalizedDelivery,
        };

        updateLocalProduct({
            ...savedProduct,
            _id: savedProduct?._id || productId,
            website_delivery: normalizedDelivery,
        });

        if (normalizeId(savedProduct?.woocommerce_id || product?.woocommerce_id)) {
            await syncProductDeliveryMetaToWoo({
                ...product,
                ...savedProduct,
                website_delivery: normalizedDelivery,
                woocommerce_id: savedProduct?.woocommerce_id || product?.woocommerce_id,
            });
        }

        toast.success('Livraison site mise à jour.');
        return savedProduct;
    };

    const handleVaseInclusionUpdate = async (product, vaseInclusion) => {
        const productId = normalizeId(product?._id || product?.id);
        if (!productId || !['unspecified', 'included', 'not_included'].includes(vaseInclusion)) {
            throw new Error('Produit ou choix du vase invalide.');
        }
        const response = await axios.put(`${API_URL}/products/${productId}`, {
            vase_inclusion: vaseInclusion,
        }, header());
        const savedProduct = { ...product, ...response.data?.product, vase_inclusion: vaseInclusion };
        updateLocalProduct(savedProduct);
        const wooProductId = normalizeId(savedProduct.woocommerce_id);
        if (wooProductId) {
            try {
                await updateWooCommerceProductVaseMeta(wooProductId, vaseInclusion);
            } catch (error) {
                toast.error('Vase enregistré dans Pegasus, mais la synchronisation site a échoué. Cliquez sur « Synchroniser le badge site ».');
                throw error;
            }
        }
        toast.success('Information vase enregistrée.');
        return savedProduct;
    };


    const handleEditClick = (product) => {
        console.log(product);
        setCloningSourceProduct(null);
        setCurrentProduct(product);
        setEditingProduct(product); // Set the product to edit
        setFormData({
            name: product.name,
            description: product.description,
            long_description: product.long_description || '',
            regular_price: product.regular_price,
            discounted_price: product.discounted_price,
            url_product: product.url_product || '',
            gallery_images: Array.isArray(product.gallery_images) ? product.gallery_images : [],
            category: product.category || null,
            stores: product.stores || [], // Ensure stores are set
            type: product.type ?? "borne",
            barcode: (!product.barcode) ? generateBarcode()?.barcodeValue : product.barcode,
            isComposant: Boolean(product.isComposant),
            woocommerce_id: product.woocommerce_id || '',
            website_delivery: normalizeWebsiteDelivery(product.website_delivery),
        });
        setShowLongDescription(Boolean(`${product.long_description || ''}`.trim()));
        resetPictureField();
        resetGalleryField();
        window.scrollTo({ top: 100, behavior: "smooth" });
    };

    const handleCloneProduct = (product) => {
        if (!product) {
            toast.error('Produit introuvable pour le clonage.');
            return;
        }

        const clonedProduct = {
            name: `${product.name || ''} - Copie`.trim(),
            description: product.description || '',
            long_description: product.long_description || '',
            regular_price: product.regular_price || '',
            discounted_price: product.discounted_price || '',
            url_product: product.url_product || '',
            gallery_images: Array.isArray(product.gallery_images) ? [...product.gallery_images] : [],
            category: product.category || null,
            stores: Array.isArray(product.stores)
                ? product.stores.map((store) => ({ ...store }))
                : [],
            type: product.type ?? "borne",
            barcode: generateBarcode()?.barcodeValue || '',
            availability: product.availability !== undefined ? product.availability : true,
            details: Array.isArray(product.details)
                ? product.details.map((detail) => ({ ...detail }))
                : [],
            isComposant: Boolean(product.isComposant),
            woocommerce_id: '',
            website_delivery: normalizeWebsiteDelivery(product.website_delivery),
        };

        resetPictureField();
        resetGalleryField();
        setEditingProduct(null);
        setCloningSourceProduct(product);
        setCurrentProduct(clonedProduct);
        setDefaultData(clonedProduct);
        setFormData(clonedProduct);
        setShowLongDescription(Boolean(`${clonedProduct.long_description || ''}`.trim()));
        setComponents([{ product: '', quantity: '1' }]);
        window.scrollTo({ top: 100, behavior: "smooth" });
    };
    const handleStoreIdsChange = (selectedOptions) => {
        setFormData({
            ...formData,
            stores: selectedOptions.map(option => ({
                store_id: option.value,
                availability: true // Default availability to true for each store
            }))
        });
    };
    const handleproductCategoryChange = (selectedOptions) => {
        console.log(selectedOptions);
        setFormData({
            ...formData,
            category: selectedOptions.value
        });
    };


    const handleStoreAvailabilityChange = (storeId, availability) => {
        setFormData({
            ...formData,
            stores: formData.stores.map(store =>
                store.store_id === storeId ? { ...store, availability } : store
            )
        });
    };
    const handlefilter = (filtertype) => {
        setFilterProducts(prev =>
            prev.includes(filtertype)
                ? prev.filter(type => type !== filtertype) // remove
                : [...prev, filtertype]                    // add
        );
        setCurrentPage(1);
    };
    const handlePlateformFilterChange = (options) => {
        setPlateformFilters(options ? options.map(option => option.value) : []);
        setCurrentPage(1);
    };
    const handleComponentFilterChange = (opts) => {
        setComponentFilter(opts ? opts.map(o => o.value) : []);
        setCurrentPage(1);
    };
    const handleFilterByCategory = (category) => {
        setSelectedCategory(category);
        setFilterProductsByCategory(category ? category.value : null);
        setCurrentPage(1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Check if required fields are filled
            if (!formData.name || !formData.regular_price || !formData.description || !formData.category) {
                toast.error("Please fill out all required fields.");
                return;
            }

            // Determine if we're adding or updating
            const isCloning = Boolean(cloningSourceProduct);
            const isAdding = !editingProduct;
            const trimmedName = `${formData.name || ''}`.trim();
            const trimmedRegularPrice = `${formData.regular_price || ''}`.trim();
            const trimmedDescription = `${formData.description || ''}`.trim();
            let nextImageUrl = formData.url_product || '';
            let nextGalleryImages = Array.isArray(formData.gallery_images) ? [...formData.gallery_images] : [];

            if (picture) {
                const [uploadedPrimaryImage] = await uploadProductImageFiles({
                    name: trimmedName,
                    regularPrice: trimmedRegularPrice,
                    description: trimmedDescription,
                    files: [picture],
                });
                nextImageUrl = uploadedPrimaryImage || '';
            }

            if (galleryPictures.length) {
                nextGalleryImages = await uploadProductImageFiles({
                    name: trimmedName,
                    regularPrice: trimmedRegularPrice,
                    description: trimmedDescription,
                    files: galleryPictures,
                });
            }


            // Determine the API endpoint and method
            const method = isAdding ? 'post' : 'put';
            const apiEndpoint = isAdding
                ? `${API_URL}/products`
                : `${API_URL}/products/${editingProduct._id}`;
            const { isComposant, ...productPayload } = {
                ...formData,
                url_product: nextImageUrl,
                gallery_images: nextGalleryImages,
            };

            // Send the request to add or update the product
            const response = await axios[method](apiEndpoint, productPayload, header());

            // Handle the response
            if (response.data.success) {
                const nextBarcode = generateBarcode()?.barcodeValue;
                const resetData = {
                    ...defaultData,
                    name: '',
                    description: '',
                    long_description: '',
                    regular_price: '',
                    discounted_price: '',
                    category: null,
                    url_product: '',
                    gallery_images: [],
                    barcode: nextBarcode,
                    details: [],
                    type: "borne",
                    availability: true,
                    isComposant: false,
                    stores: [],
                    website_delivery: normalizeWebsiteDelivery(),
                };
                setDefaultData(resetData);
                setFormData(resetData);
                setShowLongDescription(false);
                setComponents([{ product: '', quantity: '1' }]);
                const productId = isAdding ? response.data.product?._id : editingProduct?._id;
                const savedProduct = response.data.product || {
                    ...formData,
                    _id: productId,
                    woocommerce_id: editingProduct?.woocommerce_id,
                };
                if (normalizeId(savedProduct?.woocommerce_id)) {
                    try {
                        await syncProductDeliveryMetaToWoo(savedProduct);
                    } catch (wooMetaError) {
                        console.error('Woo delivery meta sync error:', wooMetaError);
                        toast.warning('Produit sauvegardé, mais les badges livraison WooCommerce ne sont pas synchronisés.');
                    }
                }
                if (productId) {
                    const isComponentUpdated = await updateIsComponentStatus(productId, isComposant);
                    if (isComponentUpdated) {
                        setProducts((prevProducts) =>
                            Array.isArray(prevProducts)
                                ? prevProducts.map((product) =>
                                    product?._id === productId
                                        ? { ...product, isComposant: isComposant }
                                        : product
                                )
                                : prevProducts
                        );
                    }
                }
                toast.success(`Product ${isCloning ? 'cloned' : isAdding ? 'added' : 'updated'}: ${response.data.product.name}`);
                setEditingProduct(null); // Reset editing state
                setCloningSourceProduct(null);
                setPicture(null);
                setGalleryPictures([]);
                const picInput = document.getElementById("picture"); if (picInput) picInput.value = null;
                if (galleryInputRef.current) {
                    galleryInputRef.current.value = null;
                }

                // Update local product states so UI reflects changes immediately
                if (response.data.product) {
                    if (isAdding) {
                        addLocalProductAndGoToFirstPage(response.data.product);
                    } else {
                        updateLocalProduct(response.data.product);
                    }
                }

                // No need to refresh - updateLocalProduct already handles it
            } else {
                toast.error('Error: ' + response.data.error);
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('An error occurred. Please try again.');
        }
    };
    const updateAvailability = async (productId, storeId, availability) => {
        try {
            await axios.post(
                `${API_URL}/products/${productId}/store/${storeId}/availability`,
                { availability },
                header({ "Content-Type": "application/json" })
            ).then(r => {
                console.log("Disponibilité mise à jour :", r.data);
                if (r.data.product) {
                    updateLocalProduct(r.data.product);
                }
            });
        } catch (error) {
            console.error("Erreur lors de la mise à jour :", error.response?.data || error.message);
        }
    };

    const getStoreExpressStoreCode = (store) => (
        String(store?.storeCode || store?.value || store?.code || store?.rawId || '').trim()
    );

    const isProductAvailableForStoreExpress = (product, store) => {
        const storeCode = getStoreExpressStoreCode(store);
        if (!storeCode || !Array.isArray(product?.stores)) {
            return false;
        }

        const entry = product.stores.find((productStore) => {
            const productStoreId = normalizeId(productStore?.store_id);
            return productStoreId === storeCode;
        });

        return Boolean(entry?.availability);
    };

    const isProductFeaturedForStoreExpress = (product, store) => {
        const storeCode = getStoreExpressStoreCode(store);
        if (!storeCode || !Array.isArray(product?.stores)) {
            return false;
        }

        const entry = product.stores.find((productStore) => {
            const productStoreId = normalizeId(productStore?.store_id);
            return productStoreId === storeCode;
        });

        return Boolean(entry?.featured);
    };

    const openStoreExpressDialog = (targetProducts) => {
        const safeProducts = Array.isArray(targetProducts)
            ? targetProducts.filter(Boolean)
            : [targetProducts].filter(Boolean);

        if (!safeProducts.length) {
            toast.warning('Sélectionnez au moins un produit.');
            return;
        }

        setStoreExpressProducts(safeProducts);
        setStoreExpressDialogOpen(true);
    };

    const openStoreExpressBulkDialog = () => {
        const selectedSet = new Set((Array.isArray(selectedProducts) ? selectedProducts : []).map((id) => normalizeId(id)));
        const targets = (Array.isArray(products) ? products : [])
            .filter((product) => selectedSet.has(normalizeId(product?._id)));

        openStoreExpressDialog(targets);
    };

    const closeStoreExpressDialog = () => {
        if (storeExpressSavingKey) {
            return;
        }

        setStoreExpressDialogOpen(false);
        setStoreExpressProducts([]);
    };

    const setStoreExpressStoreFields = async (targetProducts, store, payload) => {
        const storeCode = getStoreExpressStoreCode(store);
        const safeProducts = Array.isArray(targetProducts)
            ? targetProducts.filter(Boolean)
            : [targetProducts].filter(Boolean);

        if (!storeCode || !safeProducts.length) {
            return;
        }

        const savingLabel = typeof payload.featured === 'boolean' ? 'featured' : 'availability';
        const savingValue = typeof payload.featured === 'boolean' ? payload.featured : payload.availability;
        const savingKey = `${storeCode}:${savingLabel}:${savingValue ? 'on' : 'off'}`;
        setStoreExpressSavingKey(savingKey);

        try {
            const responses = await Promise.all(
                safeProducts.map((product) =>
                    axios.post(
                        `${API_URL}/products/${product._id}/store-express/${encodeURIComponent(storeCode)}/availability`,
                        payload,
                        header({ "Content-Type": "application/json" })
                    )
                )
            );

            const updatedProducts = responses
                .map((response) => response?.data?.product)
                .filter(Boolean);

            updatedProducts.forEach((updatedProduct) => updateLocalProduct(updatedProduct));

            setStoreExpressProducts((prevProducts) =>
                prevProducts.map((product) => {
                    const updatedProduct = updatedProducts.find((candidate) => normalizeId(candidate?._id) === normalizeId(product?._id));
                    return updatedProduct || product;
                })
            );

            toast.success(
                `${safeProducts.length} produit(s) mis à jour pour ${store.label || storeCode}`
            );
        } catch (error) {
            console.error('Erreur Store Express:', error.response?.data || error.message);
            toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour Store Express.');
        } finally {
            setStoreExpressSavingKey('');
        }
    };

    const setStoreExpressAvailability = (targetProducts, store, availability) => (
        setStoreExpressStoreFields(targetProducts, store, { availability })
    );

    const setStoreExpressFeatured = (targetProducts, store, featured) => (
        setStoreExpressStoreFields(targetProducts, store, { featured })
    );

    const updateGlobalAvailability = async (productId, availability) => {
        try {
            const response = await axios.post(
                `${API_URL}/products/${productId}/availability`,
                { availability },
                header({ "Content-Type": "application/json" })
            );
            if (response.data.product) {
                updateLocalProduct(response.data.product);
            }
            console.log("Disponibilité mise à jour :", response.data);
            return response.data;
        } catch (error) {
            console.error("Erreur lors de la mise à jour :", error.response?.data || error.message);
            return { success: false, message: error.response?.data?.message || "Erreur inconnue" };
        }
    };

    const submitComponents = async () => {
        const normalizedPayload = components
            .map((comp) => {
                const rawValue = comp.product;
                const productId = typeof rawValue === 'object'
                    ? rawValue?.value || rawValue?._id || rawValue?.id || rawValue?.product || rawValue?.component
                    : rawValue;
                const quantityNumber = Number(comp.quantity);
                return {
                    product: productId ? String(productId) : '',
                    quantity: quantityNumber,
                };
            })
            .filter((entry) => entry.product);

        if (!normalizedPayload.length) {
            toast.error('Veuillez sélectionner au moins un composant valide.');
            return;
        }

        const invalidEntry = normalizedPayload.find((entry) => !Number.isFinite(entry.quantity) || entry.quantity <= 0);
        if (invalidEntry) {
            toast.error('Chaque composant doit avoir une quantité positive.');
            return;
        }

        try {
            const response = await axios.put(`${process.env?.REACT_APP_PEGASUS_API_URL}/products/components/${currentProduct?._id}`, {
                components: normalizedPayload
            }, header());

            console.log("Produit mis à jour :", response.data);

            toast.success("Composants mis à jour avec succès.");
            fetchProducts(currentPage);
        } catch (error) {
            if (error.response) {
                console.error("Erreur API :", error.response.data);
                toast.error(`Erreur : ${error.response.data.error}`);
            } else {
                console.error("Erreur inconnue :", error);
                toast.warning("Erreur de connexion au serveur.");
            }
        } finally {
            setCurrentProduct(null);
        }
    };
    const handleDelete = async (id) => {
        try {
            await axios.delete(`${API_URL}/products/${id}`, header()).then(response => {
                if (response.status == 200) {
                    toast.success(response.data.message);
                    fetchProducts(currentPage);
                }
                else {
                    toast.error(response.data.message);
                }
            });
        } catch (e) {
            toast.error(`Une erreur c'est produite ${e?.message}`);
        }


    };

    const handleCropComplete = (file) => {
        setPicture(file);
        setPendingCropFile(null);
        setIsCropModalOpen(false);
    };

    const handleCropCancel = () => {
        setPendingCropFile(null);
        setIsCropModalOpen(false);
        if (pictureInputRef.current) {
            pictureInputRef.current.value = null;
        }
    };

    const handlePictureChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            setPicture(null);
            setPendingCropFile(null);
            return;
        }

        const reader = new FileReader();
        const img = new Image();
        const resetInput = () => {
            if (e?.target) {
                e.target.value = null;
            }
        };

        reader.onload = () => {
            const url = typeof reader.result === 'string' ? reader.result : '';
            img.onload = () => {
                const width = img.naturalWidth || img.width;
                const height = img.naturalHeight || img.height;

                if (!width || !height) {
                    toast.error("Impossible de lire l'image sélectionnée.");
                    resetInput();
                    return;
                }

                if (width < 500 || height < 500) {
                    toast.error("L'image doit mesurer au minimum 500 x 500 pixels.");
                    setPicture(null);
                    resetInput();
                    return;
                }

                const isSquare = Math.abs(width - height) < 1;
                if (isSquare) {
                    setPicture(file);
                    setPendingCropFile(null);
                    return;
                }

                setPendingCropFile(file);
                setIsCropModalOpen(true);
            };
            img.onerror = () => {
                toast.error("Impossible de charger l'image sélectionnée.");
                resetInput();
            };
            img.src = url;
        };
        reader.onerror = () => {
            toast.error("Impossible de lire l'image sélectionnée.");
            resetInput();
        };

        reader.readAsDataURL(file);
    };

    const fetchStores = async () => {

        const response = await axios.get(`${API_URL}/distinctStoreIds`, header());
        const mappedStores = Array.isArray(response.data?.response)
            ? response.data.response.map((store) => {
                const rawId = store?._id ?? store?.id ?? '';
                const normalizedRawId = rawId ? String(rawId) : '';
                const storeCode = store?.storeCode ?? store?.code ?? '';

                return {
                    value: storeCode ? String(storeCode) : normalizedRawId,
                    label: store?.name || storeCode || 'Magasin',
                    type: store?.store_type,
                    fees: store?.fees,
                    rawId: normalizedRawId,
                    storeCode: storeCode ? String(storeCode) : '',
                };
            })
            : [];

        setStores(mappedStores);
    };
    const resolveDefaultStoreId = (product) => {
        if (!Array.isArray(stores) || !stores.length) {
            return '';
        }

        if (!product || !Array.isArray(product?.stores)) {
            return stores[0]?.value || '';
        }

        for (const entry of product.stores) {
            if (!entry) {
                continue;
            }

            const rawEntryId = typeof entry.store_id === 'object'
                ? entry.store_id?._id || entry.store_id?.id || entry.store_id?.storeCode || entry.store_id?.store_id || ''
                : entry.store_id;

            const normalizedEntryId = rawEntryId ? String(rawEntryId) : '';

            if (!normalizedEntryId) {
                continue;
            }

            const matchingStore = stores.find((storeOption) => {
                if (!storeOption) {
                    return false;
                }

                const optionValue = storeOption.value ? String(storeOption.value) : '';
                if (optionValue && optionValue === normalizedEntryId) {
                    return true;
                }

                const optionRawId = storeOption.rawId ? String(storeOption.rawId) : '';
                if (optionRawId && optionRawId === normalizedEntryId) {
                    return true;
                }

                const optionStoreCode = storeOption.storeCode ? String(storeOption.storeCode) : '';
                return optionStoreCode && optionStoreCode === normalizedEntryId;
            });

            if (matchingStore?.value) {
                return matchingStore.value;
            }
        }

        return stores[0]?.value || '';
    };

    const openStockMovementModal = (product) => {
        setStockModalProduct(product || null);
        const defaultStoreId = resolveDefaultStoreId(product);
        setStockMovementForm({
            storeId: defaultStoreId || '',
            quantity: '1',
        });
    };

    const handleStockMovementStoreChange = (option) => {
        setStockMovementForm((prev) => ({
            ...prev,
            storeId: option?.value || '',
        }));
    };

    const handleStockMovementQuantityChange = (event) => {
        const { value } = event.target;
        setStockMovementForm((prev) => ({
            ...prev,
            quantity: value,
        }));
    };

    const closeStockMovementModal = () => {
        setStockModalProduct(null);
        setStockMovementForm({ storeId: '', quantity: '1' });
    };

    const getStoreRawIdFromOptionValue = useCallback((storeValue) => {
        const selectedValue = storeValue ? String(storeValue) : '';
        if (!selectedValue) {
            return '';
        }

        const selectedStore = stores.find((storeOption) => {
            if (!storeOption) return false;
            return (
                String(storeOption.value || '') === selectedValue ||
                String(storeOption.storeCode || '') === selectedValue ||
                String(storeOption.rawId || '') === selectedValue
            );
        });

        return selectedStore?.rawId ? String(selectedStore.rawId) : '';
    }, [stores]);

    const resolveProductStockPrice = useCallback((product) => {
        const candidates = [
            product?.sale_price,
            product?.discounted_price,
            product?.discountedPrice,
            product?.regular_price,
            product?.regularPrice,
            product?.price,
            product?.price_ttc,
            product?.priceTTC,
        ];

        for (const candidate of candidates) {
            const parsed = parsePriceValue(candidate);
            if (typeof parsed === 'number') {
                return parsed;
            }
        }

        return 0;
    }, [parsePriceValue]);

    const buildBulkStockDefaultRows = useCallback((targetProducts) => {
        const rows = {};

        (Array.isArray(targetProducts) ? targetProducts : []).forEach((product) => {
            const productId = normalizeId(product?._id || product?.id);
            if (!productId) return;

            const defaultStoreValue = resolveDefaultStoreId(product) || stores[0]?.value || '';
            rows[productId] = {
                stores: [{ storeId: defaultStoreValue, quantity: '1' }],
                pluCategory: '',
                customPluPrice: resolveProductStockPrice(product).toFixed(2),
            };
        });

        return rows;
    }, [resolveDefaultStoreId, resolveProductStockPrice, stores]);

    const openBulkStockModal = () => {
        if (!selectedBulkStockProducts.length) {
            toast.warning('Veuillez sélectionner au moins un produit.');
            return;
        }

        setBulkStockRows(buildBulkStockDefaultRows(selectedBulkStockProducts));
        setBulkStockGlobalStoreId('');
        setBulkStockModalOpen(true);
    };

    const closeBulkStockModal = () => {
        if (isSubmittingBulkStock) {
            return;
        }
        setBulkStockModalOpen(false);
    };

    const applyBulkStockGlobalStore = (storeId) => {
        setBulkStockGlobalStoreId(storeId || '');

        if (!storeId) {
            return;
        }

        setBulkStockRows((prev) => {
            const next = { ...prev };

            selectedBulkStockProducts.forEach((product) => {
                const productId = normalizeId(product?._id || product?.id);
                if (!productId) {
                    return;
                }

                const current = next[productId] || { stores: [{ storeId: '', quantity: '1' }] };
                const currentStores = Array.isArray(current.stores) && current.stores.length
                    ? current.stores
                    : [{ storeId: '', quantity: '1' }];

                next[productId] = {
                    ...current,
                    stores: [
                        { ...currentStores[0], storeId },
                        ...currentStores.slice(1),
                    ],
                };
            });

            return next;
        });
    };

    const updateBulkStockStoreLine = (productId, lineIndex, patch) => {
        setBulkStockRows((prev) => {
            const current = prev[productId] || { stores: [] };
            const nextStores = [...(current.stores || [])];
            nextStores[lineIndex] = { ...(nextStores[lineIndex] || {}), ...patch };
            return {
                ...prev,
                [productId]: { ...current, stores: nextStores },
            };
        });
    };

    const addBulkStockStoreLine = (productId) => {
        setBulkStockRows((prev) => {
            const current = prev[productId] || { stores: [] };
            return {
                ...prev,
                [productId]: {
                    ...current,
                    stores: [...(current.stores || []), { storeId: '', quantity: '1' }],
                },
            };
        });
    };

    const removeBulkStockStoreLine = (productId, lineIndex) => {
        setBulkStockRows((prev) => {
            const current = prev[productId] || { stores: [] };
            const nextStores = (current.stores || []).filter((_, index) => index !== lineIndex);
            return {
                ...prev,
                [productId]: {
                    ...current,
                    stores: nextStores.length ? nextStores : [{ storeId: '', quantity: '1' }],
                },
            };
        });
    };

    const updateBulkStockPluField = (productId, patch) => {
        setBulkStockRows((prev) => {
            const current = prev[productId] || { stores: [] };
            return {
                ...prev,
                [productId]: { ...current, ...patch },
            };
        });
    };

    const submitBulkStockMovements = async () => {
        const movements = [];

        selectedBulkStockProducts.forEach((product) => {
            const productId = normalizeId(product?._id || product?.id);
            const row = bulkStockRows[productId];

            (row?.stores || []).forEach((storeLine) => {
                const quantityValue = Number(storeLine?.quantity);
                const rawStoreId = getStoreRawIdFromOptionValue(storeLine?.storeId);

                if (!rawStoreId || !Number.isFinite(quantityValue) || quantityValue <= 0) {
                    return;
                }

                movements.push({
                    store_id: rawStoreId,
                    product_id: productId,
                    stock_state: 'available',
                    quantity: quantityValue,
                    manual: { description: `Ajout de stock bulk - ${product?.name || productId}` },
                });
            });
        });

        if (!movements.length) {
            toast.warning('Veuillez renseigner au moins un magasin et une quantité valide.');
            return;
        }

        setIsSubmittingBulkStock(true);
        try {
            await Promise.all(movements.map((payload) => axios.post(`${API_URL}/inventory/movements/in`, payload, header())));
            toast.success(`${movements.length} mouvement(s) de stock ajouté(s).`);
            fetchProducts(currentPage, { forceFullRefresh: true });
        } catch (error) {
            const message = error?.response?.data?.message || error.message;
            toast.error(`Impossible d'ajouter le stock en bulk : ${message}`);
        } finally {
            setIsSubmittingBulkStock(false);
        }
    };

    const buildPluBarcodeValue = (plu, price) => {
        const integerPart = Math.floor(price);
        const decimalPart = Math.round((price - integerPart) * 100);
        return `${plu}${String(integerPart).padStart(3, '0')}${String(decimalPart).padStart(2, '0')}0`;
    };

    const printBulkProductPlu = (product, useCustomPrice = false) => {
        const productId = normalizeId(product?._id || product?.id);
        const row = bulkStockRows[productId] || {};
        const selectedPluCategory = bulkPluCategories.find((category) => String(category?.category || '') === String(row.pluCategory || ''));

        if (!selectedPluCategory?.plu) {
            toast.warning('Veuillez sélectionner un PLU pour ce produit.');
            return;
        }

        const basePrice = resolveProductStockPrice(product);
        const customPrice = parsePriceValue(row.customPluPrice);
        const price = useCustomPrice ? customPrice : basePrice;

        if (!Number.isFinite(price) || price <= 0) {
            toast.warning('Veuillez saisir un prix PLU valide.');
            return;
        }

        if (price >= 1000) {
            toast.error('PLU Carrefour ne fonctionne que pour les produits avec un prix < 1000 DH.');
            return;
        }

        const escapeHtml = (value = '') =>
            String(value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');

        const barcodeValue = buildPluBarcodeValue(selectedPluCategory.plu, price);
        const productName = product?.name || 'Produit';
        const printWindow = window.open('', '_blank');

        if (!printWindow) {
            toast.error("Impossible d'ouvrir la fenêtre d'impression.");
            return;
        }

        printWindow.document.write(`
            <html>
                <head>
                    <title>PLU - ${escapeHtml(productName)}</title>
                    <script src="https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js"></script>
                    <style>
                        @page { size: 50mm 25mm; margin: 0; }
                        html, body { margin: 0; padding: 0; width: 50mm; height: 25mm; }
                        body { font-family: Arial, sans-serif; background: #fff; }
                        .plu-container {
                            width: 50mm;
                            height: 25mm;
                            display: flex;
                            align-items: center;
                            border: 1px solid #000;
                            box-sizing: border-box;
                        }
                        .qr-section {
                            width: 24mm;
                            height: 24mm;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                        }
                        #qrcode img { width: 21mm !important; height: 21mm !important; display: block; }
                        .info-section {
                            flex: 1;
                            padding: 1mm;
                            text-align: center;
                            overflow: hidden;
                        }
                        .price-label { font-size: 14pt; font-weight: 700; line-height: 1.1; }
                        .product-label { font-size: 8pt; font-weight: 600; line-height: 1.1; margin-top: 1mm; }
                        @media print { .no-print { display: none; } }
                    </style>
                </head>
                <body>
                    <div class="plu-container">
                        <div class="qr-section"><div id="qrcode"></div></div>
                        <div class="info-section">
                            <div class="price-label">${escapeHtml(price.toFixed(2))} DHS</div>
                            <div class="product-label">${escapeHtml(productName)}</div>
                        </div>
                    </div>
                    <div class="no-print" style="margin-top:16px;text-align:center;">
                        <button onclick="window.print(); window.close();">Imprimer</button>
                        <button onclick="window.close();">Fermer</button>
                    </div>
                    <script>
                        window.onload = function() {
                            new QRCode(document.getElementById("qrcode"), {
                                text: "${barcodeValue}",
                                width: 80,
                                height: 80,
                                colorDark: "#000000",
                                colorLight: "#ffffff",
                                correctLevel: QRCode.CorrectLevel.H
                            });
                        };
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };
    const handleChangeInsta = (htmlContent) => {
        // Save full HTML for the editor
        setCurrentProduct((prev) => ({
            ...prev,
            description: stripHtml(htmlContent), // this avoids the infinite loop
        }));
    };

    // When you need plain text (e.g. on submit), use:
    const stripHtml = (html) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };
    const setBarcodeToAll = async () => {
        if (!selectedProducts.length) {
            toast.warning('Veuillez sélectionner des produits');
            return;
        }

        if (!products) return;

        if (window.confirm('Êtes-vous sûr de vouloir définir les codes-barres ?')) {
            const toUpdate = products
                .filter(p => selectedProducts.includes(p._id) && (!p.barcode || p.barcode === 0))
                .map(p => ({ id: p._id, barcode: generateBarcode().barcodeValue }));

            try {
                await Promise.all(
                    toUpdate.map(item =>
                        axios.put(`${API_URL}/products/${item.id}`, { barcode: item.barcode }, header())
                    )
                );
                toast.success('Codes-barres mis à jour');
                fetchProducts(currentPage);
            } catch (error) {
                toast.error("Erreur lors de la mise à jour des codes-barres");
            }
        }
    }

    const isStockMovementFormValid = Boolean(
        stockModalProduct?._id &&
        stockMovementForm.storeId &&
        Number(stockMovementForm.quantity) > 0
    );

    const platformSummaryCards = [
        {
            label: 'Produits Glovo',
            filter: 'glovo',
            value: platformCounts.glovo,
            color: 'info',
            icon: 'fa-solid fa-motorcycle',
        },
        {
            label: 'Produits Kooul',
            filter: 'kooul',
            value: platformCounts.kooul,
            color: 'primary',
            icon: 'fa-solid fa-boxes-stacked',
        },
        {
            label: 'Produits WooCommerce',
            filter: 'woocommerce',
            value: platformCounts.woocommerce,
            color: 'success',
            icon: 'fa-brands fa-wordpress-simple',
        },
        {
            label: 'Produits Marjane actifs',
            filter: 'marjaneActive',
            value: platformCounts.marjaneActive,
            color: 'secondary',
            icon: 'fa-solid fa-store',
        },
    ];
    const showBackgroundProductsLoading = isFullProductsLoading;

    return (
        <Container
            maxWidth={false}
            disableGutters
            sx={{ py: 4, px: 3, width: '100%', maxWidth: '100%', position: 'relative' }}
        >
            <ToastContainer />
            {showBackgroundProductsLoading && (
                <Paper
                    elevation={2}
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        mb: 2,
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        borderRadius: 2,
                        backgroundColor: (theme) => theme.palette.warning.light,
                        color: (theme) => theme.palette.warning.contrastText,
                    }}
                >
                    <i className="fa-solid fa-spinner fa-spin" />
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Chargement complet des produits en arrière-plan…
                        </Typography>
                        <Typography variant="body2">
                            Le formulaire d&apos;ajout reste disponible pendant le chargement du stream produits.
                        </Typography>
                    </Box>
                </Paper>
            )}
            <Box>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    flexWrap="wrap"
                    sx={{ mb: 3 }}
                >
                    {platformSummaryCards.map((card) => (
                        <Paper
                            key={card.label}
                            component="button"
                            type="button"
                            aria-label={`Filtrer : ${card.label}`}
                            aria-pressed={summaryPlatformFilter === card.filter}
                            onClick={() => {
                                setSummaryPlatformFilter((current) => current === card.filter ? null : card.filter);
                                setCurrentPage(1);
                            }}
                            elevation={1}
                            sx={{
                                flex: 1,
                                minWidth: 240,
                                p: 2.5,
                                cursor: 'pointer',
                                textAlign: 'left',
                                font: 'inherit',
                                border: '2px solid',
                                borderColor: summaryPlatformFilter === card.filter ? `${card.color}.main` : 'transparent',
                                '&:focus-visible': { outline: '2px solid', outlineColor: `${card.color}.main`, outlineOffset: 2 },
                                borderRadius: 3,
                                backgroundColor: (theme) => theme.palette.background.paper,
                                boxShadow: '0px 8px 24px rgba(0,0,0,0.06)',
                            }}
                        >
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {card.label}
                                    </Typography>
                                    <Typography variant="h5" color={`${card.color}.main`} sx={{ fontWeight: 700 }}>
                                        {card.value}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        backgroundColor: (theme) => theme.palette[card.color]?.light || theme.palette.grey[100],
                                        color: (theme) => theme.palette[card.color]?.main || theme.palette.text.primary,
                                    }}
                                >
                                    <i className={card.icon} />
                                </Box>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            <Accordion
                expanded={isMarjaneActionsOpen}
                onChange={(_, expanded) => setIsMarjaneActionsOpen(expanded)}
                sx={{
                    mb: 3,
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0px 8px 24px rgba(0,0,0,0.06)',
                    backgroundColor: (theme) => theme.palette.background.paper,
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3 }}>
                    <Typography variant="h5" color="primary">
                        Actions
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 3, pb: 3 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap">
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            type="button"
                            onClick={setBarcodeToAll}
                            startIcon={<i className="fa-solid fa-barcode" />}
                            sx={{ borderRadius: 3, textTransform: 'none', boxShadow: 'none' }}
                        >
                            Set Barcode to All
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            type="button"
                            onClick={handleexportAimer}
                            startIcon={<i className="fa-solid fa-barcode" />}
                            sx={{ borderRadius: 3, textTransform: 'none', boxShadow: 'none' }}
                        >
                            Export Aimer Products
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            type="button"
                            onClick={handleOpenBulkDisableModal}
                            startIcon={<i className="fa-solid fa-ban" />}
                            sx={{ borderRadius: 3, textTransform: 'none', boxShadow: 'none' }}
                        >
                            Désactiver Glovo par Magasin

                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            type="button"
                            onClick={handleOpenGlovoToggleBulkModal}
                            data-bs-toggle="modal"
                            data-bs-target="#glovoToggleProductModal"
                            startIcon={<i className="fa-solid fa-toggle-on" />}
                            sx={{ borderRadius: 3, textTransform: 'none', boxShadow: 'none' }}
                            disabled={!selectedProducts || selectedProducts.length === 0}
                        >
                            Gérer disponibilité Glovo
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            type="button"
                            onClick={openBulkStockModal}
                            startIcon={<i className="fa-solid fa-boxes-stacked" />}
                            sx={{ borderRadius: 3, textTransform: 'none', boxShadow: 'none' }}
                            disabled={!selectedProducts || selectedProducts.length === 0}
                        >
                            Stock bulk
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            type="button"
                            onClick={openStoreExpressBulkDialog}
                            startIcon={<i className="fa-solid fa-store" />}
                            sx={{ borderRadius: 3, textTransform: 'none', boxShadow: 'none' }}
                            disabled={!selectedProducts || selectedProducts.length === 0}
                        >
                            Gérer Store Express
                        </Button>
                    </Stack>
                </AccordionDetails>
            </Accordion>


            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Typography variant="h5" color="text.primary">
                    Product Management
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                    <Button
                        variant="outlined"
                        color="secondary"
                        type="button"
                        startIcon={<i className="fa-solid fa-filter" />}
                        onClick={() => setFiltersModalOpen(true)}
                        sx={{ borderRadius: 3, textTransform: 'none' }}
                    >
                        Filtres
                    </Button>
                    <Button
                        variant="outlined"
                        type="button"
                        startIcon={<i className="fa-solid fa-rotate-right" />}
                        onClick={handleReloadProducts}
                        sx={{ borderRadius: 3, textTransform: 'none' }}
                    >
                        Reload
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        type="button"
                        startIcon={<i className="fa-solid fa-boxes-stacked" />}
                        onClick={openBulkStockModal}
                        disabled={!selectedProducts || selectedProducts.length === 0}
                        sx={{ borderRadius: 3, textTransform: 'none' }}
                    >
                        Stock bulk
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        type="button"
                        startIcon={<i className="fa-solid fa-arrows-rotate" />}
                        onClick={() => setWooSyncModalOpen(true)}
                        disabled={wooSyncRunning}
                        sx={{ borderRadius: 3, textTransform: 'none' }}
                    >
                        Sync WooCommerce
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        type="button"
                        data-bs-toggle="modal"
                        data-bs-target="#crudModal"
                        onClick={() => {
                            resetPictureField();
                            resetGalleryField();
                            setEditingProduct(null);
                            setCloningSourceProduct(null);
                            setCurrentProduct(null);
                            const nextBarcode = generateBarcode()?.barcodeValue;
                            const resetData = {
                                ...defaultData,
                                name: '',
                                description: '',
                                long_description: '',
                                regular_price: '',
                                discounted_price: '',
                                category: null,
                                url_product: '',
                                gallery_images: [],
                                barcode: nextBarcode,
                                details: [],
                                type: "borne",
                                availability: true,
                                isComposant: false,
                                stores: [],
                            };
                            setDefaultData(resetData);
                            setFormData(resetData);
                            setShowLongDescription(false);
                            setComponents([{ product: '', quantity: '1' }]);
                        }}
                        startIcon={<i className="fa-solid fa-plus" />}
                        sx={{ borderRadius: 3, textTransform: 'none' }}
                    >
                        Ajouter un Produit
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setPromotionsModalOpen(true)}
                        startIcon={<i className="fa-solid fa-tags" />}
                        sx={{ borderRadius: 3, textTransform: 'none' }}
                    >
                        Voir les Promotions
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        type="button"
                        onClick={handleExportCatalog}
                        disabled={selectedProducts.length === 0}
                        startIcon={<i className="fa-solid fa-file-export" />}
                        sx={{ borderRadius: 3, textTransform: 'none', boxShadow: 'none' }}
                    >
                        Export Catalogue
                    </Button>
                </Stack>
            </Stack>

            {(pagedProducts?.length > 0 || products.length > 0 || isPagedProductsLoading || isFullProductsLoading) && (
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        p: 2,
                        backgroundColor: (theme) => theme.palette.background.paper,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    {isFullProductsLoading && (products.length > 0 || pagedProducts?.length > 0) && (
                        <LinearProgress sx={{ mb: 2, borderRadius: 999 }} />
                    )}
                    <ProductTable
                        products={filterSummaryProducts(products.length ? products : (pagedProducts ?? []))}
                        fullProducts={filterSummaryProducts(products ?? [])}
                        productCategories={productCategories}
                        stores={stores}
                        updateGlobalAvailability={updateGlobalAvailability}
                        updateAvailability={updateAvailability}
                        handleEditClick={handleEditClick}
                        handleCloneProduct={handleCloneProduct}
                        handleDelete={handleDelete}
                        setCurrentProduct={setCurrentProduct}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        serverPagination={products.length === 0}
                        totalPages={products.length === 0 ? productPagination.totalPages : undefined}
                        isLoading={products.length === 0 && (pagedProducts?.length ?? 0) === 0 ? (isPagedProductsLoading || isFullProductsLoading) : false}
                        siteHandleModal={siteHandleModal}
                        handleWooCategoryEdit={handleWooCategoryEdit}
                        selectedProducts={selectedProducts}
                        setSelectedProducts={setSelectedProducts}
                        dynfilter={filterProducts}
                        selectedCategory={selectedCategory}
                        handleComponent={loadComponents}
                        plateformFilters={plateformFilters}
                        componentFilter={componentFilter}
                        onToggleIsComponent={handleToggleIsComponent}
                        onAddStock={openStockMovementModal}
                        onPrintBarcode={printProductBarcode}
                        onAddToKooul={handleOpenKooulDialog}
                        onAddToAimer={handleOpenAddToAimer}
                        onManageAimerStatus={handleOpenAimerStatusDialog}
                        onToggleGlovoAvailability={handleOpenGlovoToggleModal}
                        onManageStoreExpress={openStoreExpressDialog}
                        onOpenPublierPlateformes={handleOpenPublierPlateformes}
                        kooulPlateforms={kooulPlateforms}
                        aimerPlateforms={aimerPlateforms}
                        glovoPlateforms={plateforms}
                        onLoadAllProducts={fetchAllProductsOnce}
                        deliveryZoneOptions={deliveryZoneOptions}
                        onUpdateWebsiteDelivery={handleWebsiteDeliveryUpdate}
                        onUpdateVaseInclusion={handleVaseInclusionUpdate}
                        productAddons={productAddons}
                    />
                </Paper>
            )}





            <Dialog
                open={bulkStockModalOpen}
                onClose={closeBulkStockModal}
                fullWidth
                maxWidth="xl"
                sx={{ zIndex: 2300 }}
            >
                <DialogTitle>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                        <Box>
                            Stock bulk
                            <Typography variant="body2" color="text.secondary">
                                {selectedBulkStockProducts.length} produit(s) sélectionné(s)
                            </Typography>
                        </Box>
                        <Chip
                            color="primary"
                            variant="outlined"
                            icon={<i className="fa-solid fa-barcode" />}
                            label={`${bulkPluCategories.length} PLU disponibles`}
                        />
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {!selectedBulkStockProducts.length ? (
                        <Typography color="text.secondary">
                            Sélectionnez des produits dans la table pour ajouter du stock en bulk.
                        </Typography>
                    ) : (
                        <Stack spacing={2}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    backgroundColor: (theme) => theme.palette.success.light,
                                    borderColor: (theme) => theme.palette.success.main,
                                }}
                            >
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={1.5}
                                    alignItems={{ xs: 'stretch', md: 'center' }}
                                    justifyContent="space-between"
                                >
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            Même magasin pour tous
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Applique le magasin choisi sur la première ligne de chaque produit.
                                        </Typography>
                                    </Box>
                                    <Box sx={{ minWidth: { xs: '100%', md: 360 } }}>
                                        <Select
                                            options={stores}
                                            value={stores.find((store) => String(store.value) === String(bulkStockGlobalStoreId || '')) || null}
                                            onChange={(option) => applyBulkStockGlobalStore(option?.value || '')}
                                            placeholder="Choisir un magasin pour tous"
                                            isDisabled={isSubmittingBulkStock}
                                            isClearable
                                            classNamePrefix="select"
                                            {...bootstrapModalSelectProps}
                                        />
                                    </Box>
                                </Stack>
                            </Paper>

                            <Box sx={{ overflowX: 'auto' }}>
                                <table className="table table-bordered align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ minWidth: 260 }}>Produit</th>
                                            <th style={{ minWidth: 460 }}>Stock à ajouter par magasin</th>
                                            <th style={{ minWidth: 360 }}>PLU</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedBulkStockProducts.map((product) => {
                                            const productId = normalizeId(product?._id || product?.id);
                                            const row = bulkStockRows[productId] || { stores: [] };
                                            const productPrice = resolveProductStockPrice(product);
                                            const selectedPluCategory = bulkPluCategories.find((category) => String(category?.category || '') === String(row.pluCategory || ''));
                                            const productImageUrl = resolveImageUrl(
                                                product?.url_product ||
                                                product?.image ||
                                                product?.image_url ||
                                                product?.photo ||
                                                product?.picture ||
                                                (Array.isArray(product?.gallery_images) ? product.gallery_images[0] : '')
                                            );

                                            return (
                                                <tr key={productId}>
                                                    <td>
                                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                                            <Box
                                                                sx={{
                                                                    width: 58,
                                                                    height: 58,
                                                                    borderRadius: 2,
                                                                    overflow: 'hidden',
                                                                    flexShrink: 0,
                                                                    backgroundColor: '#f6f7f9',
                                                                    border: '1px solid #e5e7eb',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                {productImageUrl ? (
                                                                    <Box
                                                                        component="img"
                                                                        src={productImageUrl}
                                                                        alt={product?.name || 'Produit'}
                                                                        sx={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            objectFit: 'cover',
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <i className="fa-solid fa-image text-muted" />
                                                                )}
                                                            </Box>
                                                            <Box>
                                                                <div className="fw-semibold">{product?.name || 'Produit sans nom'}</div>
                                                                <div className="text-muted small">Code-barres : {product?.barcode || 'Non défini'}</div>
                                                                <div className="text-muted small">Prix : {formatCurrency(productPrice) || 'Non défini'}</div>
                                                            </Box>
                                                        </Stack>
                                                    </td>
                                                    <td>
                                                        <Stack spacing={1}>
                                                            {(row.stores || []).map((storeLine, lineIndex) => (
                                                                <Stack
                                                                    key={`${productId}-${lineIndex}`}
                                                                    direction={{ xs: 'column', md: 'row' }}
                                                                    spacing={1}
                                                                    alignItems={{ xs: 'stretch', md: 'center' }}
                                                                >
                                                                    <Box sx={{ minWidth: 240, flex: 1 }}>
                                                                        <Select
                                                                            options={stores}
                                                                            value={stores.find((store) => String(store.value) === String(storeLine.storeId || '')) || null}
                                                                            onChange={(option) => updateBulkStockStoreLine(productId, lineIndex, { storeId: option?.value || '' })}
                                                                            placeholder="Magasin"
                                                                            isDisabled={isSubmittingBulkStock}
                                                                            classNamePrefix="select"
                                                                            {...bootstrapModalSelectProps}
                                                                        />
                                                                    </Box>
                                                                    <TextField
                                                                        type="number"
                                                                        label="Quantité"
                                                                        size="small"
                                                                        value={storeLine.quantity || ''}
                                                                        onChange={(event) => updateBulkStockStoreLine(productId, lineIndex, { quantity: event.target.value })}
                                                                        inputProps={{ min: 1 }}
                                                                        disabled={isSubmittingBulkStock}
                                                                        sx={{ width: { xs: '100%', md: 120 } }}
                                                                    />
                                                                    <Button
                                                                        type="button"
                                                                        color="error"
                                                                        variant="outlined"
                                                                        size="small"
                                                                        onClick={() => removeBulkStockStoreLine(productId, lineIndex)}
                                                                        disabled={isSubmittingBulkStock}
                                                                        sx={{ minWidth: 42 }}
                                                                    >
                                                                        <i className="fa-solid fa-trash" />
                                                                    </Button>
                                                                </Stack>
                                                            ))}
                                                            <Button
                                                                type="button"
                                                                variant="text"
                                                                size="small"
                                                                onClick={() => addBulkStockStoreLine(productId)}
                                                                disabled={isSubmittingBulkStock}
                                                                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                                                                startIcon={<i className="fa-solid fa-plus" />}
                                                            >
                                                                Ajouter un magasin
                                                            </Button>
                                                        </Stack>
                                                    </td>
                                                    <td>
                                                        <Stack spacing={1}>
                                                            <Select
                                                                options={bulkPluCategories.map((category) => ({
                                                                    value: String(category?.category || ''),
                                                                    label: `${category?.category || 'Catégorie'} - PLU ${category?.plu || ''}`,
                                                                }))}
                                                                value={row.pluCategory ? {
                                                                    value: row.pluCategory,
                                                                    label: `${selectedPluCategory?.category || row.pluCategory} - PLU ${selectedPluCategory?.plu || ''}`,
                                                                } : null}
                                                                onChange={(option) => updateBulkStockPluField(productId, { pluCategory: option?.value || '' })}
                                                                placeholder="Choisir un PLU"
                                                                isDisabled={!bulkPluCategories.length}
                                                                classNamePrefix="select"
                                                                {...bootstrapModalSelectProps}
                                                            />
                                                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                                                <Button
                                                                    type="button"
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => printBulkProductPlu(product, false)}
                                                                    startIcon={<i className="fa-solid fa-print" />}
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    Imprimer
                                                                </Button>
                                                                <TextField
                                                                    type="number"
                                                                    label="Prix modifié"
                                                                    size="small"
                                                                    value={row.customPluPrice || ''}
                                                                    onChange={(event) => updateBulkStockPluField(productId, { customPluPrice: event.target.value })}
                                                                    inputProps={{ min: 0, step: '0.01' }}
                                                                    sx={{ width: { xs: '100%', md: 130 } }}
                                                                />
                                                                <Button
                                                                    type="button"
                                                                    variant="contained"
                                                                    color="warning"
                                                                    size="small"
                                                                    onClick={() => printBulkProductPlu(product, true)}
                                                                    startIcon={<i className="fa-solid fa-pen-to-square" />}
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    Imprimer avec prix
                                                                </Button>
                                                            </Stack>
                                                        </Stack>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeBulkStockModal} disabled={isSubmittingBulkStock} sx={{ textTransform: 'none' }}>
                        Fermer
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={submitBulkStockMovements}
                        disabled={isSubmittingBulkStock || !selectedBulkStockProducts.length}
                        startIcon={isSubmittingBulkStock ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-boxes-stacked" />}
                        sx={{ textTransform: 'none' }}
                    >
                        Ajouter le stock
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={storeExpressDialogOpen}
                onClose={closeStoreExpressDialog}
                fullWidth
                maxWidth="md"
                sx={{ zIndex: 2300 }}
            >
                <DialogTitle>Disponibilité Store Express</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                                Produit(s) concernés
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                {storeExpressProducts.map((product) => (
                                    <Chip
                                        key={product._id}
                                        label={product.name || product._id}
                                        size="small"
                                        variant="outlined"
                                    />
                                ))}
                            </Stack>
                        </Box>

                        <Divider />

                        <Stack spacing={1.5}>
                            {stores.map((store) => {
                                const storeCode = getStoreExpressStoreCode(store);
                                const enabledCount = storeExpressProducts.filter((product) =>
                                    isProductAvailableForStoreExpress(product, store)
                                ).length;
                                const featuredCount = storeExpressProducts.filter((product) =>
                                    isProductFeaturedForStoreExpress(product, store)
                                ).length;
                                const totalCount = storeExpressProducts.length;
                                const isSingleProduct = totalCount === 1;
                                const isChecked = isSingleProduct && enabledCount === 1;
                                const isFeatured = isSingleProduct && featuredCount === 1;
                                const activateKey = `${storeCode}:availability:on`;
                                const deactivateKey = `${storeCode}:availability:off`;
                                const featuredOnKey = `${storeCode}:featured:on`;
                                const featuredOffKey = `${storeCode}:featured:off`;

                                return (
                                    <Paper key={storeCode || store.label} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                                            justifyContent="space-between"
                                            spacing={2}
                                        >
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    {store.label || storeCode}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Store code: {storeCode || '-'}
                                                </Typography>
                                            </Box>

                                            {isSingleProduct ? (
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={isChecked}
                                                                disabled={!storeCode || Boolean(storeExpressSavingKey)}
                                                                onChange={(event) =>
                                                                    setStoreExpressAvailability(storeExpressProducts, store, event.target.checked)
                                                                }
                                                            />
                                                        }
                                                        label={isChecked ? 'Activé' : 'Désactivé'}
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={isFeatured}
                                                                disabled={!storeCode || Boolean(storeExpressSavingKey)}
                                                                onChange={(event) =>
                                                                    setStoreExpressFeatured(storeExpressProducts, store, event.target.checked)
                                                                }
                                                            />
                                                        }
                                                        label={isFeatured ? 'Mise en Avant' : 'Pas Mise en Avant'}
                                                    />
                                                </Stack>
                                            ) : (
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                    <Chip
                                                        label={`${enabledCount}/${totalCount} actifs`}
                                                        color={enabledCount === totalCount ? 'success' : enabledCount > 0 ? 'warning' : 'default'}
                                                        size="small"
                                                    />
                                                    <Chip
                                                        label={`${featuredCount}/${totalCount} mise en avant`}
                                                        color={featuredCount === totalCount ? 'success' : featuredCount > 0 ? 'warning' : 'default'}
                                                        size="small"
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        size="small"
                                                        disabled={!storeCode || Boolean(storeExpressSavingKey)}
                                                        onClick={() => setStoreExpressAvailability(storeExpressProducts, store, true)}
                                                    >
                                                        {storeExpressSavingKey === activateKey ? '...' : 'Activer'}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        disabled={!storeCode || Boolean(storeExpressSavingKey)}
                                                        onClick={() => setStoreExpressAvailability(storeExpressProducts, store, false)}
                                                    >
                                                        {storeExpressSavingKey === deactivateKey ? '...' : 'Désactiver'}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        disabled={!storeCode || Boolean(storeExpressSavingKey)}
                                                        onClick={() => setStoreExpressFeatured(storeExpressProducts, store, true)}
                                                    >
                                                        {storeExpressSavingKey === featuredOnKey ? '...' : 'Mettre en Avant'}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        size="small"
                                                        disabled={!storeCode || Boolean(storeExpressSavingKey)}
                                                        onClick={() => setStoreExpressFeatured(storeExpressProducts, store, false)}
                                                    >
                                                        {storeExpressSavingKey === featuredOffKey ? '...' : 'Retirer Mise en Avant'}
                                                    </Button>
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeStoreExpressDialog} disabled={Boolean(storeExpressSavingKey)}>
                        Fermer
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={filtersModalOpen}
                onClose={() => setFiltersModalOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Filtres</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <Typography variant="h6" color="text.primary">
                                Filtres Marjane
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap">
                                {MARJANE_FILTERS.map((filter) => (
                                    <Button
                                        key={filter.value}
                                        type="button"
                                        size="small"
                                        variant={filterProducts.includes(filter.value) ? 'contained' : 'outlined'}
                                        color={FILTER_BUTTON_COLORS[filter.value]}
                                        onClick={() => handlefilter(filter.value)}
                                        sx={{ borderRadius: 3, textTransform: 'none', boxShadow: 'none' }}
                                    >
                                        {filter.label}
                                    </Button>
                                ))}
                            </Stack>
                        </Stack>

                        <Divider />

                        <Stack spacing={1}>
                            <Typography variant="h6" color="text.primary">
                                Autres filtres
                            </Typography>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <Box sx={{ minWidth: 280 }}>
                                    <Select
                                        isMulti
                                        placeholder="Filtrer par plateforme"
                                        options={plateformFilterOptions}
                                        value={plateformFilterOptions.filter(option => plateformFilters.includes(option.value))}
                                        onChange={handlePlateformFilterChange}
                                        formatOptionLabel={(option) => (
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>{option.label}</Typography>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                                    {(option.storeLabels?.length ? option.storeLabels : ['Aucun magasin lié']).map((storeLabel, index) => (
                                                        <Chip
                                                            key={`${option.value}-${storeLabel}-${index}`}
                                                            label={storeLabel}
                                                            size="small"
                                                            color={option.storeLabels?.length ? 'primary' : 'default'}
                                                            variant={option.storeLabels?.length ? 'outlined' : 'filled'}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 250 }}>
                                    <Select
                                        isMulti
                                        placeholder="Filtrer par composants"
                                        options={componentFilterOptions}
                                        value={componentFilterOptions.filter(option => componentFilter.includes(option.value))}
                                        onChange={handleComponentFilterChange}
                                    />
                                </Box>
                            </Stack>
                        </Stack>

                        {pegasusCategories && (
                            <Box>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    Catégorie
                                </Typography>
                                <Select
                                    placeholder="Sélectionner la catégorie du produit"
                                    options={pegasusCategories.map((category) => ({ value: category._id, label: category.name }))}
                                    value={selectedCategory}
                                    onChange={handleFilterByCategory}
                                />
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFiltersModalOpen(false)} color="inherit">
                        Fermer
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog
                open={aimerCategoryDialogOpen}
                onClose={handleCloseAddToAimer}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Sélection des catégories Aimer</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Choisissez une ou plusieurs catégories Aimer à envoyer avec « {aimerCategoryProduct?.name || 'ce produit'} ».
                    </Typography>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={aimerCategories}
                        value={selectedAimerCategories}
                        onChange={(_, newValue) => setSelectedAimerCategories(newValue || [])}
                        getOptionLabel={(option) => option?.name || option?.remote_plateform_id || 'Catégorie Aimer'}
                        isOptionEqualToValue={(option, value) => normalizeId(option?._id) === normalizeId(value?._id)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Catégories Aimer"
                                placeholder="Rechercher et sélectionner"
                            />
                        )}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={normalizeId(option?._id) || index}
                                label={`${option?.name || 'Catégorie'}${option?.remote_plateform_id ? ` • ${option.remote_plateform_id}` : ''}`}
                            />
                        ))}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddToAimer} color="inherit" disabled={isAimerSubmitting}>
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitAddToAimer}
                        disabled={isAimerSubmitting}
                    >
                        Envoyer vers Aimer
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog
                open={aimerStatusDialogOpen}
                onClose={handleCloseAimerStatusDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Statut du produit Aimer</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1">
                        Choisissez le statut à appliquer pour {aimerTargetProduct?.name || 'ce produit'} sur Aimer.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={() => handleAimerStatusChange(0)}
                        disabled={isAimerSubmitting}
                    >
                        Désactiver
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => handleAimerStatusChange(1)}
                        disabled={isAimerSubmitting}
                    >
                        Activer
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog
                open={kooulDialogOpen}
                onClose={handleCloseKooulDialog}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Ajouter sur Kooul</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Sélectionnez la plateforme Kooul
                            </Typography>
                            <Autocomplete
                                options={availableKooulPlateformOptions}
                                value={selectedKooulPlateform}
                                onChange={handleKooulPlateformChange}
                                getOptionLabel={(option) => option?.optionLabel || option?.name || ''}
                                isOptionEqualToValue={(option, value) => option?.optionValue === value?.optionValue}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.optionValue}>
                                        <Stack spacing={0.5} sx={{ width: '100%' }}>
                                            <Typography variant="body1" fontWeight={600}>{option.optionLabel}</Typography>
                                            {Array.isArray(option?.store_ids) && option.store_ids.length > 0 && (
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                    {option.store_ids.map((storeEntry, index) => {
                                                        const storeId = normalizeId(storeEntry?.store_id || storeEntry);
                                                        const label = stores.find((store) =>
                                                            store.value === storeId || store.rawId === storeId || store.storeCode === storeId
                                                        )?.label || storeId;
                                                        return <Chip key={`${option.optionValue}-${storeId}-${index}`} label={label} size="small" />;
                                                    })}
                                                </Stack>
                                            )}
                                        </Stack>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Plateforme Kooul"
                                        placeholder="Choisissez la plateforme Kooul"
                                    />
                                )}
                            />
                            {selectedKooulPlateform && (
                                <Stack spacing={0.5}>
                                    <Typography variant="body2" color="text.secondary">
                                        Magasins associés
                                    </Typography>
                                    {kooulPlateformStoreLabels.length ? (
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                            {kooulPlateformStoreLabels.map((storeLabel) => (
                                                <Chip key={storeLabel} label={storeLabel} size="small" color="primary" variant="outlined" />
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Aucun magasin lié n'a été trouvé pour cette plateforme.
                                        </Typography>
                                    )}
                                </Stack>
                            )}
                        </Stack>

                        <Divider />

                        {!selectedKooulPlateform ? (
                            <Typography variant="body2" color="text.secondary">
                                Choisissez d'abord une plateforme Kooul pour afficher l'arborescence des catégories.
                            </Typography>
                        ) : kooulCategoryLevels.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Aucune catégorie Kooul synchronisée pour cette plateforme.
                            </Typography>
                        ) : (
                            <Stack spacing={2}>
                                {kooulCategoryLevels.map((level, index) => (
                                    <Autocomplete
                                        key={`kooul-category-level-${index}`}
                                        options={level.options}
                                        value={level.selected}
                                        onChange={(event, value) => handleKooulCategoryChange(index, value)}
                                        getOptionLabel={(option) => option?.name || 'Sans nom'}
                                        isOptionEqualToValue={(option, value) => normalizeId(option?._id) === normalizeId(value?._id)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={index === 0 ? 'Catégorie principale Kooul' : 'Sous-catégorie'}
                                                placeholder="Sélectionner une catégorie"
                                            />
                                        )}
                                    />
                                ))}
                                {selectedKooulCategory && (
                                    <Stack spacing={0.5}>
                                        <Typography variant="body2" color="text.secondary">
                                            Chemin sélectionné
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                            {selectedKooulCategoryPath.map((categoryId, idx) => {
                                                const category = kooulCategories.find((item) => normalizeId(item._id) === categoryId);
                                                if (!category) {
                                                    return null;
                                                }
                                                const isLast = idx === selectedKooulCategoryPath.length - 1;
                                                return (
                                                    <Chip
                                                        key={`${categoryId}-${idx}`}
                                                        label={category.name || categoryId}
                                                        color={isLast ? 'primary' : 'default'}
                                                        variant={isLast ? 'filled' : 'outlined'}
                                                        size="small"
                                                    />
                                                );
                                            })}
                                        </Stack>
                                        {selectedKooulCategoryHasChildren && (
                                            <Typography variant="caption" color="warning.main">
                                                Cette catégorie possède des sous-catégories, veuillez affiner votre sélection.
                                            </Typography>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseKooulDialog} color="inherit">
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleKooulSubmit}
                        disabled={!selectedKooulPlateform || !selectedKooulCategory || selectedKooulCategoryHasChildren || isKooulSubmitting}
                    >
                        {isKooulSubmitting ? 'Envoi en cours...' : 'Ajouter sur Kooul'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Modal modalId="exampleModal" title="Details du produit">
                {(currentProduct && stores && <ProductDetails product={currentProduct ?? defaultData} productCategories={productCategories} stores={stores} plateforms={plateforms} />)}
            </Modal>
            <Modal
                modalId="categoriesSite"
                title="Catégorie du Site"
                submitLabel={isWooCategoryUpdate ? "Mettre à jour" : "Ajouter au Site"}
                onSubmit={() => isWooCategoryUpdate ? updateWooCategories(selectedCategories) : addinWebsite(selectedCategories, fees)}
            >
                <div className="mb-3">
                    <label htmlFor="categories" className="form-label">Catégories</label>
                    <Select
                        id="categories"
                        isMulti
                        options={sitecategories}
                        value={selectedCategories}
                        onChange={handleSiteCategoryChange}
                        {...bootstrapModalSelectProps}
                    />
                </div>
                {currentProduct && !isWooCategoryUpdate && (<>
                    <div className="mb-3">
                        <label className="form-label">Commission ou Frais Supp.</label>
                        <input type="number" className="form-control" name="increment" min={0} value={fees} onChange={(e) => setFees(e.target.value)} />
                    </div>
                    <div className='text-end'>

                        <h3>{currentProduct.regular_price} + {fees ?? 0}</h3>
                        <h2 >Prix Sur Site : <span class="badge bg-warning badge-warning"> {currentProduct.regular_price + (fees ?? 0) * 1} DHS</span></h2>

                    </div>
                </>)}

            </Modal>
            <Modal
                modalId="stockMovementModal"
                title="Ajouter du stock"
                submitLabel="Ajouter"
                submitDisabled={isSubmittingStockMovement || !isStockMovementFormValid}
                onSubmit={submitStockMovement}
                onClose={closeStockMovementModal}
            >
                {stockModalProduct ? (
                    <>
                        <div className="mb-3">
                            <label className="form-label">Produit</label>
                            <div className="form-control-plaintext fw-semibold">
                                {stockModalProduct.name || 'Produit sans nom'}
                            </div>
                            {stockModalProduct.barcode && (
                                <div className="text-muted small">
                                    Code-barres : {stockModalProduct.barcode}
                                </div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Magasin</label>
                            <Select
                                options={stores}
                                value={stores.find((store) => store.value === stockMovementForm.storeId) || null}
                                onChange={handleStockMovementStoreChange}
                                placeholder="Sélectionner un magasin"
                                isDisabled={isSubmittingStockMovement}
                                classNamePrefix="select"
                                {...bootstrapModalSelectProps}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Quantité</label>
                            <input
                                type="number"
                                min="1"
                                className="form-control"
                                value={stockMovementForm.quantity}
                                onChange={handleStockMovementQuantityChange}
                                disabled={isSubmittingStockMovement}
                            />
                        </div>
                        <p className="text-muted small mb-0">
                            Ce mouvement créera une entrée de stock (mouvement IN).
                        </p>
                    </>
                ) : (
                    <p className="mb-0">Sélectionnez un produit pour ajouter du stock.</p>
                )}
            </Modal>
            <Modal
                title={editingProduct ? 'Modify Product' : cloningSourceProduct ? 'Cloner le produit' : 'Add Product'}
                modalId="crudModal"
                submitLabel={editingProduct ? 'Update Product' : cloningSourceProduct ? 'Valider le clone' : 'Add Product'}
                onSubmit={handleSubmit}
            >

                <form className="mb-4">
                    {cloningSourceProduct && (
                        <div className="alert alert-info">
                            Clone de <strong>{cloningSourceProduct.name}</strong>. Un nouveau code-barres a été généré automatiquement; vous pouvez ajuster les champs avant validation.
                        </div>
                    )}
                    <div className="mb-3">
                        <label className="form-label">Name</label>
                        <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} required />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Description</label>
                        <input type="text" className="form-control" name="description" value={formData.description} onChange={handleChange} />
                    </div>
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                checked={showLongDescription}
                                onChange={handleLongDescriptionToggle}
                            />
                        }
                        label="Ajouter une description longue"
                    />
                    {showLongDescription && (
                        <div className="mb-3">
                            <label className="form-label">Long Description</label>
                            <textarea
                                className="form-control"
                                name="long_description"
                                value={formData.long_description || ''}
                                onChange={handleChange}
                                rows={5}
                            />
                        </div>
                    )}
                    <div className="mb-3">
                        <label className="form-label">Code à Bar</label>
                        <input type="text" className="form-control" name="barcode" value={formData.barcode} onChange={handleChange} />
                    </div>
                    <div className='row'>


                        <div className='col-6'> <div className="mb-3">
                            <label className="form-label">Prix Régulier</label>
                            <input type="number" className="form-control" name="regular_price" value={formData.regular_price} onChange={handleChange} required />
                        </div></div>
                        <div className='col-6'><div className="mb-3">
                            <label className="form-label">Prix Remisé</label>
                            <input type="number" className="form-control" name="discounted_price" value={formData.discounted_price} onChange={handleChange} />
                        </div></div>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="picture" className="form-label">Picture</label>
                        <input type="file" className="form-control" id="picture" ref={pictureInputRef} onChange={handlePictureChange} />
                    </div>
                    {formImagePreview && (
                        <div className="mb-3">
                            <label className="form-label">Preview</label>
                            <div>
                                <img
                                    src={formImagePreview}
                                    alt="Product preview"
                                    width={140}
                                    className="rounded border"
                                    style={{ objectFit: 'cover' }}
                                />
                            </div>
                        </div>
                    )}
                    <div className="mb-3">
                        <label htmlFor="galleryPictures" className="form-label">Gallery Pictures</label>
                        <input
                            type="file"
                            className="form-control"
                            id="galleryPictures"
                            ref={galleryInputRef}
                            accept="image/*"
                            multiple
                            onChange={handleGalleryPicturesChange}
                        />
                    </div>
                    {(galleryPreviewUrls.length > 0 || formData?.gallery_images?.length > 0) && (
                        <div className="mb-3">
                            <label className="form-label">Galerie</label>
                            <div className="d-flex flex-wrap align-items-center">
                                {(galleryPreviewUrls.length > 0
                                    ? galleryPreviewUrls
                                    : (Array.isArray(formData?.gallery_images)
                                        ? formData.gallery_images.map((image) => resolveImageUrl(image))
                                        : [])
                                ).map((image, index) => (
                                    <img
                                        key={`${image}-${index}`}
                                        src={image}
                                        alt={`Galerie ${index + 1}`}
                                        width={80}
                                        className="me-2 mb-2 rounded border"
                                        loading="lazy"
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="mb-3">
                        <label className="form-label">Catégorie *</label>
                        <Select
                            placeholder="Selectionner la catégorie du produit"
                            options={pegasusCategories.map((category) => ({ value: category._id, label: category.name }))}
                            value={pegasusCategories
                                .map((category) => ({ value: category._id, label: category.name }))
                                .find((option) => option.value === formData.category) || null}
                            onChange={handleproductCategoryChange}
                            {...bootstrapModalSelectProps}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Store IDs</label>
                        <Select
                            isMulti
                            options={stores}
                            value={formData.stores.map(store => ({ value: store.store_id, label: (stores.find(s => s.value == store.store_id)?.label) }))}
                            onChange={handleStoreIdsChange}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            {...bootstrapModalSelectProps}
                        />
                    </div>
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                id="isComposant"
                                checked={formData.isComposant}
                                onChange={handleIsComposantChange}
                            />
                        }
                        label="Utiliser ce produit comme composant"
                        sx={{ mt: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Les produits marqués comme composants pourront être sélectionnés lors de la composition d&apos;autres produits.
                    </Typography>

                </form>
            </Modal>

            <Modal modalId="instapost" title="Description Instagram" onSubmit={postInstagram} submitLabel={(ispublishing) ? '' : 'Poster Sur Instagram'}>
                <div className="mb-3">
                    <label className="form-label h2"> <i class="fa-brands fa-instagram"></i> Caption Insta</label>
                    <textarea style={{ width: '100%' }} className='form-control'
                        value={currentProduct?.description || ""} rows={15}
                        onChange={(e) => handleChangeInsta(e.target.value)}
                    />
                </div>



            </Modal>
            <SquareImageCropModal
                open={isCropModalOpen}
                file={pendingCropFile}
                onCancel={handleCropCancel}
                onComplete={handleCropComplete}
            />

            <ComponentsOfProducts
                components={components}
                currentProduct={currentProduct}
                setComponents={setComponents}
                onUpdate={submitComponents}
                availableComponents={availableComponents}
            />
            <GlovoToggleProductModal
                products={glovoToggleModalProducts}
                onClose={handleCloseGlovoToggleModal}
                onSuccess={handleGlovoToggleSuccess}
            />
            <PublierPlateformesModal
                open={publierPlateformesOpen}
                onClose={handleClosePublierPlateformes}
                product={publierPlateformesProduct}
                aimerPlateforms={aimerPlateforms}
                aimerCategories={aimerCategories}
                onSubmitAimer={handlePublierAimerSubmit}
                kooulPlateforms={kooulPlateforms}
                onSubmitKooul={handlePublierKooulSubmit}
                onSubmitMarjane={handlePublierMarjaneSubmit}
                productCategories={productCategories}
                stores={stores}
                productAddons={productAddons}
                glovoPlatform={glovoPlatform}
                plateforms={plateforms}
                sitecategories={sitecategories}
                onSubmitSite={handlePublierSiteSubmit}
                onSubmitInstagram={handlePublierInstagramSubmit}
                onSubmitDolibarr={handlePublierDolibarrSubmit}
                onFetchDolibarrStock={handleFetchDolibarrStock}
                onUpdateDolibarrStock={handleUpdateDolibarrStock}
                onProductUpdated={handleProductUpdated}
                allProducts={products}
                selectedProductIds={selectedProducts}
                deliveryZoneOptions={deliveryZoneOptions}
            />

            {/* Pass all products that have at least one plateform of type 'glovo' */}
            <PromotionsModal
                open={promotionsModalOpen}
                onClose={() => setPromotionsModalOpen(false)}
                glovoPlatform={glovoPlatform}
                glovoPlateformIds={Array.from(glovoPlateformIds || [])}
                allProducts={products}
                allDiscounts={glovoDiscounts}
                productCategories={productCategories}
            />

            <Dialog
                open={wooSyncModalOpen}
                onClose={handleCloseWooSyncModal}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                                <i className="fa-solid fa-arrows-rotate"></i>
                                <span>Synchronisation WooCommerce</span>
                            </Stack>
                            <Chip
                                color={wooSyncPaused ? 'info' : (wooSyncRunning ? 'warning' : (wooSyncStopped ? 'default' : 'success'))}
                                label={wooSyncPaused ? 'En pause' : (wooSyncRunning ? 'En cours' : (wooSyncStopped ? 'Arrêté' : 'Terminé'))}
                                size="small"
                            />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Scan WooCommerce page par page avec score de confiance: nom, SKU/barcode, prix et image. Les matchs forts peuvent être liés rapidement; les cas douteux restent dans À vérifier.
                        </Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5}>
                        <Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                                <Typography variant="body2" color="text.secondary">
                                    Page {wooSyncProgress.page || 0} / {wooSyncProgress.totalPages || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Traitement: {wooSyncProgress.processed} produit(s)
                                </Typography>
                            </Stack>
                            <LinearProgress
                                variant={wooSyncProgress.total > 0 ? 'determinate' : 'indeterminate'}
                                value={wooSyncProgress.total > 0 ? Math.min(100, (wooSyncProgress.processed / wooSyncProgress.total) * 100) : 0}
                                sx={{ mt: 1, borderRadius: 999 }}
                            />
                        </Box>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap">
                            <Chip label={`Matchs forts: ${wooSyncProgress.matched}`} color="success" variant="outlined" />
                            <Chip label={`À vérifier: ${wooSyncProgress.review}`} color="info" variant="outlined" />
                            <Chip label={`Woo ID déjà liés: ${wooSyncProgress.updated}`} color="primary" variant="outlined" />
                            <Chip label={`Manquants: ${wooSyncProgress.missing}`} color="warning" variant="outlined" />
                            <Chip label={`Erreurs: ${wooSyncErrors.length}`} color="error" variant="outlined" />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                            {!wooSyncRunning && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<i className="fa-solid fa-play" />}
                                    onClick={handleWooSync}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Démarrer
                                </Button>
                            )}
                            {wooSyncRunning && !wooSyncPaused && (
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    startIcon={<i className="fa-solid fa-pause" />}
                                    onClick={handlePauseWooSync}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Pause
                                </Button>
                            )}
                            {wooSyncRunning && wooSyncPaused && (
                                <Button
                                    variant="contained"
                                    color="info"
                                    size="small"
                                    startIcon={<i className="fa-solid fa-play" />}
                                    onClick={handleResumeWooSync}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Reprendre
                                </Button>
                            )}
                            {wooSyncRunning && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<i className="fa-solid fa-stop" />}
                                    onClick={handleStopWooSync}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Arrêter
                                </Button>
                            )}
                        </Stack>

                        <TextField
                            value={wooSyncSearchTerm}
                            onChange={handleWooSyncSearchChange}
                            placeholder="Rechercher par nom..."
                            size="small"
                            fullWidth
                        />

                        <Tabs value={wooSyncTab} onChange={handleWooSyncTabChange}>
                            <Tab label={`Matchs forts (${filteredWooSyncMatched.length})`} />
                            <Tab label={`À vérifier (${filteredWooSyncReview.length})`} />
                            <Tab label={`Manquants (${filteredWooSyncMissing.length})`} />
                            <Tab label={`Erreurs (${filteredWooSyncErrors.length})`} />
                        </Tabs>

                        {wooSyncTab === 0 && (
                            <Stack spacing={1}>
                                {(wooSyncStrongPending.length > 0 || selectedWooSyncMatchedPending.length > 0) && (
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                                        {wooSyncStrongPending.length > 0 && (
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                onClick={handleLinkWooStrongMatchesBulk}
                                                disabled={wooBulkLinking}
                                                sx={{ textTransform: 'none', alignSelf: { xs: 'stretch', sm: 'center' } }}
                                            >
                                                {wooBulkLinking
                                                    ? 'Mise à jour...'
                                                    : `Lier tous les matchs forts (${wooSyncStrongPending.length})`}
                                            </Button>
                                        )}
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleLinkWooMatchBulk}
                                            disabled={wooBulkLinking || selectedWooSyncMatchedPending.length === 0}
                                            sx={{ textTransform: 'none', alignSelf: { xs: 'stretch', sm: 'center' } }}
                                        >
                                            {wooBulkLinking
                                                ? 'Mise à jour...'
                                                : `Définir Woo ID aux sélectionnés (${selectedWooSyncMatchedPending.length})`}
                                        </Button>
                                    </Stack>
                                )}
                                {filteredWooSyncMatched.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Aucun match pour le moment.
                                    </Typography>
                                ) : paginatedWooSyncMatched.map((item, index) => (
                                    <Paper key={`${item.wooId}-${item.localId || index}`} variant="outlined" sx={{ p: 1.5 }}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                                {item.localImage ? (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                            Local
                                                        </Typography>
                                                        <Box
                                                            component="img"
                                                            src={item.localImage}
                                                            alt={item.localName}
                                                            onClick={() => setWooSyncPreviewImage(item.localImage)}
                                                            sx={{
                                                                width: 64,
                                                                height: 64,
                                                                borderRadius: 1.5,
                                                                objectFit: 'cover',
                                                                cursor: 'pointer',
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                            }}
                                                        />
                                                    </Box>
                                                ) : null}
                                                {item.wooImage ? (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                            Woo
                                                        </Typography>
                                                        <Box
                                                            component="img"
                                                            src={item.wooImage}
                                                            alt={item.wooName}
                                                            onClick={() => setWooSyncPreviewImage(item.wooImage)}
                                                            sx={{
                                                                width: 64,
                                                                height: 64,
                                                                borderRadius: 1.5,
                                                                objectFit: 'cover',
                                                                cursor: 'pointer',
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                            }}
                                                        />
                                                    </Box>
                                                ) : null}
                                                <Typography variant="subtitle2">{item.wooName}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Local: {item.localName} | Woo ID: {item.wooId} | SKU: {item.sku || '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Prix Woo: {item.wooPrice || '-'} | Prix local: {item.localPrice || '-'}
                                                </Typography>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                    <Chip size="small" color={item.matchScore >= 85 ? 'success' : 'info'} label={`Score ${item.matchScore || 0}%`} />
                                                    {(item.matchReasons || []).slice(0, 3).map((reason) => (
                                                        <Chip key={reason} size="small" variant="outlined" label={reason} />
                                                    ))}
                                                </Stack>
                                                {Array.isArray(item.candidates) && item.candidates.length > 1 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.candidates.length - 1} autre(s) candidat(s) possible(s). Utilisez À vérifier si le score est faible.
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                                <Chip
                                                    size="small"
                                                    color={item.alreadyLinked ? 'success' : (!item.localWooId ? 'warning' : 'info')}
                                                    label={
                                                        item.alreadyLinked
                                                            ? 'Déjà lié'
                                                            : (!item.localWooId ? 'Woo ID manquant' : 'Match trouvé')
                                                    }
                                                />
                                                {!item.localWooId && (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => handleLinkWooMatchSingle(item)}
                                                        disabled={wooBulkLinking || wooLinkingIds.includes(normalizeId(item.localId))}
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        {wooLinkingIds.includes(normalizeId(item.localId)) ? 'Mise à jour...' : 'Définir Woo ID'}
                                                    </Button>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                ))}
                                {filteredWooSyncMatched.length > wooSyncPageSize && (
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button
                                            size="small"
                                            disabled={wooSyncPages.matched <= 1}
                                            onClick={() => setWooSyncPages((prev) => ({ ...prev, matched: prev.matched - 1 }))}
                                        >
                                            Précédent
                                        </Button>
                                        <Chip
                                            size="small"
                                            label={`${wooSyncPages.matched} / ${Math.max(1, Math.ceil(filteredWooSyncMatched.length / wooSyncPageSize))}`}
                                        />
                                        <Button
                                            size="small"
                                            disabled={wooSyncPages.matched >= Math.max(1, Math.ceil(filteredWooSyncMatched.length / wooSyncPageSize))}
                                            onClick={() => setWooSyncPages((prev) => ({ ...prev, matched: prev.matched + 1 }))}
                                        >
                                            Suivant
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                        )}

                        {wooSyncTab === 1 && (
                            <Stack spacing={1}>
                                {filteredWooSyncReview.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Aucun match douteux pour le moment.
                                    </Typography>
                                ) : paginatedWooSyncReview.map((item, index) => (
                                    <Paper key={`review-${item.wooId}-${item.localId || index}`} variant="outlined" sx={{ p: 1.5, borderColor: 'info.light', bgcolor: 'rgba(3, 169, 244, 0.04)' }}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flex: 1 }}>
                                                <Stack direction="row" spacing={1}>
                                                    {item.localImage ? (
                                                        <Box
                                                            component="img"
                                                            src={item.localImage}
                                                            alt={item.localName}
                                                            onClick={() => setWooSyncPreviewImage(item.localImage)}
                                                            sx={{ width: 70, height: 70, borderRadius: 1.5, objectFit: 'cover', cursor: 'pointer', border: '1px solid', borderColor: 'divider' }}
                                                        />
                                                    ) : null}
                                                    {item.wooImage ? (
                                                        <Box
                                                            component="img"
                                                            src={item.wooImage}
                                                            alt={item.wooName}
                                                            onClick={() => setWooSyncPreviewImage(item.wooImage)}
                                                            sx={{ width: 70, height: 70, borderRadius: 1.5, objectFit: 'cover', cursor: 'pointer', border: '1px solid', borderColor: 'divider' }}
                                                        />
                                                    ) : null}
                                                </Stack>
                                                <Box>
                                                    <Typography variant="subtitle2">{item.wooName}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Proposition locale: {item.localName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Woo ID: {item.wooId} | SKU: {item.sku || '-'} | Prix Woo: {item.wooPrice || '-'} | Prix local: {item.localPrice || '-'}
                                                    </Typography>
                                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75 }}>
                                                        <Chip size="small" color="info" label={`Score ${item.matchScore || 0}%`} />
                                                        {(item.matchReasons || []).slice(0, 4).map((reason) => (
                                                            <Chip key={reason} size="small" variant="outlined" label={reason} />
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                            <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                                                <Chip size="small" color="info" label="Validation manuelle requise" />
                                                {!item.localWooId && (
                                                    <Button
                                                        variant="contained"
                                                        color="info"
                                                        size="small"
                                                        onClick={() => handleLinkWooMatchSingle(item)}
                                                        disabled={wooBulkLinking || wooLinkingIds.includes(normalizeId(item.localId))}
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        {wooLinkingIds.includes(normalizeId(item.localId)) ? 'Mise à jour...' : 'Confirmer ce lien'}
                                                    </Button>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                ))}
                                {filteredWooSyncReview.length > wooSyncPageSize && (
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button
                                            size="small"
                                            disabled={wooSyncPages.review <= 1}
                                            onClick={() => setWooSyncPages((prev) => ({ ...prev, review: prev.review - 1 }))}
                                        >
                                            Précédent
                                        </Button>
                                        <Chip
                                            size="small"
                                            label={`${wooSyncPages.review} / ${Math.max(1, Math.ceil(filteredWooSyncReview.length / wooSyncPageSize))}`}
                                        />
                                        <Button
                                            size="small"
                                            disabled={wooSyncPages.review >= Math.max(1, Math.ceil(filteredWooSyncReview.length / wooSyncPageSize))}
                                            onClick={() => setWooSyncPages((prev) => ({ ...prev, review: prev.review + 1 }))}
                                        >
                                            Suivant
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                        )}

                        {wooSyncTab === 2 && (
                            <Stack spacing={1}>
                                {filteredWooSyncMissing.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Aucun produit manquant pour le moment.
                                    </Typography>
                                ) : paginatedWooSyncMissing.map((item, index) => (
                                    <Paper key={`${item.wooId}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                                            {item.image ? (
                                                <Box
                                                    component="img"
                                                    src={item.image}
                                                    alt={item.name}
                                                    onClick={() => setWooSyncPreviewImage(item.image)}
                                                    sx={{
                                                        width: 64,
                                                        height: 64,
                                                        borderRadius: 1.5,
                                                        objectFit: 'cover',
                                                        cursor: 'pointer',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                    }}
                                                />
                                            ) : null}
                                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" sx={{ flex: 1 }}>
                                                <Box>
                                                    <Typography variant="subtitle2">{item.name}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Woo ID: {item.wooId} | SKU: {item.sku || '-'} | Prix: {item.price || '-'} | Statut: {item.status || '-'}
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => openCreateProductFromWooMissing(item)}
                                                    sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, textTransform: 'none' }}
                                                >
                                                    Ajouter
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                ))}
                                {filteredWooSyncMissing.length > wooSyncPageSize && (
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button
                                            size="small"
                                            disabled={wooSyncPages.missing <= 1}
                                            onClick={() => setWooSyncPages((prev) => ({ ...prev, missing: prev.missing - 1 }))}
                                        >
                                            Précédent
                                        </Button>
                                        <Chip
                                            size="small"
                                            label={`${wooSyncPages.missing} / ${Math.max(1, Math.ceil(filteredWooSyncMissing.length / wooSyncPageSize))}`}
                                        />
                                        <Button
                                            size="small"
                                            disabled={wooSyncPages.missing >= Math.max(1, Math.ceil(filteredWooSyncMissing.length / wooSyncPageSize))}
                                            onClick={() => setWooSyncPages((prev) => ({ ...prev, missing: prev.missing + 1 }))}
                                        >
                                            Suivant
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                        )}

                        {wooSyncTab === 3 && (
                            <Stack spacing={1}>
                                {filteredWooSyncErrors.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Aucune erreur pour le moment.
                                    </Typography>
                                ) : paginatedWooSyncErrors.map((item, index) => (
                                    <Paper key={`${item.type}-${index}`} variant="outlined" sx={{ p: 1.5, borderColor: 'error.main' }}>
                                        <Typography variant="subtitle2">{item.wooName || item.localName || 'Erreur WooCommerce'}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {item.message}
                                        </Typography>
                                    </Paper>
                                ))}
                                {filteredWooSyncErrors.length > wooSyncPageSize && (
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button
                                            size="small"
                                            disabled={wooSyncPages.errors <= 1}
                                            onClick={() => setWooSyncPages((prev) => ({ ...prev, errors: prev.errors - 1 }))}
                                        >
                                            Précédent
                                        </Button>
                                        <Chip
                                            size="small"
                                            label={`${wooSyncPages.errors} / ${Math.max(1, Math.ceil(filteredWooSyncErrors.length / wooSyncPageSize))}`}
                                        />
                                        <Button
                                            size="small"
                                            disabled={wooSyncPages.errors >= Math.max(1, Math.ceil(filteredWooSyncErrors.length / wooSyncPageSize))}
                                            onClick={() => setWooSyncPages((prev) => ({ ...prev, errors: prev.errors + 1 }))}
                                        >
                                            Suivant
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseWooSyncModal} disabled={wooSyncRunning} color="inherit">
                        Fermer
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(wooSyncPreviewImage)}
                onClose={() => setWooSyncPreviewImage(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
                    {wooSyncPreviewImage ? (
                        <Box
                            component="img"
                            src={wooSyncPreviewImage}
                            alt="Woo sync preview"
                            sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                        />
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWooSyncPreviewImage(null)} color="inherit">
                        Fermer
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Disable Glovo Products Modal */}
            <Dialog 
                open={bulkDisableModalOpen} 
                onClose={handleCloseBulkDisableModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { minHeight: 400 } }}
            >
                <DialogTitle>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <i className="fa-solid fa-ban" style={{ color: '#d32f2f' }}></i>
                        <span>Désactiver Produits Glovo par Magasin</span>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3}>
                        <Typography variant="body2" color="text.secondary">
                            Sélectionnez un magasin pour désactiver tous les produits Glovo actifs associés à ce magasin.
                        </Typography>

                        {/* Store Selector */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Magasin
                            </Typography>
                            <Select
                                value={selectedBulkDisableStore}
                                onChange={setSelectedBulkDisableStore}
                                options={(() => {
                                    // Only show stores that have at least one product with a Glovo plateform and a roadmap for that store
                                    // where roadmap.data.availability.available === true (enabled in Glovo)
                                    const enabledStoreCodes = new Set();
                                    const allVendorIds = [];
                                    products.forEach(product => {
                                        Array.isArray(product.plateforms) && product.plateforms.forEach(pf => {
                                            // Check if this plateform is a Glovo plateform
                                            const pfId = normalizeId(pf.plateform_id || pf._id || pf.id);
                                            if (!glovoPlateformIds.has(pfId)) return;
                                            
                                            // Check roadmaps for this Glovo plateform
                                            if (Array.isArray(pf.roadmap)) {
                                                pf.roadmap.forEach(rm => {
                                                    if (rm.vendorId) {
                                                        allVendorIds.push(String(rm.vendorId));
                                                    }
                                                    if (rm.vendorId && rm.data?.availability?.available === true) {
                                                        enabledStoreCodes.add(String(rm.vendorId).trim());
                                                    }
                                                });
                                            }
                                        });
                                    });
                                    const allStoreCodes = stores.map(store => [store.storeCode, store.code, store.value, store._id, store.id]
                                        .map(x => x && String(x).trim())
                                        .filter(Boolean)).flat();
                                    // ...removed Glovo Bulk Disable logs...
                                    const filtered = stores.filter(store => {
                                        // Try all possible code fields, all as string
                                        const codes = [store.storeCode, store.code, store.value, store._id, store.id]
                                            .map(x => x && String(x).trim())
                                            .filter(Boolean);
                                        const match = codes.some(code => enabledStoreCodes.has(code));
                                        codes.forEach(code => {
                                            enabledStoreCodes.forEach(vendorId => {
                                                if (code === vendorId) {
                                                    // removed Glovo Bulk Disable log
                                                } else {
                                                    // removed Glovo Bulk Disable log
                                                }
                                            });
                                        });
                                        return match;
                                    });
                                    // ...removed Glovo Bulk Disable logs...
                                    return filtered.map(store => ({
                                        ...store,
                                        label: store.label || store.storeName || store.name || 'Magasin sans nom',
                                        value: store.value || store._id || store.id,
                                        storeCode: store.storeCode || store.code
                                    }));
                                })()}
                                placeholder="Sélectionner un magasin..."
                                isClearable
                                isDisabled={bulkDisableInProgress}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderColor: '#ddd',
                                        '&:hover': { borderColor: '#aaa' }
                                    })
                                }}
                            />
                        </Box>


                        {/* Bulk Disable Queue Display */}
                        {bulkDisableQueue.length > 0 && (
                            <Box sx={{ maxHeight: 220, overflowY: 'auto', mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    File d'attente ({bulkDisableQueue.length} produits)
                                </Typography>
                                <Stack spacing={1}>
                                    {bulkDisableQueue.map((item, idx) => (
                                        <Stack key={item.product._id} direction="row" alignItems="center" spacing={2} sx={{ p: 1, borderRadius: 1, background: '#f7f7f7' }}>
                                            <Typography variant="body2" sx={{ flex: 1 }}>{item.product.name}</Typography>
                                            {item.status === 'pending' && <Typography color="text.secondary" variant="caption">En attente</Typography>}
                                            {item.status === 'in-progress' && <Typography color="info.main" variant="caption">En cours...</Typography>}
                                            {item.status === 'done' && <Typography color="success.main" variant="caption">Désactivé</Typography>}
                                            {item.status === 'error' && <Typography color="error" variant="caption">Erreur: {item.errorMsg}</Typography>}
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* Progress Bar and Pause/Resume */}
                        {bulkDisableQueue.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="body2" fontWeight="600">
                                        Progression
                                    </Typography>
                                    <Typography variant="body2" color="primary">
                                        {bulkDisableProgress.current} / {bulkDisableProgress.total}
                                    </Typography>
                                </Stack>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: 8,
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: 1,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: `${(bulkDisableProgress.current / (bulkDisableProgress.total || 1)) * 100}%`,
                                            height: '100%',
                                            backgroundColor: '#1976d2',
                                            transition: 'width 0.3s ease'
                                        }}
                                    />
                                </Box>
                                <Stack direction="row" spacing={2} mt={2}>
                                    <Button
                                        variant="outlined"
                                        color={bulkDisablePaused ? 'success' : 'warning'}
                                        onClick={() => setBulkDisablePaused(p => !p)}
                                        disabled={!bulkDisableInProgress}
                                    >
                                        {bulkDisablePaused ? 'Reprendre' : 'Pause'}
                                    </Button>
                                </Stack>
                            </Box>
                        )}

                        {/* Errors Display */}
                        {bulkDisableProgress.errors && bulkDisableProgress.errors.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" color="error" gutterBottom>
                                    Erreurs ({bulkDisableProgress.errors.length})
                                </Typography>
                                <Paper 
                                    variant="outlined" 
                                    sx={{ 
                                        p: 2, 
                                        maxHeight: 200, 
                                        overflow: 'auto',
                                        backgroundColor: '#fff3f3'
                                    }}
                                >
                                    <Stack spacing={1}>
                                        {bulkDisableProgress.errors.map((err, idx) => (
                                            <Typography key={idx} variant="caption" color="error">
                                                • {err.product}: {err.error}
                                            </Typography>
                                        ))}
                                    </Stack>
                                </Paper>
                            </Box>
                        )}

                        {/* Summary */}
                        {selectedBulkDisableStore && !bulkDisableInProgress && (
                            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                                <Typography variant="body2">
                                    <strong>Magasin sélectionné:</strong> {selectedBulkDisableStore.label || selectedBulkDisableStore.storeName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mt={1}>
                                    Tous les produits Glovo actifs pour ce magasin seront désactivés.
                                </Typography>
                            </Paper>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button 
                        onClick={handleCloseBulkDisableModal}
                        disabled={bulkDisableInProgress}
                        color="inherit"
                    >
                        {bulkDisableInProgress ? 'Fermer' : 'Annuler'}
                    </Button>
                    <Button 
                        onClick={handleBulkDisableGlovoProducts}
                        disabled={!selectedBulkDisableStore || bulkDisableInProgress}
                        variant="contained"
                        color="error"
                        startIcon={bulkDisableInProgress ? 
                            <i className="fa-solid fa-spinner fa-spin"></i> : 
                            <i className="fa-solid fa-ban"></i>
                        }
                    >
                        {bulkDisableInProgress ? 'Désactivation...' : 'Désactiver Tout'}
                    </Button>
                </DialogActions>
            </Dialog>
            </Box>
        </Container>
    );
}

export default StoreQuickProduct;


